import * as views from './alan_api';
import * as application from '../../libraries/model_lib/types/application/alan_api';
import * as client_bindings from '../../libraries/client_lib/types/client_bindings/alan_api';
import * as gui_model from '../gui_model/alan_api';
import * as parameters from '../parameters/alan_api';
import * as phrases from '../phrases/alan_api';
import * as settings from '../settings/alan_api';
import * as widget from '../widget/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cviews
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cviews|undefined;
	public abstract get root():Cviews;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Twidget_instance = {
	'configuration':Tgui_widget_configuration_node;
	'widget':string;
};
export class Cwidget_instance extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly widget:Cwidget_instance.Dwidget
	};
	constructor(init:Twidget_instance, public location:AlanNode, public input: {
		client_binding: () => client_bindings.Cbinding_context,
		context: () => views.Ccontext,
		view: () => views.Cviews__
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cwidget_instance.Dconfiguration(init['configuration'], $this),
			widget: new Cwidget_instance.Dwidget(init['widget'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Twidget_expression = {
	'type':['group', Tgroup__type__widget_expression]|['parent', Tparent__type__widget_expression]|['reference', Treference__type__widget_expression]|['reference rule', Treference_rule__type__widget_expression]|['state', Tstate__type__widget_expression]|['state context rule', Tstate_context_rule]|['switch', Tswitch]|['view', Tview__type__widget_expression]|['widget', Twidget__type__widget_expression];
};
export class Cwidget_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cwidget_expression.Dtype
	};
	constructor(init:Twidget_expression, public location:AlanNode, public input: {
		client_binding: () => client_bindings.Cbinding_context,
		context: () => views.Ccontext,
		view: () => views.Cviews__
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cwidget_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tgroup__type__widget_expression = {
	'group':string;
	'next':Twidget_expression;
};
export class Cgroup__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__widget_expression.Dgroup,
		readonly next:Cwidget_expression
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tgroup__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__widget_expression.Dgroup(init['group'], $this),
			next: new Cgroup__type__widget_expression.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__widget_expression = {
	'next':Twidget_expression;
};
export class Cparent__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly next:Cwidget_expression
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		parent: () => gui_model.Cgui_node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		parent: cache((detach:boolean) => {
			const views__widget_expression__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_node())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tparent__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			next: new Cparent__type__widget_expression.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__type__widget_expression = {
	'next':Twidget_expression;
	'text':string;
};
export class Creference__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly next:Cwidget_expression,
		readonly text:Creference__type__widget_expression.Dtext
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Treference__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			next: new Creference__type__widget_expression.Dnext(init['next'], $this),
			text: new Creference__type__widget_expression.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_rule__type__widget_expression = {
	'next':Twidget_expression;
	'rule':string;
	'text':string;
};
export class Creference_rule__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly next:Cwidget_expression,
		readonly rule:Creference_rule__type__widget_expression.Drule,
		readonly text:Creference_rule__type__widget_expression.Dtext
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__reference_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__reference_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Treference_rule__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			next: new Creference_rule__type__widget_expression.Dnext(init['next'], $this),
			rule: new Creference_rule__type__widget_expression.Drule(init['rule'], $this),
			text: new Creference_rule__type__widget_expression.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__widget_expression = {
	'next':Twidget_expression;
	'state':string;
	'state group':string;
};
export class Cstate__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly next:Cwidget_expression,
		readonly state:Cstate__type__widget_expression.Dstate,
		readonly state_group:Cstate__type__widget_expression.Dstate_group
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tstate__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			next: new Cstate__type__widget_expression.Dnext(init['next'], $this),
			state: new Cstate__type__widget_expression.Dstate(init['state'], $this),
			state_group: new Cstate__type__widget_expression.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_context_rule = {
	'next':Twidget_expression;
	'rule':string;
};
export class Cstate_context_rule extends AlanNode {
	public readonly properties:{
		readonly next:Cwidget_expression,
		readonly rule:Cstate_context_rule.Drule
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		state: () => gui_model.Cstates__state_group__type__property__type__attributes
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		state: cache((detach:boolean) => {
			const views__widget_expression__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})

	}
	constructor(init:Tstate_context_rule, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			next: new Cstate_context_rule.Dnext(init['next'], $this),
			rule: new Cstate_context_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state context rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tswitch = {
	'state group':string;
	'states':Record<string, Tstates__switch>;
};
export class Cswitch extends AlanNode {
	public readonly properties:{
		readonly state_group:Cswitch.Dstate_group,
		readonly states:Cswitch.Dstates
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__switch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__switch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tswitch, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			state_group: new Cswitch.Dstate_group(init['state group'], $this),
			states: new Cswitch.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'switch'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__switch extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates__switch) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__switch = {
	'next':Twidget_expression;
};
export class Cstates__switch extends AlanDictionaryEntry {
	public key:Kstates__switch;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly next:Cwidget_expression
	};
	constructor(key:string, init:Tstates__switch, public parent:Cswitch) {
		super();
		const $this = this;
		this.key = new Kstates__switch(key, $this);
		this.properties = {
			next: new Cstates__switch.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tview__type__widget_expression = {
	'using views':['external', Texternal__using_views__view__type__widget_expression]|'internal'|['internal', {}];
	'view':string;
};
export class Cview__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly using_views:Cview__type__widget_expression.Dusing_views,
		readonly view:Cview__type__widget_expression.Dview
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		view_binding: () => client_bindings.Cbinding_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__widget_expression__type__view_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__widget_expression__type__view_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		view_binding: cache((detach:boolean) => {
			const views__widget_expression__type__view_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.client_binding()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.root.input.client_bindings)
						.then(context => context?.properties.initial_view_binding?.ref)
						.then(context => context?.properties.binding)
						.then(context => context?.definitions.binding_context).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tview__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			using_views: new Cview__type__widget_expression.Dusing_views(init['using views'], $this),
			view: new Cview__type__widget_expression.Dview(init['view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Texternal__using_views__view__type__widget_expression = {
	'views':string;
};
export class Cexternal__using_views__view__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly views:Cexternal__using_views__view__type__widget_expression.Dviews
	};
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Texternal__using_views__view__type__widget_expression, public parent:Cview__type__widget_expression) {
		super();
		const $this = this;
		this.properties = {
			views: new Cexternal__using_views__view__type__widget_expression.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'external'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinternal__using_views__view__type__widget_expression = {
};
export class Cinternal__using_views__view__type__widget_expression extends AlanNode {
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root).result!)
	}
	constructor(init:Tinternal__using_views__view__type__widget_expression, public parent:Cview__type__widget_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'internal'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type__widget_expression = {
	'lazy':'no'|['no', {}]|'yes'|['yes', {}];
	'widget':Twidget_instance;
};
export class Cwidget__type__widget_expression extends AlanNode {
	public readonly properties:{
		readonly lazy:Cwidget__type__widget_expression.Dlazy,
		readonly widget:Cwidget_instance
	};
	constructor(init:Twidget__type__widget_expression, public parent:Cwidget_expression) {
		super();
		const $this = this;
		this.properties = {
			lazy: new Cwidget__type__widget_expression.Dlazy(init['lazy'], $this),
			widget: new Cwidget__type__widget_expression.Dwidget(init['widget'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__lazy = {
};
export class Cno__lazy extends AlanNode {
	constructor(init:Tno__lazy, public parent:Cwidget__type__widget_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'lazy'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__lazy = {
};
export class Cyes__lazy extends AlanNode {
	constructor(init:Tyes__lazy, public parent:Cwidget__type__widget_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'lazy'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview_context_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__view_context_path];
};
export class Cview_context_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cview_context_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tview_context_path, public location:AlanNode, public input: {
		context: () => views.Ccontext
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cview_context_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__view_context_path = {
};
export class Cno__has_steps__view_context_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		model: () => views.Centry_or_node_context,
		node: () => gui_model.Cgui_node
	} = {
		model: cache((detach:boolean) => {
			const views__view_context_path__has_steps__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node: cache((detach:boolean) => {
			const views__view_context_path__has_steps__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tno__has_steps__view_context_path, public parent:Cview_context_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__view_context_path = {
	'type':'entity'|['entity', {}]|['parent', Tparent__type__yes__has_steps__view_context_path]|'query entry'|['query entry', {}]|'reference'|['reference', {}]|'state rule'|['state rule', {}];
};
export class Cyes__has_steps__view_context_path extends AlanNode {
	public readonly properties:{
		readonly type:Cyes__has_steps__view_context_path.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.selected_node()).result!)
	}
	constructor(init:Tyes__has_steps__view_context_path, public parent:Cview_context_path) {
		super();
		const $this = this;
		this.properties = {
			type: new Cyes__has_steps__view_context_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentity__type__yes = {
};
export class Centity__type__yes extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node_context())
			.then(context => context?.component_root.output.gui_entity()).result!)
	}
	public readonly inferences:{
		model: () => views.Centry_or_node_context,
		node_context: () => gui_model.Cgui_node
	} = {
		model: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__entity_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node_context: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__entity_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tentity__type__yes, public parent:Cyes__has_steps__view_context_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entity'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__yes__has_steps__view_context_path = {
	'path':Tview_context_parent_path;
};
export class Cparent__type__yes__has_steps__view_context_path extends AlanNode {
	public readonly properties:{
		readonly path:Cview_context_parent_path
	};
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	public readonly inferences:{
		model: () => views.Centry_or_node_context,
		node: () => gui_model.Cgui_node
	} = {
		model: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tparent__type__yes__has_steps__view_context_path, public parent:Cyes__has_steps__view_context_path) {
		super();
		const $this = this;
		this.properties = {
			path: new Cparent__type__yes__has_steps__view_context_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_entry = {
};
export class Cquery_entry extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.query_context())
			.then(context => context?.properties.context?.state.node.output.selected_context())
			.then(context => context?.component_root.output.gui_entity()).result!)
	}
	public readonly inferences:{
		query_context: () => views.Cqueries
	} = {
		query_context: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__query_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'query' ? context.variant.definition as views.Cqueries : undefined).result!;
		})

	}
	constructor(init:Tquery_entry, public parent:Cyes__has_steps__view_context_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__type__yes__has_steps__view_context_path = {
};
export class Creference__type__yes__has_steps__view_context_path extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node_context())
			.then(context => context?.component_root.output.gui_entity()).result!)
	}
	public readonly inferences:{
		model: () => views.Centry_or_node_context,
		node_context: () => gui_model.Cgui_node
	} = {
		model: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node_context: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Treference__type__yes__has_steps__view_context_path, public parent:Cyes__has_steps__view_context_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule__type__yes__has_steps__view_context_path = {
};
export class Cstate_rule__type__yes__has_steps__view_context_path extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.binding_context())
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node())
			.then(context => context?.component_root.output.gui_entity()).result!)
	}
	public readonly inferences:{
		binding_context: () => gui_model.Crules,
		model: () => views.Centry_or_node_context
	} = {
		binding_context: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'where rule' ? context.variant.definition as gui_model.Crules : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__view_context_path__has_steps__yes__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tstate_rule__type__yes__has_steps__view_context_path, public parent:Cyes__has_steps__view_context_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview_context_parent_path = {
	'has tail':'no'|['no', {}]|['yes', Tyes__has_tail];
	'type':'collection parent'|['collection parent', {}]|'group parent'|['group parent', {}]|'state parent'|['state parent', {}];
};
export class Cview_context_parent_path extends AlanNode {
	public readonly properties:{
		readonly has_tail:Cview_context_parent_path.Dhas_tail,
		readonly type:Cview_context_parent_path.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_tail?.state.node.output.result_node()).result!)
	};
	constructor(init:Tview_context_parent_path, public location:AlanNode, public input: {
		context: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_tail: new Cview_context_parent_path.Dhas_tail(init['has tail'], $this),
			type: new Cview_context_parent_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_tail = {
};
export class Cno__has_tail extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.type?.state.node.output.selected_node()).result!)
	}
	constructor(init:Tno__has_tail, public parent:Cview_context_parent_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_tail = {
	'tail':Tview_context_parent_path;
};
export class Cyes__has_tail extends AlanNode {
	public readonly properties:{
		readonly tail:Cview_context_parent_path
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_tail, public parent:Cview_context_parent_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_tail.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_parent = {
};
export class Ccollection_parent extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent()).result!)
	}
	public readonly inferences:{
		collection: () => gui_model.Ccollection__type__property,
		parent: () => gui_model.Cgui_node
	} = {
		collection: cache((detach:boolean) => {
			const views__view_context_parent_path__type__collection_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as gui_model.Ccollection__type__property : undefined).result!;
		})
		,
		parent: cache((detach:boolean) => {
			const views__view_context_parent_path__type__collection_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tcollection_parent, public parent:Cview_context_parent_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup_parent = {
};
export class Cgroup_parent extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent()).result!)
	}
	public readonly inferences:{
		group: () => gui_model.Cgroup__type__property,
		parent: () => gui_model.Cgui_node
	} = {
		group: cache((detach:boolean) => {
			const views__view_context_parent_path__type__group_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'group' ? context.variant.definition as gui_model.Cgroup__type__property : undefined).result!;
		})
		,
		parent: cache((detach:boolean) => {
			const views__view_context_parent_path__type__group_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tgroup_parent, public parent:Cview_context_parent_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_parent = {
};
export class Cstate_parent extends AlanNode {
	public readonly output:{
		selected_node: () => gui_model.Cgui_node;
	} = {
		selected_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent()).result!)
	}
	public readonly inferences:{
		parent: () => gui_model.Cgui_node,
		state_group: () => gui_model.Cstates__state_group__type__property__type__attributes
	} = {
		parent: cache((detach:boolean) => {
			const views__view_context_parent_path__type__state_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		state_group: cache((detach:boolean) => {
			const views__view_context_parent_path__type__state_parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})

	}
	constructor(init:Tstate_parent, public parent:Cview_context_parent_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_path_step = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__query_path_step];
};
export class Cquery_path_step extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cquery_path_step.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tquery_path_step, public location:AlanNode, public input: {
		gui_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cquery_path_step.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__query_path_step = {
};
export class Cno__has_steps__query_path_step extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_node()).result!)
	}
	constructor(init:Tno__has_steps__query_path_step, public parent:Cquery_path_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__query_path_step = {
	'tail':Tquery_path_step;
	'type':['group', Tgroup__type__yes__has_steps__query_path_step]|['state', Tstate__type__yes__has_steps__query_path_step];
};
export class Cyes__has_steps__query_path_step extends AlanNode {
	public readonly properties:{
		readonly tail:Cquery_path_step,
		readonly type:Cyes__has_steps__query_path_step.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__query_path_step, public parent:Cquery_path_step) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__query_path_step.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__query_path_step.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__query_path_step = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__query_path_step extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__query_path_step.Dgroup
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__query_path_step, public parent:Cyes__has_steps__query_path_step) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__query_path_step.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__query_path_step = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__query_path_step extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__query_path_step.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__query_path_step.Dstate_group
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tstate__type__yes__has_steps__query_path_step, public parent:Cyes__has_steps__query_path_step) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__query_path_step.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__query_path_step.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__query_path];
};
export class Cquery_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cquery_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.gui_node()).result!)
	};
	constructor(init:Tquery_path, public location:AlanNode, public input: {
		gui_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cquery_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__query_path = {
};
export class Cno__has_steps__query_path extends AlanNode {
	public readonly output:{
		gui_node: () => gui_model.Cgui_node;
	} = {
		gui_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_node()).result!)
	}
	constructor(init:Tno__has_steps__query_path, public parent:Cquery_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__query_path = {
	'collection':string;
	'head':Tquery_path_step;
	'tail':Tquery_path;
};
export class Cyes__has_steps__query_path extends AlanNode {
	public readonly properties:{
		readonly collection:Cyes__has_steps__query_path.Dcollection,
		readonly head:Cquery_path_step,
		readonly tail:Cquery_path
	};
	public readonly output:{
		gui_node: () => gui_model.Cgui_node;
	} = {
		gui_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__query_path, public parent:Cquery_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cyes__has_steps__query_path.Dcollection(init['collection'], $this),
			head: new Cyes__has_steps__query_path.Dhead(init['head'], $this),
			tail: new Cyes__has_steps__query_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Voptional_node = { name: 'none', definition: (typeof Coptional_node.Pnone)}|{ name: 'node', definition: gui_model.Cgui_node}
export class Coptional_node extends AlanStruct {
	public static Pnone:Coptional_node = new class PrimitiveInstance extends Coptional_node {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_node})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_node) { super(); }
	public cast<K extends Voptional_node['name']>(_variant:K):Extract<Voptional_node, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_node['name']]:(($:Extract<Voptional_node, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional node'`; }
	public is(other:Coptional_node):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_node = views.Coptional_node;
type Voptional_key_context = { name: 'none', definition: (typeof Coptional_key_context.Pnone)}|{ name: 'set', definition: views.Ckey_context}
export class Coptional_key_context extends AlanStruct {
	public static Pnone:Coptional_key_context = new class PrimitiveInstance extends Coptional_key_context {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_key_context})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_key_context) { super(); }
	public cast<K extends Voptional_key_context['name']>(_variant:K):Extract<Voptional_key_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_key_context['name']]:(($:Extract<Voptional_key_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional key context'`; }
	public is(other:Coptional_key_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_key_context = views.Coptional_key_context;
export type Tnumber_filter_expression = {
	'operator':['equals', Tequals]|['range', Trange];
};
export class Cnumber_filter_expression extends AlanNode {
	public readonly properties:{
		readonly operator:Cnumber_filter_expression.Doperator
	};
	constructor(init:Tnumber_filter_expression, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cnumber_filter_expression.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tequals = {
	'criterium':number;
};
export class Cequals extends AlanNode {
	public readonly properties:{
		readonly criterium:Cequals.Dcriterium
	};
	constructor(init:Tequals, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cequals.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Trange = {
	'greater than':'no'|['no', {}]|['yes', Tyes__greater_than];
	'less than':'no'|['no', {}]|['yes', Tyes__less_than];
};
export class Crange extends AlanNode {
	public readonly properties:{
		readonly greater_than:Crange.Dgreater_than,
		readonly less_than:Crange.Dless_than
	};
	constructor(init:Trange, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			greater_than: new Crange.Dgreater_than(init['greater than'], $this),
			less_than: new Crange.Dless_than(init['less than'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'range'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__greater_than = {
};
export class Cno__greater_than extends AlanNode {
	constructor(init:Tno__greater_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__greater_than = {
	'criterium':number;
};
export class Cyes__greater_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cyes__greater_than.Dcriterium
	};
	constructor(init:Tyes__greater_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__greater_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__less_than = {
};
export class Cno__less_than extends AlanNode {
	public readonly inferences:{
		must_be_greater_than: () => views.Cyes__greater_than
	} = {
		must_be_greater_than: cache((detach:boolean) => {
			const views__number_filter_expression__operator__range__less_than__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.greater_than.state.name === 'yes') {
						return resolve(context.properties.greater_than.state.node as views.Cyes__greater_than).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__less_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__less_than = {
	'criterium':number;
};
export class Cyes__less_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cyes__less_than.Dcriterium
	};
	constructor(init:Tyes__less_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__less_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__node_path];
};
export class Cnode_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tnode_path, public location:AlanNode, public input: {
		context_node: () => gui_model.Cgui_node,
		existence_support: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__node_path = {
};
export class Cno__has_steps__node_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__node_path, public parent:Cnode_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__node_path = {
	'tail':Tnode_path;
	'type':['collection entry', Tcollection_entry]|['group', Tgroup__type__yes__has_steps__node_path]|'parent'|['parent', {}]|['reference', Treference__type__yes__has_steps__node_path]|['reference rule', Treference_rule__type__yes]|['state', Tstate__type__yes__has_steps__node_path]|['state rule', Tstate_rule__type__yes__has_steps__node_path];
};
export class Cyes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cnode_path,
		readonly type:Cyes__has_steps__node_path.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__node_path, public parent:Cnode_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__node_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__node_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry = {
	'collection':string;
	'entry key':string;
};
export class Ccollection_entry extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry.Dcollection,
		readonly entry_key:string
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const views__node_path__has_steps__yes__type__collection_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tcollection_entry, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry.Dcollection(init['collection'], $this),
			entry_key: init['entry key']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__node_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__node_path.Dgroup
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__node_path.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__yes__has_steps__node_path = {
};
export class Cparent__type__yes__has_steps__node_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_node()).result!)
	}
	public readonly inferences:{
		parent_node: () => gui_model.Cgui_node
	} = {
		parent_node: cache((detach:boolean) => {
			const views__node_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tparent__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__type__yes__has_steps__node_path = {
	'text':string;
};
export class Creference__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly text:Creference__type__yes__has_steps__node_path.Dtext
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.text?.inferences.navigable())
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	constructor(init:Treference__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			text: new Creference__type__yes__has_steps__node_path.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_rule__type__yes = {
	'property':string;
	'rule':string;
};
export class Creference_rule__type__yes extends AlanNode {
	public readonly properties:{
		readonly property:Creference_rule__type__yes.Dproperty,
		readonly rule:Creference_rule__type__yes.Drule
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	constructor(init:Treference_rule__type__yes, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			property: new Creference_rule__type__yes.Dproperty(init['property'], $this),
			rule: new Creference_rule__type__yes.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__node_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__node_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__node_path.Dstate_group
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const views__node_path__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tstate__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__node_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__node_path.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule__type__yes__has_steps__node_path = {
	'rule':string;
};
export class Cstate_rule__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly rule:Cstate_rule__type__yes__has_steps__node_path.Drule
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	public readonly inferences:{
		state: () => gui_model.Cstates__state_group__type__property__type__attributes
	} = {
		state: cache((detach:boolean) => {
			const views__node_path__has_steps__yes__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})

	}
	constructor(init:Tstate_rule__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_rule__type__yes__has_steps__node_path.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
type Vcontext = { name: 'join', definition: views.Cjoin_context}|{ name: 'ordered graph', definition: views.Cordered_graph}|{ name: 'report', definition: views.Creport}|{ name: 'state group', definition: views.Cstate_group__type__model_binding}|{ name: 'state', definition: views.Cstates__states_list}|{ name: 'model', definition: views.Centry_or_node_context}
export class Ccontext extends AlanStruct {
	constructor(
		public readonly variant:Vcontext) { super(); }
	public readonly output:{
		default_context: () => views.Ccontext;
	} = {
		default_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'join': {
						const views__context_out___default_context___join_nval = context.cast('join');
						return resolve(context)
							.then(context => views__context_out___default_context___join_nval)
							.then(context => context?.component_root.output.model_context()).result;
					}
					case 'model': {
						return resolve(context)
							.then(() => this).result;
					}
					case 'ordered graph': {
						return resolve(context)
							.then(() => this).result;
					}
					case 'report': {
						return resolve(context)
							.then(() => this).result;
					}
					case 'state': {
						return resolve(context)
							.then(() => this).result;
					}
					case 'state group': {
						return resolve(context)
							.then(() => this).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vcontext['name']>(_variant:K):Extract<Vcontext, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcontext['name']]:(($:Extract<Vcontext, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'context'`; }
	public is(other:Ccontext):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Ventry_or_node_context = { name: 'model', definition: views.Cmodel_context}|{ name: 'query', definition: views.Cqueries}
export class Centry_or_node_context extends AlanStruct {
	constructor(
		public readonly variant:Ventry_or_node_context, public input: {
			model_context: () => gui_model.Cgui_context
		}) { super(); }
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'model', definition: this})
	}
	public readonly output:{
		model_context: () => gui_model.Cgui_context;
	} = {
		model_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.model_context()).result!)
	};
	public cast<K extends Ventry_or_node_context['name']>(_variant:K):Extract<Ventry_or_node_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventry_or_node_context['name']]:(($:Extract<Ventry_or_node_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entry or node context'`; }
	public is(other:Centry_or_node_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vmodel_context = { name: 'text', definition: views.Ctext__type__model_binding}|{ name: 'action', definition: views.Caction}|{ name: 'gui context', definition: views.Cgui_model_context}
export class Cmodel_context extends AlanStruct {
	constructor(
		public readonly variant:Vmodel_context, public input: {
			model_context: () => gui_model.Cgui_context
		}) { super(); }
	public definitions:{
		entry_or_node_context: Centry_or_node_context;
	} = {
		entry_or_node_context: new Centry_or_node_context({name:'model', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.model_context()).result!)
		})
	}
	public readonly output:{
		model_context: () => gui_model.Cgui_context;
	} = {
		model_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.model_context()).result!)
	};
	public cast<K extends Vmodel_context['name']>(_variant:K):Extract<Vmodel_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vmodel_context['name']]:(($:Extract<Vmodel_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'model context'`; }
	public is(other:Cmodel_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tmodel_binding_property_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__model_binding_property_path];
};
export class Cmodel_binding_property_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cmodel_binding_property_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tmodel_binding_property_path, public location:AlanNode, public input: {
		gui_node: () => gui_model.Cgui_node,
		participation: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cmodel_binding_property_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__model_binding_property_path = {
};
export class Cno__has_steps__model_binding_property_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_node()).result!)
	}
	constructor(init:Tno__has_steps__model_binding_property_path, public parent:Cmodel_binding_property_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__model_binding_property_path = {
	'tail':Tmodel_binding_property_path;
	'type':['group', Tgroup__type__yes__has_steps__model_binding_property_path]|'parent'|['parent', {}]|['state group', Tstate_group__type__yes];
};
export class Cyes__has_steps__model_binding_property_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cmodel_binding_property_path,
		readonly type:Cyes__has_steps__model_binding_property_path.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__model_binding_property_path, public parent:Cmodel_binding_property_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__model_binding_property_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__model_binding_property_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__model_binding_property_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__model_binding_property_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__model_binding_property_path.Dgroup
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__model_binding_property_path, public parent:Cyes__has_steps__model_binding_property_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__model_binding_property_path.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__yes__has_steps__model_binding_property_path = {
};
export class Cparent__type__yes__has_steps__model_binding_property_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_node()).result!)
	}
	public readonly inferences:{
		parent_node: () => gui_model.Cgui_node
	} = {
		parent_node: cache((detach:boolean) => {
			const views__model_binding_property_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.gui_node())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tparent__type__yes__has_steps__model_binding_property_path, public parent:Cyes__has_steps__model_binding_property_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__yes = {
	'state':string;
	'state group':string;
};
export class Cstate_group__type__yes extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group__type__yes.Dstate,
		readonly state_group:Cstate_group__type__yes.Dstate_group
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		conditional: () => application.Coptional__existence
	} = {
		conditional: cache((detach:boolean) => {
			const views__model_binding_property_path__has_steps__yes__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.participation())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tstate_group__type__yes, public parent:Cyes__has_steps__model_binding_property_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group__type__yes.Dstate(init['state'], $this),
			state_group: new Cstate_group__type__yes.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
type Vlist_counter = { name: 'entry', definition: views.Cyes__has_steps__gui_widget_configuration_list}|{ name: 'start', definition: (typeof Clist_counter.Pstart)}
export class Clist_counter extends AlanStruct {
	public static Pstart:Clist_counter = new class PrimitiveInstance extends Clist_counter {
		constructor () {
			super({name: 'start', definition: undefined as unknown as Clist_counter})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vlist_counter) { super(); }
	public cast<K extends Vlist_counter['name']>(_variant:K):Extract<Vlist_counter, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vlist_counter['name']]:(($:Extract<Vlist_counter, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'list counter'`; }
	public is(other:Clist_counter):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cstart = views.Clist_counter;
type Vkey_context = { name: 'graph', definition: gui_model.Cordered_graphs}|{ name: 'rule', definition: gui_model.Crules}|{ name: 'state', definition: gui_model.Cstates__state_group__type__property__type__attributes}|{ name: 'attribute', definition: gui_model.Cattributes}
export class Ckey_context extends AlanStruct {
	constructor(
		public readonly variant:Vkey_context) { super(); }
	public definitions:{
		optional_key_context: Coptional_key_context;
	} = {
		optional_key_context: new Coptional_key_context({name:'set', definition: this})
	}
	public cast<K extends Vkey_context['name']>(_variant:K):Extract<Vkey_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vkey_context['name']]:(($:Extract<Vkey_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'key context'`; }
	public is(other:Ckey_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vjoin_context = { name: 'join', definition: views.Cyes__join}
export class Cjoin_context extends AlanStruct {
	constructor(
		public readonly variant:Vjoin_context, public input: {
			model_context: () => views.Ccontext,
			referenced_model_context: () => views.Ccontext
		}) { super(); }
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'join', definition: this})
	}
	public readonly output:{
		model_context: () => views.Ccontext;
		referenced_model_context: () => views.Ccontext;
	} = {
		model_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.model_context()).result!),
		referenced_model_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.referenced_model_context()).result!)
	};
	public cast<K extends Vjoin_context['name']>(_variant:K):Extract<Vjoin_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vjoin_context['name']]:(($:Extract<Vjoin_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'join context'`; }
	public is(other:Cjoin_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
function evaluate__infer_key_context(detach:boolean, views__infer_key_context_nval:views.Ccontext, ):views.Coptional_key_context {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => views__infer_key_context_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'join': {
					return resolve(context)
						.then(() => self)
						.then(() => views.Coptional_key_context.Pnone).result;
				}
				case 'model': {
					const views__infer_key_context___model_nval = context.cast('model');
					return resolve(context)
						.then(context => views__infer_key_context___model_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'model': {
									const views__infer_key_context___model___model_nval = context.cast('model');
									return resolve(context)
										.then(context => views__infer_key_context___model___model_nval)
										.then(context => context?.component_root.output.model_context())
										.then(context => {
											switch (context?.variant.name) {
												case 'gui node': {
													const views__infer_key_context___model___model___gui_node_nval = context.cast('gui node');
													return resolve(context)
														.then(context => views__infer_key_context___model___model___gui_node_nval)
														.then(context => context?.component_root.output.location())
														.then(context => {
															switch (context?.variant.name) {
																case 'collection': {
																	const views__infer_key_context___model___model___gui_node___collection_nval = context.cast('collection');
																	return resolve(context)
																		.then(context => views__infer_key_context___model___model___gui_node___collection_nval)
																		.then(context => context?.parent)
																		.then(context => context?.parent)
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Ckey_context({name: 'attribute', definition: conv_context});
																		})
																		.then(context => context?.definitions.optional_key_context).result;
																}
																case 'group': {
																	const views__infer_key_context___model___model___gui_node___group_nval = context.cast('group');
																	return resolve(context)
																		.then(context => views__infer_key_context___model___model___gui_node___group_nval)
																		.then(context => context?.parent)
																		.then(context => context?.parent)
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Ckey_context({name: 'attribute', definition: conv_context});
																		})
																		.then(context => context?.definitions.optional_key_context).result;
																}
																case 'operation': {
																	const views__infer_key_context___model___model___gui_node___operation_nval = context.cast('operation');
																	return resolve(context)
																		.then(context => views__infer_key_context___model___model___gui_node___operation_nval)
																		.then(context => {
																			switch (context?.variant.name) {
																				case 'action': {
																					const views__infer_key_context___model___model___gui_node___operation___action_nval = context.cast('action');
																					return resolve(context)
																						.then(context => views__infer_key_context___model___model___gui_node___operation___action_nval)
																						.then(context => context?.parent)
																						.then(context => {
																							const conv_context = resolve(context).result!;
																							return new Ckey_context({name: 'attribute', definition: conv_context});
																						})
																						.then(context => context?.definitions.optional_key_context).result;
																				}
																				case 'command': {
																					const views__infer_key_context___model___model___gui_node___operation___command_nval = context.cast('command');
																					return resolve(context)
																						.then(context => views__infer_key_context___model___model___gui_node___operation___command_nval)
																						.then(context => context?.parent)
																						.then(context => {
																							const conv_context = resolve(context).result!;
																							return new Ckey_context({name: 'attribute', definition: conv_context});
																						})
																						.then(context => context?.definitions.optional_key_context).result;
																				}
																				case undefined: return undefined;
																				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																			};
																		}).result;
																}
																case 'root': {
																	return resolve(context)
																		.then(() => self)
																		.then(() => views.Coptional_key_context.Pnone).result;
																}
																case 'state': {
																	const views__infer_key_context___model___model___gui_node___state_nval = context.cast('state');
																	return resolve(context)
																		.then(context => views__infer_key_context___model___model___gui_node___state_nval)
																		.then(context => {
																			const conv_context = resolve(context).result!;
																			return new Ckey_context({name: 'state', definition: conv_context});
																		})
																		.then(context => context?.definitions.optional_key_context).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case 'where rule': {
													const views__infer_key_context___model___model___where_rule_nval = context.cast('where rule');
													return resolve(context)
														.then(context => views__infer_key_context___model___model___where_rule_nval)
														.then(context => {
															const conv_context = resolve(context).result!;
															return new Ckey_context({name: 'rule', definition: conv_context});
														})
														.then(context => context?.definitions.optional_key_context).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'query': {
									return resolve(context)
										.then(() => self)
										.then(() => views.Coptional_key_context.Pnone).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case 'ordered graph': {
					const views__infer_key_context___ordered_graph_nval = context.cast('ordered graph');
					return resolve(context)
						.then(context => views__infer_key_context___ordered_graph_nval)
						.then(context => context?.properties.ordered_graph?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ckey_context({name: 'graph', definition: conv_context});
						})
						.then(context => context?.definitions.optional_key_context).result;
				}
				case 'report': {
					return resolve(context)
						.then(() => self)
						.then(() => views.Coptional_key_context.Pnone).result;
				}
				case 'state': {
					const views__infer_key_context___state_nval = context.cast('state');
					return resolve(context)
						.then(context => views__infer_key_context___state_nval)
						.then(context => context?.key?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ckey_context({name: 'state', definition: conv_context});
						})
						.then(context => context?.definitions.optional_key_context).result;
				}
				case 'state group': {
					const views__infer_key_context___state_group_nval = context.cast('state group');
					return resolve(context)
						.then(context => views__infer_key_context___state_group_nval)
						.then(context => context?.properties.state_group?.ref)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ckey_context({name: 'attribute', definition: conv_context});
						})
						.then(context => context?.definitions.optional_key_context).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tgui_widget_configuration_node = {
	'configuration':Record<string, Tconfiguration__gui_widget_configuration_node>;
};
export class Cgui_widget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node.Dconfiguration
	};
	constructor(init:Tgui_widget_configuration_node, public location:AlanNode, public input: {
		client_binding: () => client_bindings.Cbinding_context,
		context: () => views.Ccontext,
		view: () => views.Cviews__,
		widget_configuration_node: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cgui_widget_configuration_node.Dconfiguration(init['configuration'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kconfiguration__gui_widget_configuration_node extends Reference<widget.Cattributes__widget_configuration_node, string> {
	public readonly inferences:{
		client_binding_context: () => client_bindings.Cbinding_context
	}
	constructor(key:string, $this:Cconfiguration__gui_widget_configuration_node) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.widget_configuration_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			client_binding_context: cache((detach:boolean) => {
				const views__gui_widget_configuration_node__configuration_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => evaluate__attribute_client_binding_check(detach, 
						resolve(context)
							.then(context => views__gui_widget_configuration_node__configuration_key_nval).result!, {
						inherited_binding: () => resolve($this.parent)
							.then(() => $this.parent)
							.then(context => context?.component_root.input.client_binding()).result!

						})).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'configuration'::key`; }
}
export type Tconfiguration__gui_widget_configuration_node = {
	'attribute':Tgui_widget_configuration_attribute;
};
export class Cconfiguration__gui_widget_configuration_node extends AlanDictionaryEntry {
	public key:Kconfiguration__gui_widget_configuration_node;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly attribute:Cgui_widget_configuration_attribute
	};
	constructor(key:string, init:Tconfiguration__gui_widget_configuration_node, public parent:Cgui_widget_configuration_node) {
		super();
		const $this = this;
		this.key = new Kconfiguration__gui_widget_configuration_node(key, $this);
		this.properties = {
			attribute: new Cconfiguration__gui_widget_configuration_node.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'configuration'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tgui_widget_configuration_list = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__gui_widget_configuration_list];
};
export class Cgui_widget_configuration_list extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgui_widget_configuration_list.Dhas_steps
	};
	public readonly output:{
		previous_entry: () => views.Clist_counter;
	} = {
		previous_entry: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.previous_entry()).result!)
	};
	constructor(init:Tgui_widget_configuration_list, public location:AlanNode, public input: {
		client_binding: () => client_bindings.Cbinding_context,
		context: () => views.Ccontext,
		previous_entry: () => views.Clist_counter,
		view: () => views.Cviews__,
		widget_configuration_node: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgui_widget_configuration_list.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__gui_widget_configuration_list = {
};
export class Cno__has_steps__gui_widget_configuration_list extends AlanNode {
	constructor(init:Tno__has_steps__gui_widget_configuration_list, public parent:Cgui_widget_configuration_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__gui_widget_configuration_list = {
	'configuration':Tgui_widget_configuration_node;
	'tail':Tgui_widget_configuration_list;
};
export class Cyes__has_steps__gui_widget_configuration_list extends AlanNode {
	public definitions:{
		list_counter: Clist_counter;
	} = {
		list_counter: new Clist_counter({name:'entry', definition: this})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly tail:Cgui_widget_configuration_list
	};
	constructor(init:Tyes__has_steps__gui_widget_configuration_list, public parent:Cgui_widget_configuration_list) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cyes__has_steps__gui_widget_configuration_list.Dconfiguration(init['configuration'], $this),
			tail: new Cyes__has_steps__gui_widget_configuration_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_widget_configuration_attribute = {
	'type':['configuration', Tconfiguration__type]|['model binding', Tmodel_binding]|['view', Tview__type__gui_widget_configuration_attribute]|['widget', Twidget__type__gui_widget_configuration_attribute]|['window', Twindow__type__gui_widget_configuration_attribute];
};
export class Cgui_widget_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly type:Cgui_widget_configuration_attribute.Dtype
	};
	public readonly output:{
		gui_widget_configuration_node: () => views.Cgui_widget_configuration_node;
	} = {
		gui_widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_widget_configuration_node()).result!)
	};
	constructor(init:Tgui_widget_configuration_attribute, public location:AlanNode, public input: {
		client_binding: () => client_bindings.Cbinding_context,
		context: () => views.Ccontext,
		gui_widget_configuration_node: () => views.Cgui_widget_configuration_node,
		view: () => views.Cviews__,
		widget_configuration_attribute: () => widget.Cattributes__widget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cgui_widget_configuration_attribute.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tconfiguration__type = {
	'type':['list', Tlist]|['number', Tnumber__type__configuration]|['state group', Tstate_group__type__configuration]|['states list', Tstates_list__type]|['text', Ttext__type__configuration];
};
export class Cconfiguration__type extends AlanNode {
	public readonly properties:{
		readonly type:Cconfiguration__type.Dtype
	};
	public readonly inferences:{
		configuration: () => widget.Cconfiguration__type__attributes__widget_configuration_node
	} = {
		configuration: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.widget_configuration_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tconfiguration__type, public parent:Cgui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			type: new Cconfiguration__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist = {
	'list':Tgui_widget_configuration_list;
};
export class Clist extends AlanNode {
	public readonly properties:{
		readonly list:Cgui_widget_configuration_list
	};
	public readonly inferences:{
		list: () => widget.Clist__type
	} = {
		list: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration())
				.then(context => {
					if (context?.properties.type.state.name === 'list') {
						return resolve(context.properties.type.state.node as widget.Clist__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tlist, public parent:Cconfiguration__type) {
		super();
		const $this = this;
		this.properties = {
			list: new Clist.Dlist(init['list'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__configuration = {
	'source':['now', Tnow__source__number]|['static', Tstatic__source__number];
};
export class Cnumber__type__configuration extends AlanNode {
	public readonly properties:{
		readonly source:Cnumber__type__configuration.Dsource
	};
	public readonly inferences:{
		number: () => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node
	} = {
		number: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as widget.Cnumber__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__configuration, public parent:Cconfiguration__type) {
		super();
		const $this = this;
		this.properties = {
			source: new Cnumber__type__configuration.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow__source__number = {
	'has offset':'none'|['none', {}]|['yes', Tyes__has_offset__now__source__number];
};
export class Cnow__source__number extends AlanNode {
	public readonly properties:{
		readonly has_offset:Cnow__source__number.Dhas_offset
	};
	constructor(init:Tnow__source__number, public parent:Cnumber__type__configuration) {
		super();
		const $this = this;
		this.properties = {
			has_offset: new Cnow__source__number.Dhas_offset(init['has offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'now'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__has_offset__now__source__number = {
};
export class Cnone__has_offset__now__source__number extends AlanNode {
	constructor(init:Tnone__has_offset__now__source__number, public parent:Cnow__source__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has offset'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_offset__now__source__number = {
	'offset':number;
};
export class Cyes__has_offset__now__source__number extends AlanNode {
	public readonly properties:{
		readonly offset:Cyes__has_offset__now__source__number.Doffset
	};
	constructor(init:Tyes__has_offset__now__source__number, public parent:Cnow__source__number) {
		super();
		const $this = this;
		this.properties = {
			offset: new Cyes__has_offset__now__source__number.Doffset(init['offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has offset'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__source__number = {
	'number':number;
};
export class Cstatic__source__number extends AlanNode {
	public readonly properties:{
		readonly number:Cstatic__source__number.Dnumber
	};
	constructor(init:Tstatic__source__number, public parent:Cnumber__type__configuration) {
		super();
		const $this = this;
		this.properties = {
			number: new Cstatic__source__number.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__configuration = {
	'configuration':Tgui_widget_configuration_node;
	'state':string;
};
export class Cstate_group__type__configuration extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly state:Cstate_group__type__configuration.Dstate
	};
	public readonly inferences:{
		state_group: () => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node
	} = {
		state_group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__configuration, public parent:Cconfiguration__type) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cstate_group__type__configuration.Dconfiguration(init['configuration'], $this),
			state: new Cstate_group__type__configuration.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates_list__type = {
	'has states':'no'|['no', {}]|['yes', Tyes__has_states];
	'states':Record<string, Tstates__states_list>;
};
export class Cstates_list__type extends AlanNode {
	public readonly properties:{
		readonly has_states:Cstates_list__type.Dhas_states,
		readonly states:Cstates_list__type.Dstates
	};
	public readonly inferences:{
		list: () => widget.Clist__type,
		state_group_binding: () => views.Cstate_group__type__model_binding
	} = {
		list: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__states_list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration())
				.then(context => {
					if (context?.properties.type.state.name === 'list') {
						return resolve(context.properties.type.state.node as widget.Clist__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		state_group_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__states_list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'state group' ? context.variant.definition as views.Cstate_group__type__model_binding : undefined).result!;
		})

	}
	constructor(init:Tstates_list__type, public parent:Cconfiguration__type) {
		super();
		const $this = this;
		this.properties = {
			has_states: new Cstates_list__type.Dhas_states(init['has states'], $this),
			states: new Cstates_list__type.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'states list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_states = {
};
export class Cno__has_states extends AlanNode {
	constructor(init:Tno__has_states, public parent:Cstates_list__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has states'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_states = {
	'first':string;
};
export class Cyes__has_states extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_states.Dfirst
	};
	constructor(init:Tyes__has_states, public parent:Cstates_list__type) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_states.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has states'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__states_list extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates__states_list) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group_binding())
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__states_list = {
	'configuration':Tgui_widget_configuration_node;
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__states];
};
export class Cstates__states_list extends AlanDictionaryEntry {
	public key:Kstates__states_list;
	public get key_value() { return this.key.entry; }
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'state', definition: this})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly has_successor:Cstates__states_list.Dhas_successor
	};
	constructor(key:string, init:Tstates__states_list, public parent:Cstates_list__type) {
		super();
		const $this = this;
		this.key = new Kstates__states_list(key, $this);
		this.properties = {
			configuration: new Cstates__states_list.Dconfiguration(init['configuration'], $this),
			has_successor: new Cstates__states_list.Dhas_successor(init['has successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__states = {
};
export class Cno__has_successor__states extends AlanNode {
	constructor(init:Tno__has_successor__states, public parent:Cstates__states_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__states = {
	'successor':string;
};
export class Cyes__has_successor__states extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__states.Dsuccessor
	};
	constructor(init:Tyes__has_successor__states, public parent:Cstates__states_list) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__states.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__configuration = {
	'type':['key', Tkey]|['phrase', Tphrase]|['static', Tstatic__type];
};
export class Ctext__type__configuration extends AlanNode {
	public readonly properties:{
		readonly type:Ctext__type__configuration.Dtype
	};
	public readonly inferences:{
		text: () => widget.Ctext__type__configuration__type__attributes__widget_configuration_node
	} = {
		text: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as widget.Ctext__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__configuration, public parent:Cconfiguration__type) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext__type__configuration.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkey = {
	'translate':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Ckey extends AlanNode {
	public readonly properties:{
		readonly translate:Ckey.Dtranslate
	};
	public readonly inferences:{
		is_set: () => views.Ckey_context,
		key_context: () => views.Coptional_key_context
	} = {
		is_set: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__text__type__key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.key_context())
				.then(context => context?.variant.name === 'set' ? context.variant.definition as views.Ckey_context : undefined).result!;
		})
		,
		key_context: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__configuration__type__text__type__key_nval = this.parent;
			return resolve(this.parent)
				.then(context => evaluate__infer_key_context(detach, 
					resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.context()).result!, )).result!;
		})

	}
	constructor(init:Tkey, public parent:Ctext__type__configuration) {
		super();
		const $this = this;
		this.properties = {
			translate: new Ckey.Dtranslate(init['translate'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'key'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__translate = {
};
export class Cno__translate extends AlanNode {
	constructor(init:Tno__translate, public parent:Ckey) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'translate'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__translate = {
};
export class Cyes__translate extends AlanNode {
	constructor(init:Tyes__translate, public parent:Ckey) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'translate'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tphrase = {
	'value':string;
};
export class Cphrase extends AlanNode {
	public readonly properties:{
		readonly value:Cphrase.Dvalue
	};
	constructor(init:Tphrase, public parent:Ctext__type__configuration) {
		super();
		const $this = this;
		this.properties = {
			value: new Cphrase.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'phrase'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type = {
	'value':string;
};
export class Cstatic__type extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tstatic__type, public parent:Ctext__type__configuration) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmodel_binding = {
	'type':['action', Taction]|['collection', Tcollection__type__model_binding]|['command', Tcommand]|['current node', Tcurrent_node__type]|['entity', Tentity__type__model_binding]|['file', Tfile__type__model_binding]|['group', Tgroup__type__model_binding]|['joined entry', Tjoined_entry]|['number', Tnumber__type__model_binding]|['ordered graph', Tordered_graph]|['query', Tquery]|['query file', Tquery_file]|['query number', Tquery_number]|['query stategroup', Tquery_stategroup]|['query text', Tquery_text]|['report', Treport]|['report number parameter', Treport_number_parameter]|['report text parameter', Treport_text_parameter]|['sort property', Tsort_property]|['state', Tstate__type__model_binding]|['state group', Tstate_group__type__model_binding]|['state rule', Tstate_rule__type__model_binding]|['text', Ttext__type__model_binding]|['user', Tuser]|['window', Twindow__type__model_binding];
};
export class Cmodel_binding extends AlanNode {
	public readonly properties:{
		readonly type:Cmodel_binding.Dtype
	};
	public readonly inferences:{
		model_binding: () => widget.Cbinding__type__attributes
	} = {
		model_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.widget_configuration_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'binding') {
						return resolve(context.properties.type.state.node as widget.Cbinding__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmodel_binding, public parent:Cgui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			type: new Cmodel_binding.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'model binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Taction = {
	'action':string;
	'can use containing view':'no'|['no', {}]|'yes'|['yes', {}];
	'configuration':Tgui_widget_configuration_node;
	'property path':Tmodel_binding_property_path;
	'view bindings':Record<string, Tview_bindings>;
};
export class Caction extends AlanNode {
	public definitions:{
		model_context: Cmodel_context;
	} = {
		model_context: new Cmodel_context({name:'action', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.action?.ref)
				.then(context => context?.properties.parameters)
				.then(context => context?.definitions.gui_context).result!)
		})
	}
	public readonly properties:{
		readonly action:Caction.Daction,
		readonly can_use_containing_view:Caction.Dcan_use_containing_view,
		readonly configuration:Cgui_widget_configuration_node,
		readonly property_path:Cmodel_binding_property_path,
		readonly view_bindings:Caction.Dview_bindings
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		operation: () => client_bindings.Coperation__type
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		operation: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'operation') {
								return context.properties.type.state.node as client_bindings.Coperation__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Taction, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			action: new Caction.Daction(init['action'], $this),
			can_use_containing_view: new Caction.Dcan_use_containing_view(init['can use containing view'], $this),
			configuration: new Caction.Dconfiguration(init['configuration'], $this),
			property_path: new Caction.Dproperty_path(init['property path'], $this),
			view_bindings: new Caction.Dview_bindings(init['view bindings'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'action'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_use_containing_view = {
};
export class Cno__can_use_containing_view extends AlanNode {
	constructor(init:Tno__can_use_containing_view, public parent:Caction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can use containing view'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_use_containing_view = {
};
export class Cyes__can_use_containing_view extends AlanNode {
	constructor(init:Tyes__can_use_containing_view, public parent:Caction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can use containing view'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kview_bindings extends Reference<gui_model.Cviews, string> {
	constructor(key:string, $this:Cview_bindings) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.action?.ref)
			.then(context => {
				const entry = context?.properties.views.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'view bindings'::key`; }
}
export type Tview_bindings = {
	'can open entry':'no'|['no', {}]|['yes', Tyes__can_open_entry];
	'using views':['external', Texternal__using_views__view_bindings]|'internal'|['internal', {}];
	'view':string;
	'window':string;
};
export class Cview_bindings extends AlanDictionaryEntry {
	public key:Kview_bindings;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly can_open_entry:Cview_bindings.Dcan_open_entry,
		readonly using_views:Cview_bindings.Dusing_views,
		readonly view:Cview_bindings.Dview,
		readonly window:Cview_bindings.Dwindow
	};
	constructor(key:string, init:Tview_bindings, public parent:Caction) {
		super();
		const $this = this;
		this.key = new Kview_bindings(key, $this);
		this.properties = {
			can_open_entry: new Cview_bindings.Dcan_open_entry(init['can open entry'], $this),
			using_views: new Cview_bindings.Dusing_views(init['using views'], $this),
			view: new Cview_bindings.Dview(init['view'], $this),
			window: new Cview_bindings.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'view bindings'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__can_open_entry = {
};
export class Cno__can_open_entry extends AlanNode {
	constructor(init:Tno__can_open_entry, public parent:Cview_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can open entry'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_open_entry = {
	'using views':['external', Texternal__using_views__yes]|'internal'|['internal', {}];
	'view':string;
	'window':string;
};
export class Cyes__can_open_entry extends AlanNode {
	public readonly properties:{
		readonly using_views:Cyes__can_open_entry.Dusing_views,
		readonly view:Cyes__can_open_entry.Dview,
		readonly window:Cyes__can_open_entry.Dwindow
	};
	constructor(init:Tyes__can_open_entry, public parent:Cview_bindings) {
		super();
		const $this = this;
		this.properties = {
			using_views: new Cyes__can_open_entry.Dusing_views(init['using views'], $this),
			view: new Cyes__can_open_entry.Dview(init['view'], $this),
			window: new Cyes__can_open_entry.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can open entry'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Texternal__using_views__yes = {
	'views':string;
};
export class Cexternal__using_views__yes extends AlanNode {
	public readonly properties:{
		readonly views:Cexternal__using_views__yes.Dviews
	};
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Texternal__using_views__yes, public parent:Cyes__can_open_entry) {
		super();
		const $this = this;
		this.properties = {
			views: new Cexternal__using_views__yes.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'external'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinternal__using_views__yes = {
};
export class Cinternal__using_views__yes extends AlanNode {
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root).result!)
	}
	constructor(init:Tinternal__using_views__yes, public parent:Cyes__can_open_entry) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'internal'`; }
	public get entity() { return this.parent.entity; }
}
export type Texternal__using_views__view_bindings = {
	'views':string;
};
export class Cexternal__using_views__view_bindings extends AlanNode {
	public readonly properties:{
		readonly views:Cexternal__using_views__view_bindings.Dviews
	};
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Texternal__using_views__view_bindings, public parent:Cview_bindings) {
		super();
		const $this = this;
		this.properties = {
			views: new Cexternal__using_views__view_bindings.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'external'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinternal__using_views__view_bindings = {
};
export class Cinternal__using_views__view_bindings extends AlanNode {
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root).result!)
	}
	constructor(init:Tinternal__using_views__view_bindings, public parent:Cview_bindings) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'internal'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__model_binding = {
	'collection':string;
	'configuration':Tgui_widget_configuration_node;
	'join':'no'|['no', {}]|'yes'|['yes', {}];
	'property path':Tmodel_binding_property_path;
};
export class Ccollection__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__type__model_binding.Dcollection,
		readonly configuration:Cgui_widget_configuration_node,
		readonly join:Ccollection__type__model_binding.Djoin,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tcollection__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__type__model_binding.Dcollection(init['collection'], $this),
			configuration: new Ccollection__type__model_binding.Dconfiguration(init['configuration'], $this),
			join: new Ccollection__type__model_binding.Djoin(init['join'], $this),
			property_path: new Ccollection__type__model_binding.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__join = {
};
export class Cno__join extends AlanNode {
	public readonly output:{
		context: () => views.Ccontext;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__gui_node_to_context(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!, )).result!)
	}
	public readonly inferences:{
		collection_binding: () => client_bindings.Ccollection__type__node
	} = {
		collection_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__collection__join__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return context.properties.type.state.node as client_bindings.Ccollection__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__join, public parent:Ccollection__type__model_binding) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'join'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__join = {
};
export class Cyes__join extends AlanNode {
	public definitions:{
		join_context: Cjoin_context;
	} = {
		join_context: new Cjoin_context({name:'join', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(context => evaluate__gui_node_to_context(detach, 
					resolve(context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.properties.collection?.ref)
						.then(context => context?.properties.node).result!, )).result!),
			referenced_model_context: cache((detach:boolean) => resolve(this)
				.then(context => evaluate__gui_node_to_context(detach, 
					resolve(context)
						.then(() => this)
						.then(context => context?.inferences.navigable())
						.then(context => context?.properties.referenced_node)
						.then(context => context?.component_root.output.result_gui_node()).result!, )).result!)
		})
	}
	public readonly output:{
		context: () => views.Ccontext;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.join_context)
			.then(context => context?.definitions.context).result!)
	}
	public readonly inferences:{
		join_binding: () => client_bindings.Cjoin__type,
		navigable: () => gui_model.Cyes__navigable
	} = {
		join_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__collection__join__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'join') {
								return context.properties.type.state.node as client_bindings.Cjoin__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		navigable: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__collection__join__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.collection?.ref)
				.then(context => context?.properties.node?.inferences.text_key())
				.then(context => {
					if (context?.properties.navigable.state.name === 'yes') {
						return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__join, public parent:Ccollection__type__model_binding) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'join'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand = {
	'command':string;
	'configuration':Tgui_widget_configuration_node;
	'property path':Tmodel_binding_property_path;
};
export class Ccommand extends AlanNode {
	public readonly properties:{
		readonly command:Ccommand.Dcommand,
		readonly configuration:Cgui_widget_configuration_node,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		operation: () => client_bindings.Coperation__type
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		operation: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'operation') {
								return context.properties.type.state.node as client_bindings.Coperation__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			command: new Ccommand.Dcommand(init['command'], $this),
			configuration: new Ccommand.Dconfiguration(init['configuration'], $this),
			property_path: new Ccommand.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_node__type = {
	'configuration':Tgui_widget_configuration_node;
	'property path':Tmodel_binding_property_path;
};
export class Ccurrent_node__type extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		node: () => client_bindings.Cnode__type__node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__current_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__current_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__current_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'node') {
								return context.properties.type.state.node as client_bindings.Cnode__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcurrent_node__type, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Ccurrent_node__type.Dconfiguration(init['configuration'], $this),
			property_path: new Ccurrent_node__type.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'current node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentity__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
};
export class Centity__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node
	};
	public readonly inferences:{
		gui_entity: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		node: () => client_bindings.Centity__type
	} = {
		gui_entity: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__entity_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__entity_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__entity_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'entity') {
								return context.properties.type.state.node as client_bindings.Centity__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentity__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Centity__type__model_binding.Dconfiguration(init['configuration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entity'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'property':string;
	'property path':Tmodel_binding_property_path;
};
export class Cfile__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly property:Cfile__type__model_binding.Dproperty,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		file: () => client_bindings.Cfile__type__node,
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		file: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'file') {
								return context.properties.type.state.node as client_bindings.Cfile__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tfile__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cfile__type__model_binding.Dconfiguration(init['configuration'], $this),
			property: new Cfile__type__model_binding.Dproperty(init['property'], $this),
			property_path: new Cfile__type__model_binding.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'group':string;
};
export class Cgroup__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly group:Cgroup__type__model_binding.Dgroup
	};
	public readonly inferences:{
		group: () => client_bindings.Cgroup__type,
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context
	} = {
		group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'group') {
								return context.properties.type.state.node as client_bindings.Cgroup__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})

	}
	constructor(init:Tgroup__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cgroup__type__model_binding.Dconfiguration(init['configuration'], $this),
			group: new Cgroup__type__model_binding.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tjoined_entry = {
	'configuration':Tgui_widget_configuration_node;
};
export class Cjoined_entry extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node
	};
	public readonly inferences:{
		join: () => views.Cjoin_context,
		join_binding: () => client_bindings.Cjoined_entry__type
	} = {
		join: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__joined_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'join' ? context.variant.definition as views.Cjoin_context : undefined).result!;
		})
		,
		join_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__joined_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'joined entry') {
								return context.properties.type.state.node as client_bindings.Cjoined_entry__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tjoined_entry, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cjoined_entry.Dconfiguration(init['configuration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'joined entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'property':string;
	'property path':Tmodel_binding_property_path;
};
export class Cnumber__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly property:Cnumber__type__model_binding.Dproperty,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		number: () => client_bindings.Cnumber__type__node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		number: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return context.properties.type.state.node as client_bindings.Cnumber__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cnumber__type__model_binding.Dconfiguration(init['configuration'], $this),
			property: new Cnumber__type__model_binding.Dproperty(init['property'], $this),
			property_path: new Cnumber__type__model_binding.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordered_graph = {
	'configuration':Tgui_widget_configuration_node;
	'ordered graph':string;
};
export class Cordered_graph extends AlanNode {
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'ordered graph', definition: this})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly ordered_graph:Cordered_graph.Dordered_graph
	};
	public readonly inferences:{
		collection: () => gui_model.Ccollection__type__property,
		gui_node: () => gui_model.Cgui_node,
		has_constraints: () => gui_model.Cyes__has_ordered_graphs,
		model: () => views.Centry_or_node_context,
		ordered_graph: () => client_bindings.Cordered_graph__type
	} = {
		collection: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as gui_model.Ccollection__type__property : undefined).result!;
		})
		,
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		has_constraints: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => {
					if (context?.properties.has_ordered_graphs.state.name === 'yes') {
						return resolve(context.properties.has_ordered_graphs.state.node as gui_model.Cyes__has_ordered_graphs).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		ordered_graph: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__ordered_graph_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'ordered graph') {
								return context.properties.type.state.node as client_bindings.Cordered_graph__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tordered_graph, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cordered_graph.Dconfiguration(init['configuration'], $this),
			ordered_graph: new Cordered_graph.Dordered_graph(init['ordered graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'ordered graph'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery = {
	'configuration':Tgui_widget_configuration_node;
	'has refresh interval':'no'|['no', {}]|['yes', Tyes__has_refresh_interval];
	'query context':['candidates', Tcandidates__query_context]|['node', Tnode__query_context];
};
export class Cquery extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly has_refresh_interval:Cquery.Dhas_refresh_interval,
		readonly query_context:Cquery.Dquery_context
	};
	constructor(init:Tquery, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cquery.Dconfiguration(init['configuration'], $this),
			has_refresh_interval: new Cquery.Dhas_refresh_interval(init['has refresh interval'], $this),
			query_context: new Cquery.Dquery_context(init['query context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_refresh_interval = {
};
export class Cno__has_refresh_interval extends AlanNode {
	constructor(init:Tno__has_refresh_interval, public parent:Cquery) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has refresh interval'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_refresh_interval = {
	'interval':number;
};
export class Cyes__has_refresh_interval extends AlanNode {
	public readonly properties:{
		readonly interval:Cyes__has_refresh_interval.Dinterval
	};
	constructor(init:Tyes__has_refresh_interval, public parent:Cquery) {
		super();
		const $this = this;
		this.properties = {
			interval: new Cyes__has_refresh_interval.Dinterval(init['interval'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has refresh interval'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates__query_context = {
	'query':string;
};
export class Ccandidates__query_context extends AlanNode {
	public readonly properties:{
		readonly query:Ccandidates__query_context.Dquery
	};
	public readonly output:{
		query: () => views.Cqueries;
	} = {
		query: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query?.inferences.query())
			.then(context => context?.parent).result!)
	}
	public readonly inferences:{
		model: () => views.Centry_or_node_context,
		navigable: () => gui_model.Cyes__navigable,
		text: () => views.Ctext__type__model_binding
	} = {
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		navigable: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.text())
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.navigable.state.name === 'yes') {
						return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		text: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Cmodel_context : undefined)
				.then(context => context?.variant.name === 'text' ? context.variant.definition as views.Ctext__type__model_binding : undefined).result!;
		})

	}
	constructor(init:Tcandidates__query_context, public parent:Cquery) {
		super();
		const $this = this;
		this.properties = {
			query: new Ccandidates__query_context.Dquery(init['query'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query context'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__query_context = {
	'switch':['current node', Tcurrent_node__switch]|['root', Troot__switch__node__query_context];
};
export class Cnode__query_context extends AlanNode {
	public readonly properties:{
		readonly switch_:Cnode__query_context.Dswitch_
	};
	public readonly output:{
		query: () => views.Cqueries;
	} = {
		query: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.switch_?.state.node.output.query()).result!)
	}
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		query: () => client_bindings.Cquery__type__node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		query: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'query') {
								return context.properties.type.state.node as client_bindings.Cquery__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__query_context, public parent:Cquery) {
		super();
		const $this = this;
		this.properties = {
			switch_: new Cnode__query_context.Dswitch_(init['switch'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query context'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_node__switch = {
	'property path':Tmodel_binding_property_path;
	'query':string;
};
export class Ccurrent_node__switch extends AlanNode {
	public readonly properties:{
		readonly property_path:Cmodel_binding_property_path,
		readonly query:Ccurrent_node__switch.Dquery
	};
	public readonly output:{
		query: () => views.Cqueries;
	} = {
		query: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query?.inferences.query())
			.then(context => context?.parent)
			.then(context => context?.parent).result!)
	}
	constructor(init:Tcurrent_node__switch, public parent:Cnode__query_context) {
		super();
		const $this = this;
		this.properties = {
			property_path: new Ccurrent_node__switch.Dproperty_path(init['property path'], $this),
			query: new Ccurrent_node__switch.Dquery(init['query'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch'?'current node'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot__switch__node__query_context = {
	'query':string;
};
export class Croot__switch__node__query_context extends AlanNode {
	public readonly properties:{
		readonly query:Croot__switch__node__query_context.Dquery
	};
	public readonly output:{
		query: () => views.Cqueries;
	} = {
		query: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query?.inferences.query())
			.then(context => context?.parent)
			.then(context => context?.parent).result!)
	}
	constructor(init:Troot__switch__node__query_context, public parent:Cnode__query_context) {
		super();
		const $this = this;
		this.properties = {
			query: new Croot__switch__node__query_context.Dquery(init['query'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_file = {
	'configuration':Tgui_widget_configuration_node;
	'file':string;
};
export class Cquery_file extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly file:Cquery_file.Dfile
	};
	public readonly inferences:{
		query: () => views.Cqueries,
		query_file: () => client_bindings.Cfile__type__query
	} = {
		query: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'query' ? context.variant.definition as views.Cqueries : undefined).result!;
		})
		,
		query_file: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'query') {
						return resolve(context.properties.type.state.node as client_bindings.Cquery__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'file') {
								return context.properties.type.state.node as client_bindings.Cfile__type__query;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery_file, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cquery_file.Dconfiguration(init['configuration'], $this),
			file: new Cquery_file.Dfile(init['file'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_number = {
	'configuration':Tgui_widget_configuration_node;
	'number':string;
};
export class Cquery_number extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly number:Cquery_number.Dnumber
	};
	public readonly inferences:{
		query: () => views.Cqueries,
		query_number: () => client_bindings.Cnumber__type__query
	} = {
		query: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'query' ? context.variant.definition as views.Cqueries : undefined).result!;
		})
		,
		query_number: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'query') {
						return resolve(context.properties.type.state.node as client_bindings.Cquery__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return context.properties.type.state.node as client_bindings.Cnumber__type__query;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery_number, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cquery_number.Dconfiguration(init['configuration'], $this),
			number: new Cquery_number.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_stategroup = {
	'configuration':Tgui_widget_configuration_node;
	'stategroup':string;
};
export class Cquery_stategroup extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly stategroup:Cquery_stategroup.Dstategroup
	};
	public readonly inferences:{
		query: () => views.Cqueries,
		query_stategroup: () => client_bindings.Cstategroup
	} = {
		query: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_stategroup_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'query' ? context.variant.definition as views.Cqueries : undefined).result!;
		})
		,
		query_stategroup: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_stategroup_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'query') {
						return resolve(context.properties.type.state.node as client_bindings.Cquery__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'stategroup') {
								return context.properties.type.state.node as client_bindings.Cstategroup;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery_stategroup, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cquery_stategroup.Dconfiguration(init['configuration'], $this),
			stategroup: new Cquery_stategroup.Dstategroup(init['stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query stategroup'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_text = {
	'configuration':Tgui_widget_configuration_node;
	'text':string;
};
export class Cquery_text extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly text:Cquery_text.Dtext
	};
	public readonly inferences:{
		query: () => views.Cqueries,
		query_text: () => client_bindings.Ctext__type__query
	} = {
		query: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined)
				.then(context => context?.variant.name === 'query' ? context.variant.definition as views.Cqueries : undefined).result!;
		})
		,
		query_text: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__query_text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'query') {
						return resolve(context.properties.type.state.node as client_bindings.Cquery__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as client_bindings.Ctext__type__query;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery_text, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cquery_text.Dconfiguration(init['configuration'], $this),
			text: new Cquery_text.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'query text'`; }
	public get entity() { return this.parent.entity; }
}
export type Treport = {
	'configuration':Tgui_widget_configuration_node;
	'report':string;
};
export class Creport extends AlanNode {
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'report', definition: this})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly report:Creport.Dreport
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		report: () => client_bindings.Cnode__type__report
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		report: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'report') {
						return resolve(context.properties.type.state.node as client_bindings.Creport)
						.then(context => {
							if (context?.properties.type.state.name === 'node') {
								return context.properties.type.state.node as client_bindings.Cnode__type__report;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Treport, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Creport.Dconfiguration(init['configuration'], $this),
			report: new Creport.Dreport(init['report'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'report'`; }
	public get entity() { return this.parent.entity; }
}
export type Treport_number_parameter = {
	'configuration':Tgui_widget_configuration_node;
	'number':string;
};
export class Creport_number_parameter extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly number:Creport_number_parameter.Dnumber
	};
	public readonly inferences:{
		report: () => views.Creport,
		report_number: () => client_bindings.Cnumber__type__report
	} = {
		report: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_number_parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'report' ? context.variant.definition as views.Creport : undefined).result!;
		})
		,
		report_number: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_number_parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'report') {
						return resolve(context.properties.type.state.node as client_bindings.Creport)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return context.properties.type.state.node as client_bindings.Cnumber__type__report;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Treport_number_parameter, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Creport_number_parameter.Dconfiguration(init['configuration'], $this),
			number: new Creport_number_parameter.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'report number parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Treport_text_parameter = {
	'configuration':Tgui_widget_configuration_node;
	'text':string;
};
export class Creport_text_parameter extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly text:Creport_text_parameter.Dtext
	};
	public readonly inferences:{
		report: () => views.Creport,
		report_text: () => client_bindings.Ctext__type__report
	} = {
		report: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_text_parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'report' ? context.variant.definition as views.Creport : undefined).result!;
		})
		,
		report_text: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__report_text_parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'report') {
						return resolve(context.properties.type.state.node as client_bindings.Creport)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as client_bindings.Ctext__type__report;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Treport_text_parameter, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Creport_text_parameter.Dconfiguration(init['configuration'], $this),
			text: new Creport_text_parameter.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'report text parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsort_property = {
	'configuration':Tgui_widget_configuration_node;
	'on':'number'|['number', {}]|'state group'|['state group', {}]|'text'|['text', {}];
	'property':string;
	'property path':Tmodel_binding_property_path;
};
export class Csort_property extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly on:Csort_property.Don,
		readonly property:Csort_property.Dproperty,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		collection: () => gui_model.Ccollection__type__property,
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		sort_property: () => client_bindings.Csort_property__type
	} = {
		collection: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as gui_model.Ccollection__type__property : undefined).result!;
		})
		,
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		sort_property: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'sort property') {
								return context.properties.type.state.node as client_bindings.Csort_property__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsort_property, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Csort_property.Dconfiguration(init['configuration'], $this),
			on: new Csort_property.Don(init['on'], $this),
			property: new Csort_property.Dproperty(init['property'], $this),
			property_path: new Csort_property.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sort property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__on = {
};
export class Cnumber__on extends AlanNode {
	public readonly inferences:{
		state_group: () => gui_model.Cnumber__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property__on__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__on, public parent:Csort_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'on'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__on = {
};
export class Cstate_group__on extends AlanNode {
	public readonly inferences:{
		state_group: () => gui_model.Cstate_group__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property__on__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__on, public parent:Csort_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'on'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__on = {
};
export class Ctext__on extends AlanNode {
	public readonly inferences:{
		state_group: () => gui_model.Ctext__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__sort_property__on__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__on, public parent:Csort_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'on'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'state group context':['state group binding', Tstate_group_binding]|'states list'|['states list', {}];
};
export class Cstate__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly state_group_context:Cstate__type__model_binding.Dstate_group_context
	};
	public readonly inferences:{
		state: () => client_bindings.Cstate__type__node
	} = {
		state: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'state') {
								return context.properties.type.state.node as client_bindings.Cstate__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cstate__type__model_binding.Dconfiguration(init['configuration'], $this),
			state_group_context: new Cstate__type__model_binding.Dstate_group_context(init['state group context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group_binding = {
	'state':string;
};
export class Cstate_group_binding extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group_binding.Dstate
	};
	public readonly output:{
		state_node_context: () => gui_model.Cgui_node;
	} = {
		state_node_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		state_group_binding: () => views.Cstate_group__type__model_binding
	} = {
		state_group_binding: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state__state_group_context__state_group_binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'state group' ? context.variant.definition as views.Cstate_group__type__model_binding : undefined).result!;
		})

	}
	constructor(init:Tstate_group_binding, public parent:Cstate__type__model_binding) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group_binding.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'state group context'?'state group binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates_list__state_group_context = {
};
export class Cstates_list__state_group_context extends AlanNode {
	public readonly output:{
		state_node_context: () => gui_model.Cgui_node;
	} = {
		state_node_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.state_context())
			.then(context => context?.key?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		state_context: () => views.Cstates__states_list
	} = {
		state_context: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state__state_group_context__states_list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as views.Cstates__states_list : undefined).result!;
		})

	}
	constructor(init:Tstates_list__state_group_context, public parent:Cstate__type__model_binding) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'state group context'?'states list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'property path':Tmodel_binding_property_path;
	'state group':string;
};
export class Cstate_group__type__model_binding extends AlanNode {
	public definitions:{
		context: Ccontext;
	} = {
		context: new Ccontext({name:'state group', definition: this})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly property_path:Cmodel_binding_property_path,
		readonly state_group:Cstate_group__type__model_binding.Dstate_group
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		state_group: () => client_bindings.Cstate_group__type__node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		state_group: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return context.properties.type.state.node as client_bindings.Cstate_group__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cstate_group__type__model_binding.Dconfiguration(init['configuration'], $this),
			property_path: new Cstate_group__type__model_binding.Dproperty_path(init['property path'], $this),
			state_group: new Cstate_group__type__model_binding.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'rule':string;
};
export class Cstate_rule__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly rule:Cstate_rule__type__model_binding.Drule
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		state: () => gui_model.Cstates__state_group__type__property__type__attributes,
		where_rule: () => client_bindings.Cwhere_rule__type
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		state: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.gui_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as gui_model.Cstates__state_group__type__property__type__attributes : undefined).result!;
		})
		,
		where_rule: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'where rule') {
								return context.properties.type.state.node as client_bindings.Cwhere_rule__type;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_rule__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cstate_rule__type__model_binding.Dconfiguration(init['configuration'], $this),
			rule: new Cstate_rule__type__model_binding.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'property':string;
	'property path':Tmodel_binding_property_path;
};
export class Ctext__type__model_binding extends AlanNode {
	public definitions:{
		model_context: Cmodel_context;
	} = {
		model_context: new Cmodel_context({name:'text', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.property?.ref)
				.then(context => context?.properties.navigable?.state.node.output.context()).result!)
		})
	}
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly property:Ctext__type__model_binding.Dproperty,
		readonly property_path:Cmodel_binding_property_path
	};
	public readonly inferences:{
		gui_node: () => gui_model.Cgui_node,
		model: () => views.Centry_or_node_context,
		text: () => client_bindings.Ctext__type__node
	} = {
		gui_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.model())
				.then(context => context?.component_root.output.model_context())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		model: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.default_context())
				.then(context => context?.variant.name === 'model' ? context.variant.definition as views.Centry_or_node_context : undefined).result!;
		})
		,
		text: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'node') {
						return resolve(context.properties.type.state.node as client_bindings.Cnode__type__bindings)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as client_bindings.Ctext__type__node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Ctext__type__model_binding.Dconfiguration(init['configuration'], $this),
			property: new Ctext__type__model_binding.Dproperty(init['property'], $this),
			property_path: new Ctext__type__model_binding.Dproperty_path(init['property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser = {
	'configuration':Tgui_widget_configuration_node;
};
export class Cuser extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node
	};
	public readonly inferences:{
		has_user_node: () => gui_model.Cyes__has_user_node,
		user: () => client_bindings.Cuser__type
	} = {
		has_user_node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.gui_model)
				.then(context => {
					if (context?.properties.has_user_node.state.name === 'yes') {
						return resolve(context.properties.has_user_node.state.node as gui_model.Cyes__has_user_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		user: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'user') {
						return resolve(context.properties.type.state.node as client_bindings.Cuser__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tuser, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cuser.Dconfiguration(init['configuration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow__type__model_binding = {
	'configuration':Tgui_widget_configuration_node;
	'window':string;
};
export class Cwindow__type__model_binding extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly window:Cwindow__type__model_binding.Dwindow
	};
	public readonly inferences:{
		node: () => client_bindings.Cwindow__type
	} = {
		node: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__model_binding__type__window_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.model_binding())
				.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
				.then(context => {
					if (context?.properties.type.state.name === 'window') {
						return resolve(context.properties.type.state.node as client_bindings.Cwindow__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twindow__type__model_binding, public parent:Cmodel_binding) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cwindow__type__model_binding.Dconfiguration(init['configuration'], $this),
			window: new Cwindow__type__model_binding.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type__gui_widget_configuration_attribute = {
	'render':['in window', Tin_window];
	'using views':['external', Texternal__using_views__view__type__gui_widget_configuration_attribute]|'internal'|['internal', {}];
	'view':string;
	'view context':Tview_context_path;
};
export class Cview__type__gui_widget_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly render:Cview__type__gui_widget_configuration_attribute.Drender,
		readonly using_views:Cview__type__gui_widget_configuration_attribute.Dusing_views,
		readonly view:Cview__type__gui_widget_configuration_attribute.Dview,
		readonly view_context:Cview_context_path
	};
	constructor(init:Tview__type__gui_widget_configuration_attribute, public parent:Cgui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			render: new Cview__type__gui_widget_configuration_attribute.Drender(init['render'], $this),
			using_views: new Cview__type__gui_widget_configuration_attribute.Dusing_views(init['using views'], $this),
			view: new Cview__type__gui_widget_configuration_attribute.Dview(init['view'], $this),
			view_context: new Cview__type__gui_widget_configuration_attribute.Dview_context(init['view context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Tin_window = {
	'window':string;
};
export class Cin_window extends AlanNode {
	public readonly properties:{
		readonly window:Cin_window.Dwindow
	};
	public readonly inferences:{
		open_view: () => widget.Cview__type__attributes
	} = {
		open_view: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__view__render__in_window_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.widget_configuration_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'view') {
						return resolve(context.properties.type.state.node as widget.Cview__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tin_window, public parent:Cview__type__gui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			window: new Cin_window.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'render'?'in window'`; }
	public get entity() { return this.parent.entity; }
}
export type Texternal__using_views__view__type__gui_widget_configuration_attribute = {
	'views':string;
};
export class Cexternal__using_views__view__type__gui_widget_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly views:Cexternal__using_views__view__type__gui_widget_configuration_attribute.Dviews
	};
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.views?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Texternal__using_views__view__type__gui_widget_configuration_attribute, public parent:Cview__type__gui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			views: new Cexternal__using_views__view__type__gui_widget_configuration_attribute.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'external'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinternal__using_views__view__type__gui_widget_configuration_attribute = {
};
export class Cinternal__using_views__view__type__gui_widget_configuration_attribute extends AlanNode {
	public readonly output:{
		definition: () => views.Cviews;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root).result!)
	}
	constructor(init:Tinternal__using_views__view__type__gui_widget_configuration_attribute, public parent:Cview__type__gui_widget_configuration_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'using views'?'internal'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type__gui_widget_configuration_attribute = {
	'instance':Twidget_expression;
};
export class Cwidget__type__gui_widget_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly instance:Cwidget_expression
	};
	public readonly inferences:{
		widget: () => widget.Cwidget__type
	} = {
		widget: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__widget_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.widget_configuration_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'widget') {
						return resolve(context.properties.type.state.node as widget.Cwidget__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twidget__type__gui_widget_configuration_attribute, public parent:Cgui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			instance: new Cwidget__type__gui_widget_configuration_attribute.Dinstance(init['instance'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow__type__gui_widget_configuration_attribute = {
	'configuration':Tgui_widget_configuration_node;
	'window':string;
};
export class Cwindow__type__gui_widget_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly configuration:Cgui_widget_configuration_node,
		readonly window:Cwindow__type__gui_widget_configuration_attribute.Dwindow
	};
	public readonly inferences:{
		window: () => widget.Cwindow__type
	} = {
		window: cache((detach:boolean) => {
			const views__gui_widget_configuration_attribute__type__window_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.widget_configuration_attribute())
				.then(context => {
					if (context?.properties.type.state.name === 'window') {
						return resolve(context.properties.type.state.node as widget.Cwindow__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twindow__type__gui_widget_configuration_attribute, public parent:Cgui_widget_configuration_attribute) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cwindow__type__gui_widget_configuration_attribute.Dconfiguration(init['configuration'], $this),
			window: new Cwindow__type__gui_widget_configuration_attribute.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_node_type_path_step = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__gui_node_type_path_step];
};
export class Cgui_node_type_path_step extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgui_node_type_path_step.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tgui_node_type_path_step, public location:AlanNode, public input: {
		context_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgui_node_type_path_step.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__gui_node_type_path_step = {
};
export class Cno__has_steps__gui_node_type_path_step extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__gui_node_type_path_step, public parent:Cgui_node_type_path_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__gui_node_type_path_step = {
	'attribute':string;
	'tail':Tgui_node_type_path_step;
	'type':'node'|['node', {}]|['state', Tstate__type__yes__has_steps__gui_node_type_path_step];
};
export class Cyes__has_steps__gui_node_type_path_step extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_steps__gui_node_type_path_step.Dattribute,
		readonly tail:Cgui_node_type_path_step,
		readonly type:Cyes__has_steps__gui_node_type_path_step.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__gui_node_type_path_step, public parent:Cgui_node_type_path_step) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_steps__gui_node_type_path_step.Dattribute(init['attribute'], $this),
			tail: new Cyes__has_steps__gui_node_type_path_step.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__gui_node_type_path_step.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type__yes = {
};
export class Cnode__type__yes extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		node: () => gui_model.Cgui_node,
		optional_node: () => views.Coptional_node
	} = {
		node: cache((detach:boolean) => {
			const views__gui_node_type_path_step__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.optional_node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		optional_node: cache((detach:boolean) => {
			const views__gui_node_type_path_step__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => context?.properties.type)
				.then(context => {
					const state_group = context;
					switch (state_group?.state.name) {
						case 'action': {
							const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___action_nval = state_group.cast('action');
							return resolve(context)
								.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___action_nval)
								.then(context => context?.properties.parameters)
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Coptional_node({name: 'node', definition: conv_context});
								}).result;
						}
						case 'command': {
							const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___command_nval = state_group.cast('command');
							return resolve(context)
								.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___command_nval)
								.then(context => context?.properties.type)
								.then(context => {
									const state_group = context;
									switch (state_group?.state.name) {
										case 'component': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => views.Coptional_node.Pnone).result;
										}
										case 'global': {
											const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___command___global_nval = state_group.cast('global');
											return resolve(context)
												.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___command___global_nval)
												.then(context => context?.properties.parameters)
												.then(context => {
													const conv_context = resolve(context).result!;
													return new Coptional_node({name: 'node', definition: conv_context});
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
									};}).result;
						}
						case 'property': {
							const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property_nval = state_group.cast('property');
							return resolve(context)
								.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property_nval)
								.then(context => context?.properties.type)
								.then(context => {
									const state_group = context;
									switch (state_group?.state.name) {
										case 'collection': {
											const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property___collection_nval = state_group.cast('collection');
											return resolve(context)
												.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property___collection_nval)
												.then(context => context?.properties.node)
												.then(context => {
													const conv_context = resolve(context).result!;
													return new Coptional_node({name: 'node', definition: conv_context});
												}).result;
										}
										case 'file': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => views.Coptional_node.Pnone).result;
										}
										case 'group': {
											const views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property___group_nval = state_group.cast('group');
											return resolve(context)
												.then(context => views__gui_node_type_path_step__has_steps__yes__type__node_inf___optional_node___property___group_nval)
												.then(context => context?.properties.gui_node)
												.then(context => {
													const conv_context = resolve(context).result!;
													return new Coptional_node({name: 'node', definition: conv_context});
												}).result;
										}
										case 'number': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => views.Coptional_node.Pnone).result;
										}
										case 'state group': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => views.Coptional_node.Pnone).result;
										}
										case 'text': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => views.Coptional_node.Pnone).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
									};}).result;
						}
						case 'reference set': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => views.Coptional_node.Pnone).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
					};}).result!;
		})

	}
	constructor(init:Tnode__type__yes, public parent:Cyes__has_steps__gui_node_type_path_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__gui_node_type_path_step = {
	'state':string;
};
export class Cstate__type__yes__has_steps__gui_node_type_path_step extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__gui_node_type_path_step.Dstate
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		state_group: () => gui_model.Cstate_group__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const views__gui_node_type_path_step__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate__type__yes__has_steps__gui_node_type_path_step, public parent:Cyes__has_steps__gui_node_type_path_step) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__gui_node_type_path_step.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_node_type_path = {
	'steps':Tgui_node_type_path_step;
};
export class Cgui_node_type_path extends AlanNode {
	public readonly properties:{
		readonly steps:Cgui_node_type_path_step
	};
	public readonly output:{
		result_gui_node: () => gui_model.Cgui_node;
	} = {
		result_gui_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.steps)
			.then(context => context?.component_root.output.result_node()).result!)
	};
	constructor(init:Tgui_node_type_path, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			steps: new Cgui_node_type_path.Dsteps(init['steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__gui_node_to_context(detach:boolean, views__gui_node_to_context_nval:gui_model.Cgui_node, ):views.Ccontext {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => views__gui_node_to_context_nval)
		.then(context => {
			const conv_context = resolve(context)
				.then(context => context?.definitions.gui_context).result!;
			return new Cgui_model_context({name: 'gui context', definition: conv_context});
		})
		.then(context => context?.definitions.model_context)
		.then(context => context?.definitions.entry_or_node_context)
		.then(context => context?.definitions.context).result!
}
type Vgui_model_context = { name: 'gui context', definition: gui_model.Cgui_context}
export class Cgui_model_context extends AlanStruct {
	constructor(
		public readonly variant:Vgui_model_context) { super(); }
	public definitions:{
		model_context: Cmodel_context;
	} = {
		model_context: new Cmodel_context({name:'gui context', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => {
					switch (context?.variant.name) {
						case 'gui context': {
							const views__gui_model_context_var___model_context_in___model_context___gui_context_nval = context.cast('gui context');
							return resolve(context)
								.then(context => views__gui_model_context_var___model_context_in___model_context___gui_context_nval).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!)
		})
	}
	public readonly output:{
		context: () => views.Ccontext;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.model_context)
			.then(context => context?.definitions.entry_or_node_context)
			.then(context => context?.definitions.context).result!)
	};
	public cast<K extends Vgui_model_context['name']>(_variant:K):Extract<Vgui_model_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vgui_model_context['name']]:(($:Extract<Vgui_model_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'gui model context'`; }
	public is(other:Cgui_model_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
function evaluate__client_binding_switch(detach:boolean, views__client_binding_switch_nval:widget.Cwidget, input: {
	client_binding: () => client_bindings.Cbinding_context
}):client_bindings.Cbinding_context {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => views__client_binding_switch_nval)
		.then(context => context?.properties.binding_context)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'none': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.client_binding()).result;
				}
				case 'select': {
					const views__client_binding_switch___select_nval = state_group.cast('select');
					return resolve(context)
						.then(context => views__client_binding_switch___select_nval)
						.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding())
						.then(context => context?.properties.binding)
						.then(context => context?.definitions.binding_context).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}
function evaluate__client_binding_check(detach:boolean, views__client_binding_check_nval:widget.Cwidget, input: {
	client_binding: () => client_bindings.Cbinding_context
}):client_bindings.Cbinding_context {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => views__client_binding_check_nval)
		.then(context => context?.properties.binding_context)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'none': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.client_binding()).result;
				}
				case 'select': {
					const views__client_binding_check___select_nval = state_group.cast('select');
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => views__client_binding_check___select_nval)
								.then(context => context?.properties.binding?.ref)
								.then(context => context?.properties.binding)
								.then(context => context?.definitions.binding_context).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.client_binding()).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}
function evaluate__attribute_client_binding_check(detach:boolean, views__attribute_client_binding_check_nval:widget.Cattributes__widget_configuration_node, input: {
	inherited_binding: () => client_bindings.Cbinding_context
}):client_bindings.Cbinding_context {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => views__attribute_client_binding_check_nval)
		.then(context => context?.properties.switch_client_binding_context)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'no': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.inherited_binding()).result;
				}
				case 'yes': {
					return resolve(context)
						.then(context => views__attribute_client_binding_check_nval)
						.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding()).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}

export type Tviews = {
	'dependencies':Record<string, {}>;
	'views':Record<string, Tviews__>;
};
export class Cviews extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly dependencies:Cviews.Ddependencies,
		readonly views:Cviews.Dviews
	};
	constructor(init:Tviews, public readonly input: {
	'application':application.Capplication
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'phrases':phrases.Cphrases
	'reports':{[name:string]:parameters.Cparameters}
	'settings':settings.Csettings
	'views':{[name:string]:Cviews}
	'widgets':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			dependencies: new Cviews.Ddependencies(init['dependencies'], $this),
			views: new Cviews.Dviews(init['views'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export class Kdependencies extends Reference<views.Cviews, string> {
	constructor(key:string, $this:Cdependencies) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent.root.input.views[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dependencies'::key`; }
}
export type Tdependencies = {
};
export class Cdependencies extends AlanDictionaryEntry {
	public key:Kdependencies;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Tdependencies, public parent:Cviews) {
		super();
		const $this = this;
		this.key = new Kdependencies(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dependencies'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tviews__ = {
	'instance':Twidget_expression;
	'node type':Tgui_node_type_path;
	'queries':Record<string, Tqueries>;
	'translate title':'no'|['no', {}]|['yes', Tyes__translate_title];
};
export class Cviews__ extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly instance:Cwidget_expression,
		readonly node_type:Cgui_node_type_path,
		readonly queries:Cviews__.Dqueries,
		readonly translate_title:Cviews__.Dtranslate_title
	};
	constructor(key:string, init:Tviews__, public parent:Cviews) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			instance: new Cviews__.Dinstance(init['instance'], $this),
			node_type: new Cviews__.Dnode_type(init['node type'], $this),
			queries: new Cviews__.Dqueries(init['queries'], $this),
			translate_title: new Cviews__.Dtranslate_title(init['translate title'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'views'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tqueries = {
	'columns':Record<string, Tcolumns>;
	'context':['candidates', Tcandidates__context]|['node', Tnode__context];
	'filters':Tfilters__queries;
	'has columns':'no'|['no', {}]|['yes', Tyes__has_columns];
	'query limits':Tquery_limits;
};
export class Cqueries extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		entry_or_node_context: Centry_or_node_context;
	} = {
		entry_or_node_context: new Centry_or_node_context({name:'query', definition: this}, {
			model_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.context?.state.node.output.selected_context())
				.then(context => context?.definitions.gui_context).result!)
		})
	}
	public readonly properties:{
		readonly columns:Cqueries.Dcolumns,
		readonly context:Cqueries.Dcontext,
		readonly filters:Cfilters__queries,
		readonly has_columns:Cqueries.Dhas_columns,
		readonly query_limits:Cquery_limits
	};
	constructor(key:string, init:Tqueries, public parent:Cviews__) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			columns: new Cqueries.Dcolumns(init['columns'], $this),
			context: new Cqueries.Dcontext(init['context'], $this),
			filters: new Cfilters__queries(init['filters'], $this),
			has_columns: new Cqueries.Dhas_columns(init['has columns'], $this),
			query_limits: new Cquery_limits(init['query limits'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'queries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcolumns = {
	'column type':'content'|['content', {}]|'id'|['id', {}];
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__columns];
	'name':string;
	'path':Tnode_path;
	'type':['file', Tfile__type__columns]|['number', Tnumber__type__columns]|['state group', Tstate_group__type__columns]|['text', Ttext__type__columns]|['widget', Twidget__type__columns];
};
export class Ccolumns extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly column_type:Ccolumns.Dcolumn_type,
		readonly has_successor:Ccolumns.Dhas_successor,
		readonly name:Ccolumns.Dname,
		readonly path:Cnode_path,
		readonly type:Ccolumns.Dtype
	};
	constructor(key:string, init:Tcolumns, public parent:Cqueries) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			column_type: new Ccolumns.Dcolumn_type(init['column type'], $this),
			has_successor: new Ccolumns.Dhas_successor(init['has successor'], $this),
			name: new Ccolumns.Dname(init['name'], $this),
			path: new Ccolumns.Dpath(init['path'], $this),
			type: new Ccolumns.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'columns'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcontent = {
};
export class Ccontent extends AlanNode {
	constructor(init:Tcontent, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'content'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid = {
};
export class Cid extends AlanNode {
	constructor(init:Tid, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'id'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_successor__columns = {
};
export class Cno__has_successor__columns extends AlanNode {
	constructor(init:Tno__has_successor__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__columns = {
	'successor':string;
};
export class Cyes__has_successor__columns extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__columns.Dsuccessor
	};
	constructor(init:Tyes__has_successor__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__columns.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__columns = {
	'file':string;
};
export class Cfile__type__columns extends AlanNode {
	public readonly properties:{
		readonly file:Cfile__type__columns.Dfile
	};
	constructor(init:Tfile__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			file: new Cfile__type__columns.Dfile(init['file'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__columns = {
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter__number];
	'number':string;
};
export class Cnumber__type__columns extends AlanNode {
	public readonly properties:{
		readonly has_filter:Cnumber__type__columns.Dhas_filter,
		readonly number:Cnumber__type__columns.Dnumber
	};
	constructor(init:Tnumber__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			has_filter: new Cnumber__type__columns.Dhas_filter(init['has filter'], $this),
			number: new Cnumber__type__columns.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter__number = {
};
export class Cno__has_filter__number extends AlanNode {
	constructor(init:Tno__has_filter__number, public parent:Cnumber__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter__number = {
	'initial criteria':'none'|['none', {}]|['yes', Tyes__initial_criteria];
	'operator selected':'no'|['no', {}]|['yes', Tyes__operator_selected];
};
export class Cyes__has_filter__number extends AlanNode {
	public readonly properties:{
		readonly initial_criteria:Cyes__has_filter__number.Dinitial_criteria,
		readonly operator_selected:Cyes__has_filter__number.Doperator_selected
	};
	constructor(init:Tyes__has_filter__number, public parent:Cnumber__type__columns) {
		super();
		const $this = this;
		this.properties = {
			initial_criteria: new Cyes__has_filter__number.Dinitial_criteria(init['initial criteria'], $this),
			operator_selected: new Cyes__has_filter__number.Doperator_selected(init['operator selected'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__initial_criteria = {
};
export class Cnone__initial_criteria extends AlanNode {
	constructor(init:Tnone__initial_criteria, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initial criteria'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initial_criteria = {
	'source':['now', Tnow__source__yes]|['static', Tstatic__source__yes];
};
export class Cyes__initial_criteria extends AlanNode {
	public readonly properties:{
		readonly source:Cyes__initial_criteria.Dsource
	};
	constructor(init:Tyes__initial_criteria, public parent:Cyes__has_filter__number) {
		super();
		const $this = this;
		this.properties = {
			source: new Cyes__initial_criteria.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initial criteria'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow__source__yes = {
	'has offset':'none'|['none', {}]|['yes', Tyes__has_offset__now__source__yes];
};
export class Cnow__source__yes extends AlanNode {
	public readonly properties:{
		readonly has_offset:Cnow__source__yes.Dhas_offset
	};
	constructor(init:Tnow__source__yes, public parent:Cyes__initial_criteria) {
		super();
		const $this = this;
		this.properties = {
			has_offset: new Cnow__source__yes.Dhas_offset(init['has offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'now'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__has_offset__now__source__yes = {
};
export class Cnone__has_offset__now__source__yes extends AlanNode {
	constructor(init:Tnone__has_offset__now__source__yes, public parent:Cnow__source__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has offset'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_offset__now__source__yes = {
	'offset':number;
};
export class Cyes__has_offset__now__source__yes extends AlanNode {
	public readonly properties:{
		readonly offset:Cyes__has_offset__now__source__yes.Doffset
	};
	constructor(init:Tyes__has_offset__now__source__yes, public parent:Cnow__source__yes) {
		super();
		const $this = this;
		this.properties = {
			offset: new Cyes__has_offset__now__source__yes.Doffset(init['offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has offset'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__source__yes = {
	'number':number;
};
export class Cstatic__source__yes extends AlanNode {
	public readonly properties:{
		readonly number:Cstatic__source__yes.Dnumber
	};
	constructor(init:Tstatic__source__yes, public parent:Cyes__initial_criteria) {
		super();
		const $this = this;
		this.properties = {
			number: new Cstatic__source__yes.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__operator_selected = {
};
export class Cno__operator_selected extends AlanNode {
	constructor(init:Tno__operator_selected, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__operator_selected = {
	'operator':'equal to'|['equal to', {}]|'greater than'|['greater than', {}]|'greater than or equal to'|['greater than or equal to', {}]|'smaller than'|['smaller than', {}]|'smaller than or equal to'|['smaller than or equal to', {}];
};
export class Cyes__operator_selected extends AlanNode {
	public readonly properties:{
		readonly operator:Cyes__operator_selected.Doperator
	};
	constructor(init:Tyes__operator_selected, public parent:Cyes__has_filter__number) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cyes__operator_selected.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequal_to = {
};
export class Cequal_to extends AlanNode {
	constructor(init:Tequal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than = {
};
export class Cgreater_than extends AlanNode {
	constructor(init:Tgreater_than, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'greater than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than_or_equal_to = {
};
export class Cgreater_than_or_equal_to extends AlanNode {
	constructor(init:Tgreater_than_or_equal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'greater than or equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmaller_than = {
};
export class Csmaller_than extends AlanNode {
	constructor(init:Tsmaller_than, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'smaller than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmaller_than_or_equal_to = {
};
export class Csmaller_than_or_equal_to extends AlanNode {
	constructor(init:Tsmaller_than_or_equal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'smaller than or equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__columns = {
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter__state_group];
	'state group':string;
};
export class Cstate_group__type__columns extends AlanNode {
	public readonly properties:{
		readonly has_filter:Cstate_group__type__columns.Dhas_filter,
		readonly state_group:Cstate_group__type__columns.Dstate_group
	};
	constructor(init:Tstate_group__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			has_filter: new Cstate_group__type__columns.Dhas_filter(init['has filter'], $this),
			state_group: new Cstate_group__type__columns.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter__state_group = {
};
export class Cno__has_filter__state_group extends AlanNode {
	constructor(init:Tno__has_filter__state_group, public parent:Cstate_group__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter__state_group = {
	'filter enabled':'no'|['no', {}]|'yes'|['yes', {}];
	'states':Record<string, Tstates__yes>;
};
export class Cyes__has_filter__state_group extends AlanNode {
	public readonly properties:{
		readonly filter_enabled:Cyes__has_filter__state_group.Dfilter_enabled,
		readonly states:Cyes__has_filter__state_group.Dstates
	};
	constructor(init:Tyes__has_filter__state_group, public parent:Cstate_group__type__columns) {
		super();
		const $this = this;
		this.properties = {
			filter_enabled: new Cyes__has_filter__state_group.Dfilter_enabled(init['filter enabled'], $this),
			states: new Cyes__has_filter__state_group.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_enabled = {
};
export class Cno__filter_enabled extends AlanNode {
	constructor(init:Tno__filter_enabled, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter enabled'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_enabled = {
};
export class Cyes__filter_enabled extends AlanNode {
	constructor(init:Tyes__filter_enabled, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter enabled'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__yes extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates__yes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__yes = {
	'is selected':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cstates__yes extends AlanDictionaryEntry {
	public key:Kstates__yes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly is_selected:Cstates__yes.Dis_selected
	};
	constructor(key:string, init:Tstates__yes, public parent:Cyes__has_filter__state_group) {
		super();
		const $this = this;
		this.key = new Kstates__yes(key, $this);
		this.properties = {
			is_selected: new Cstates__yes.Dis_selected(init['is selected'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__is_selected = {
};
export class Cno__is_selected extends AlanNode {
	constructor(init:Tno__is_selected, public parent:Cstates__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_selected = {
};
export class Cyes__is_selected extends AlanNode {
	constructor(init:Tyes__is_selected, public parent:Cstates__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__columns = {
	'filter':'current id path'|['current id path', {}]|'none'|['none', {}]|['simple', Tsimple];
	'text':string;
};
export class Ctext__type__columns extends AlanNode {
	public readonly properties:{
		readonly filter:Ctext__type__columns.Dfilter,
		readonly text:Ctext__type__columns.Dtext
	};
	constructor(init:Ttext__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			filter: new Ctext__type__columns.Dfilter(init['filter'], $this),
			text: new Ctext__type__columns.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_id_path = {
};
export class Ccurrent_id_path extends AlanNode {
	public readonly inferences:{
		navigable: () => gui_model.Cyes__navigable,
		valid_entity: () => gui_model.Cgui_node
	} = {
		navigable: cache((detach:boolean) => {
			const views__views__queries__columns__type__text__filter__current_id_path_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.text?.ref)
				.then(context => {
					if (context?.properties.navigable.state.name === 'yes') {
						return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		valid_entity: cache((detach:boolean) => {
			const views__views__queries__columns__type__text__filter__current_id_path_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.inferences.navigable())
						.then(context => context?.properties.referenced_node)
						.then(context => context?.component_root.output.result_gui_node())
						.then(context => context?.component_root.output.gui_entity()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.node_type)
						.then(context => context?.component_root.output.result_gui_node())
						.then(context => context?.component_root.output.gui_entity()).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tcurrent_id_path, public parent:Ctext__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter'?'current id path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__filter = {
};
export class Cnone__filter extends AlanNode {
	constructor(init:Tnone__filter, public parent:Ctext__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsimple = {
	'criteria':string;
};
export class Csimple extends AlanNode {
	public readonly properties:{
		readonly criteria:string
	};
	constructor(init:Tsimple, public parent:Ctext__type__columns) {
		super();
		const $this = this;
		this.properties = {
			criteria: init['criteria']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter'?'simple'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type__columns = {
	'instance':Twidget_expression;
};
export class Cwidget__type__columns extends AlanNode {
	public readonly properties:{
		readonly instance:Cwidget_expression
	};
	constructor(init:Twidget__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			instance: new Cwidget__type__columns.Dinstance(init['instance'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates__context = {
	'property':string;
	'property context':Tgui_node_type_path_step;
};
export class Ccandidates__context extends AlanNode {
	public readonly properties:{
		readonly property:Ccandidates__context.Dproperty,
		readonly property_context:Cgui_node_type_path_step
	};
	public readonly output:{
		selected_context: () => gui_model.Cgui_node;
	} = {
		selected_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node()).result!)
	}
	constructor(init:Tcandidates__context, public parent:Cqueries) {
		super();
		const $this = this;
		this.properties = {
			property: new Ccandidates__context.Dproperty(init['property'], $this),
			property_context: new Ccandidates__context.Dproperty_context(init['property context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__context = {
	'path':Tquery_path;
	'switch':['current', Tcurrent]|'root'|['root', {}];
};
export class Cnode__context extends AlanNode {
	public readonly properties:{
		readonly path:Cquery_path,
		readonly switch_:Cnode__context.Dswitch_
	};
	public readonly output:{
		selected_context: () => gui_model.Cgui_node;
	} = {
		selected_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.result_node())
			.then(context => context?.component_root.output.gui_entity()).result!)
	}
	constructor(init:Tnode__context, public parent:Cqueries) {
		super();
		const $this = this;
		this.properties = {
			path: new Cnode__context.Dpath(init['path'], $this),
			switch_: new Cnode__context.Dswitch_(init['switch'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent = {
	'query context':Tgui_node_type_path_step;
};
export class Ccurrent extends AlanNode {
	public readonly properties:{
		readonly query_context:Cgui_node_type_path_step
	};
	public readonly output:{
		selected_context: () => gui_model.Cgui_node;
	} = {
		selected_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.query_context)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tcurrent, public parent:Cnode__context) {
		super();
		const $this = this;
		this.properties = {
			query_context: new Ccurrent.Dquery_context(init['query context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch'?'current'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot__switch__node__context = {
};
export class Croot__switch__node__context extends AlanNode {
	public readonly output:{
		selected_context: () => gui_model.Cgui_node;
	} = {
		selected_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root.input.gui_model)
			.then(context => context?.properties.root).result!)
	}
	constructor(init:Troot__switch__node__context, public parent:Cnode__context) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilters__queries = {
	'filters':Record<string, Tfilters__filters>;
	'todo filter':'no'|['no', {}]|['yes', Tyes__todo_filter];
};
export class Cfilters__queries extends AlanNode {
	public readonly properties:{
		readonly filters:Cfilters__queries.Dfilters,
		readonly todo_filter:Cfilters__queries.Dtodo_filter
	};
	constructor(init:Tfilters__queries, public parent:Cqueries) {
		super();
		const $this = this;
		this.properties = {
			filters: new Cfilters__queries.Dfilters(init['filters'], $this),
			todo_filter: new Cfilters__queries.Dtodo_filter(init['todo filter'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilters__filters = {
	'path':Tnode_path;
	'type':['node', Tnode__type__filters]|['property', Tproperty];
};
export class Cfilters__filters extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly path:Cnode_path,
		readonly type:Cfilters__filters.Dtype
	};
	constructor(key:string, init:Tfilters__filters, public parent:Cfilters__queries) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			path: new Cfilters__filters.Dpath(init['path'], $this),
			type: new Cfilters__filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnode__type__filters = {
	'type':['existence', Texistence]|'view context'|['view context', {}];
};
export class Cnode__type__filters extends AlanNode {
	public readonly properties:{
		readonly type:Cnode__type__filters.Dtype
	};
	constructor(init:Tnode__type__filters, public parent:Cfilters__filters) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode__type__filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence = {
	'operator':'exists'|['exists', {}]|'not exists'|['not exists', {}];
};
export class Cexistence extends AlanNode {
	public readonly properties:{
		readonly operator:Cexistence.Doperator
	};
	constructor(init:Texistence, public parent:Cnode__type__filters) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cexistence.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists = {
};
export class Cexists extends AlanNode {
	constructor(init:Texists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_exists = {
};
export class Cnot_exists extends AlanNode {
	constructor(init:Tnot_exists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview_context = {
};
export class Cview_context extends AlanNode {
	public readonly inferences:{
		valid_entity: () => gui_model.Cgui_node
	} = {
		valid_entity: cache((detach:boolean) => {
			const views__views__queries__filters__filters__type__node__type__view_context_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.node_type)
						.then(context => context?.component_root.output.result_gui_node())
						.then(context => context?.component_root.output.gui_entity()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.path)
						.then(context => context?.component_root.output.result_node())
						.then(context => context?.component_root.output.gui_entity()).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tview_context, public parent:Cnode__type__filters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty = {
	'property':string;
	'type':['collection', Tcollection__type__property]|['number', Tnumber__type__property]|['state group', Tstate_group__type__property]|['text', Ttext__type__property];
};
export class Cproperty extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty.Dproperty,
		readonly type:Cproperty.Dtype
	};
	constructor(init:Tproperty, public parent:Cfilters__filters) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty.Dproperty(init['property'], $this),
			type: new Cproperty.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property = {
	'keys':Record<string, {}>;
	'operator':'in'|['in', {}]|'not in'|['not in', {}];
};
export class Ccollection__type__property extends AlanNode {
	public readonly properties:{
		readonly keys:Ccollection__type__property.Dkeys,
		readonly operator:Ccollection__type__property.Doperator
	};
	public readonly inferences:{
		collection_type: () => gui_model.Ccollection__type__property
	} = {
		collection_type: cache((detach:boolean) => {
			const views__views__queries__filters__filters__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as gui_model.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			keys: new Ccollection__type__property.Dkeys(init['keys'], $this),
			operator: new Ccollection__type__property.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkeys = {
};
export class Ckeys extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	constructor(key:string, init:Tkeys, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.key = key;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'keys'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tin = {
};
export class Cin extends AlanNode {
	constructor(init:Tin, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_in = {
};
export class Cnot_in extends AlanNode {
	constructor(init:Tnot_in, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
	'expression':Tnumber_filter_expression;
};
export class Cnumber__type__property extends AlanNode {
	public readonly properties:{
		readonly expression:Cnumber_filter_expression
	};
	public readonly inferences:{
		number_type: () => gui_model.Cnumber__type__property__type__attributes
	} = {
		number_type: cache((detach:boolean) => {
			const views__views__queries__filters__filters__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber__type__property.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property = {
	'states to include':Record<string, {}>;
};
export class Cstate_group__type__property extends AlanNode {
	public readonly properties:{
		readonly states_to_include:Cstate_group__type__property.Dstates_to_include
	};
	public readonly inferences:{
		state_group_type: () => gui_model.Cstate_group__type__property__type__attributes
	} = {
		state_group_type: cache((detach:boolean) => {
			const views__views__queries__filters__filters__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			states_to_include: new Cstate_group__type__property.Dstates_to_include(init['states to include'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates_to_include extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates_to_include) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group_type())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'::key`; }
}
export type Tstates_to_include = {
};
export class Cstates_to_include extends AlanDictionaryEntry {
	public key:Kstates_to_include;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Tstates_to_include, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.key = new Kstates_to_include(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__property = {
	'text':string;
};
export class Ctext__type__property extends AlanNode {
	public readonly properties:{
		readonly text:string
	};
	public readonly inferences:{
		text_type: () => gui_model.Ctext__type__property__type__attributes
	} = {
		text_type: cache((detach:boolean) => {
			const views__views__queries__filters__filters__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			text: init['text']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__todo_filter = {
};
export class Cno__todo_filter extends AlanNode {
	constructor(init:Tno__todo_filter, public parent:Cfilters__queries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__todo_filter = {
	'path':Tnode_path;
};
export class Cyes__todo_filter extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path
	};
	constructor(init:Tyes__todo_filter, public parent:Cfilters__queries) {
		super();
		const $this = this;
		this.properties = {
			path: new Cyes__todo_filter.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_columns = {
};
export class Cno__has_columns extends AlanNode {
	constructor(init:Tno__has_columns, public parent:Cqueries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has columns'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_columns = {
	'first':string;
};
export class Cyes__has_columns extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_columns.Dfirst
	};
	constructor(init:Tyes__has_columns, public parent:Cqueries) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_columns.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has columns'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery_limits = {
	'absolute maximum':number;
	'sample size':number;
	'show all limit':number;
};
export class Cquery_limits extends AlanNode {
	public readonly properties:{
		readonly absolute_maximum:Cquery_limits.Dabsolute_maximum,
		readonly sample_size:Cquery_limits.Dsample_size,
		readonly show_all_limit:Cquery_limits.Dshow_all_limit
	};
	constructor(init:Tquery_limits, public parent:Cqueries) {
		super();
		const $this = this;
		this.properties = {
			absolute_maximum: new Cquery_limits.Dabsolute_maximum(init['absolute maximum'], $this),
			sample_size: new Cquery_limits.Dsample_size(init['sample size'], $this),
			show_all_limit: new Cquery_limits.Dshow_all_limit(init['show all limit'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query limits'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__translate_title = {
};
export class Cno__translate_title extends AlanNode {
	constructor(init:Tno__translate_title, public parent:Cviews__) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'translate title'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__translate_title = {
	'title':string;
};
export class Cyes__translate_title extends AlanNode {
	public readonly properties:{
		readonly title:Cyes__translate_title.Dtitle
	};
	constructor(init:Tyes__translate_title, public parent:Cviews__) {
		super();
		const $this = this;
		this.properties = {
			title: new Cyes__translate_title.Dtitle(init['title'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'translate title'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cgui_node_type_path {
	export class Dsteps extends Cgui_node_type_path_step {
		constructor(data:Tgui_node_type_path['steps'], parent:Cgui_node_type_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.gui_model)
					.then(context => context?.properties.root).result!)
			}, 'steps')
		}
	}
}
export namespace Cgui_node_type_path_step {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__gui_node_type_path_step, init:Tno__has_steps__gui_node_type_path_step}|
		{ name: 'yes', node:Cyes__has_steps__gui_node_type_path_step, init:Tyes__has_steps__gui_node_type_path_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__gui_node_type_path_step, parent:Cgui_node_type_path_step) => new Cno__has_steps__gui_node_type_path_step(init, parent);
				case 'yes': return (init:Tyes__has_steps__gui_node_type_path_step, parent:Cgui_node_type_path_step) => new Cyes__has_steps__gui_node_type_path_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__gui_node_type_path_step;
				case 'yes': return finalize_yes__has_steps__gui_node_type_path_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_node_type_path_step['has steps'], parent:Cgui_node_type_path_step) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__gui_node_type_path_step {
	export class Dattribute extends Reference<gui_model.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_steps__gui_node_type_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dtail extends Cgui_node_type_path_step {
		constructor(data:Tyes__has_steps__gui_node_type_path_step['tail'], parent:Cyes__has_steps__gui_node_type_path_step) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode__type__yes, init:Tnode__type__yes}|
		{ name: 'state', node:Cstate__type__yes__has_steps__gui_node_type_path_step, init:Tstate__type__yes__has_steps__gui_node_type_path_step}> {
		protected initializer(state: 'node'|'state') {
			switch (state) {
				case 'node': return (init:Tnode__type__yes, parent:Cyes__has_steps__gui_node_type_path_step) => new Cnode__type__yes(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__gui_node_type_path_step, parent:Cyes__has_steps__gui_node_type_path_step) => new Cstate__type__yes__has_steps__gui_node_type_path_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'state') {
			switch (state) {
				case 'node': return finalize_node__type__yes;
				case 'state': return finalize_state__type__yes__has_steps__gui_node_type_path_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__gui_node_type_path_step['type'], parent:Cyes__has_steps__gui_node_type_path_step) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate__type__yes__has_steps__gui_node_type_path_step {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__gui_node_type_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cgui_widget_configuration_attribute {
	export class Dtype extends StateGroup<
		{ name: 'configuration', node:Cconfiguration__type, init:Tconfiguration__type}|
		{ name: 'model binding', node:Cmodel_binding, init:Tmodel_binding}|
		{ name: 'view', node:Cview__type__gui_widget_configuration_attribute, init:Tview__type__gui_widget_configuration_attribute}|
		{ name: 'widget', node:Cwidget__type__gui_widget_configuration_attribute, init:Twidget__type__gui_widget_configuration_attribute}|
		{ name: 'window', node:Cwindow__type__gui_widget_configuration_attribute, init:Twindow__type__gui_widget_configuration_attribute}> {
		protected initializer(state: 'configuration'|'model binding'|'view'|'widget'|'window') {
			switch (state) {
				case 'configuration': return (init:Tconfiguration__type, parent:Cgui_widget_configuration_attribute) => new Cconfiguration__type(init, parent);
				case 'model binding': return (init:Tmodel_binding, parent:Cgui_widget_configuration_attribute) => new Cmodel_binding(init, parent);
				case 'view': return (init:Tview__type__gui_widget_configuration_attribute, parent:Cgui_widget_configuration_attribute) => new Cview__type__gui_widget_configuration_attribute(init, parent);
				case 'widget': return (init:Twidget__type__gui_widget_configuration_attribute, parent:Cgui_widget_configuration_attribute) => new Cwidget__type__gui_widget_configuration_attribute(init, parent);
				case 'window': return (init:Twindow__type__gui_widget_configuration_attribute, parent:Cgui_widget_configuration_attribute) => new Cwindow__type__gui_widget_configuration_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'configuration'|'model binding'|'view'|'widget'|'window') {
			switch (state) {
				case 'configuration': return finalize_configuration__type;
				case 'model binding': return finalize_model_binding;
				case 'view': return finalize_view__type__gui_widget_configuration_attribute;
				case 'widget': return finalize_widget__type__gui_widget_configuration_attribute;
				case 'window': return finalize_window__type__gui_widget_configuration_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_widget_configuration_attribute['type'], parent:Cgui_widget_configuration_attribute) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconfiguration__type {
	export class Dtype extends StateGroup<
		{ name: 'list', node:Clist, init:Tlist}|
		{ name: 'number', node:Cnumber__type__configuration, init:Tnumber__type__configuration}|
		{ name: 'state group', node:Cstate_group__type__configuration, init:Tstate_group__type__configuration}|
		{ name: 'states list', node:Cstates_list__type, init:Tstates_list__type}|
		{ name: 'text', node:Ctext__type__configuration, init:Ttext__type__configuration}> {
		protected initializer(state: 'list'|'number'|'state group'|'states list'|'text') {
			switch (state) {
				case 'list': return (init:Tlist, parent:Cconfiguration__type) => new Clist(init, parent);
				case 'number': return (init:Tnumber__type__configuration, parent:Cconfiguration__type) => new Cnumber__type__configuration(init, parent);
				case 'state group': return (init:Tstate_group__type__configuration, parent:Cconfiguration__type) => new Cstate_group__type__configuration(init, parent);
				case 'states list': return (init:Tstates_list__type, parent:Cconfiguration__type) => new Cstates_list__type(init, parent);
				case 'text': return (init:Ttext__type__configuration, parent:Cconfiguration__type) => new Ctext__type__configuration(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'list'|'number'|'state group'|'states list'|'text') {
			switch (state) {
				case 'list': return finalize_list;
				case 'number': return finalize_number__type__configuration;
				case 'state group': return finalize_state_group__type__configuration;
				case 'states list': return finalize_states_list__type;
				case 'text': return finalize_text__type__configuration;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration__type['type'], parent:Cconfiguration__type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Clist {
	export class Dlist extends Cgui_widget_configuration_list {
		constructor(data:Tlist['list'], parent:Clist) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				previous_entry: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => views.Clist_counter.Pstart).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.list())
					.then(context => context?.properties.node).result!)
			}, 'list')
		}
	}
}
export namespace Cnumber__type__configuration {
	export class Dsource extends StateGroup<
		{ name: 'now', node:Cnow__source__number, init:Tnow__source__number}|
		{ name: 'static', node:Cstatic__source__number, init:Tstatic__source__number}> {
		protected initializer(state: 'now'|'static') {
			switch (state) {
				case 'now': return (init:Tnow__source__number, parent:Cnumber__type__configuration) => new Cnow__source__number(init, parent);
				case 'static': return (init:Tstatic__source__number, parent:Cnumber__type__configuration) => new Cstatic__source__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'now'|'static') {
			switch (state) {
				case 'now': return finalize_now__source__number;
				case 'static': return finalize_static__source__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__configuration['source'], parent:Cnumber__type__configuration) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cnow__source__number {
	export class Dhas_offset extends StateGroup<
		{ name: 'none', node:Cnone__has_offset__now__source__number, init:Tnone__has_offset__now__source__number}|
		{ name: 'yes', node:Cyes__has_offset__now__source__number, init:Tyes__has_offset__now__source__number}> {
		protected initializer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return (init:Tnone__has_offset__now__source__number, parent:Cnow__source__number) => new Cnone__has_offset__now__source__number(init, parent);
				case 'yes': return (init:Tyes__has_offset__now__source__number, parent:Cnow__source__number) => new Cyes__has_offset__now__source__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return finalize_none__has_offset__now__source__number;
				case 'yes': return finalize_yes__has_offset__now__source__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnow__source__number['has offset'], parent:Cnow__source__number) {
			super(data, parent, 'has offset');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has offset'`; }
	}
}
export namespace Cyes__has_offset__now__source__number {
	export class Doffset extends AlanInteger {
		constructor(data:Tyes__has_offset__now__source__number['offset'], parent:Cyes__has_offset__now__source__number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'offset'`; }
	}
}
export namespace Cstatic__source__number {
	export class Dnumber extends AlanInteger {
		constructor(data:Tstatic__source__number['number'], parent:Cstatic__source__number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cstate_group__type__configuration {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tstate_group__type__configuration['configuration'], parent:Cstate_group__type__configuration) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dstate extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__configuration) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstates_list__type {
	export class Dhas_states extends StateGroup<
		{ name: 'no', node:Cno__has_states, init:Tno__has_states}|
		{ name: 'yes', node:Cyes__has_states, init:Tyes__has_states}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_states, parent:Cstates_list__type) => new Cno__has_states(init, parent);
				case 'yes': return (init:Tyes__has_states, parent:Cstates_list__type) => new Cyes__has_states(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_states;
				case 'yes': return finalize_yes__has_states;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates_list__type['has states'], parent:Cstates_list__type) {
			super(data, parent, 'has states');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has states'`; }
	}
	export class Dstates extends AlanTopology<{ node:Cstates__states_list, init:Tstates__states_list},Cstates_list__type,'view order'> {
		protected graph_iterator(graph:('view order')):(node:Cstates__states_list) => Cstates__states_list {
			switch (graph) {
				case 'view order': return (entry:Cstates__states_list) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as views.Cyes__has_successor__states;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cstates_list__type, key:string, entry_init:Tstates__states_list) { return new Cstates__states_list(key, entry_init, parent); }
		protected finalize = finalize_states__states_list
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(views__gui_widget_configuration_attribute__type__configuration__type__states_list__states_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_group_binding())
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstates_list__type['states'], parent:Cstates_list__type) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_states {
	export class Dfirst extends Reference<views.Cstates__states_list,string> {

		constructor(data:string, $this:Cyes__has_states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cstates__states_list {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tstates__states_list['configuration'], parent:Cstates__states_list) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.list())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__states, init:Tno__has_successor__states}|
		{ name: 'yes', node:Cyes__has_successor__states, init:Tyes__has_successor__states}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__states, parent:Cstates__states_list) => new Cno__has_successor__states(init, parent);
				case 'yes': return (init:Tyes__has_successor__states, parent:Cstates__states_list) => new Cyes__has_successor__states(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__states;
				case 'yes': return finalize_yes__has_successor__states;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__states_list['has successor'], parent:Cstates__states_list) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
}
export namespace Cyes__has_successor__states {
	export class Dsuccessor extends Reference<views.Cstates__states_list,string> {

		constructor(data:string, $this:Cyes__has_successor__states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Ctext__type__configuration {
	export class Dtype extends StateGroup<
		{ name: 'key', node:Ckey, init:Tkey}|
		{ name: 'phrase', node:Cphrase, init:Tphrase}|
		{ name: 'static', node:Cstatic__type, init:Tstatic__type}> {
		protected initializer(state: 'key'|'phrase'|'static') {
			switch (state) {
				case 'key': return (init:Tkey, parent:Ctext__type__configuration) => new Ckey(init, parent);
				case 'phrase': return (init:Tphrase, parent:Ctext__type__configuration) => new Cphrase(init, parent);
				case 'static': return (init:Tstatic__type, parent:Ctext__type__configuration) => new Cstatic__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'key'|'phrase'|'static') {
			switch (state) {
				case 'key': return finalize_key;
				case 'phrase': return finalize_phrase;
				case 'static': return finalize_static__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__configuration['type'], parent:Ctext__type__configuration) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ckey {
	export class Dtranslate extends StateGroup<
		{ name: 'no', node:Cno__translate, init:Tno__translate}|
		{ name: 'yes', node:Cyes__translate, init:Tyes__translate}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__translate, parent:Ckey) => new Cno__translate(init, parent);
				case 'yes': return (init:Tyes__translate, parent:Ckey) => new Cyes__translate(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__translate;
				case 'yes': return finalize_yes__translate;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tkey['translate'], parent:Ckey) {
			super(data, parent, 'translate');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'translate'`; }
	}
}
export namespace Cphrase {
	export class Dvalue extends Reference<phrases.Cphrases__,string> {

		constructor(data:string, $this:Cphrase) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.phrases)
				.then(context => {
					const entry = context?.properties.phrases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstatic__type {
}
export namespace Cmodel_binding {
	export class Dtype extends StateGroup<
		{ name: 'action', node:Caction, init:Taction}|
		{ name: 'collection', node:Ccollection__type__model_binding, init:Tcollection__type__model_binding}|
		{ name: 'command', node:Ccommand, init:Tcommand}|
		{ name: 'current node', node:Ccurrent_node__type, init:Tcurrent_node__type}|
		{ name: 'entity', node:Centity__type__model_binding, init:Tentity__type__model_binding}|
		{ name: 'file', node:Cfile__type__model_binding, init:Tfile__type__model_binding}|
		{ name: 'group', node:Cgroup__type__model_binding, init:Tgroup__type__model_binding}|
		{ name: 'joined entry', node:Cjoined_entry, init:Tjoined_entry}|
		{ name: 'number', node:Cnumber__type__model_binding, init:Tnumber__type__model_binding}|
		{ name: 'ordered graph', node:Cordered_graph, init:Tordered_graph}|
		{ name: 'query', node:Cquery, init:Tquery}|
		{ name: 'query file', node:Cquery_file, init:Tquery_file}|
		{ name: 'query number', node:Cquery_number, init:Tquery_number}|
		{ name: 'query stategroup', node:Cquery_stategroup, init:Tquery_stategroup}|
		{ name: 'query text', node:Cquery_text, init:Tquery_text}|
		{ name: 'report', node:Creport, init:Treport}|
		{ name: 'report number parameter', node:Creport_number_parameter, init:Treport_number_parameter}|
		{ name: 'report text parameter', node:Creport_text_parameter, init:Treport_text_parameter}|
		{ name: 'sort property', node:Csort_property, init:Tsort_property}|
		{ name: 'state', node:Cstate__type__model_binding, init:Tstate__type__model_binding}|
		{ name: 'state group', node:Cstate_group__type__model_binding, init:Tstate_group__type__model_binding}|
		{ name: 'state rule', node:Cstate_rule__type__model_binding, init:Tstate_rule__type__model_binding}|
		{ name: 'text', node:Ctext__type__model_binding, init:Ttext__type__model_binding}|
		{ name: 'user', node:Cuser, init:Tuser}|
		{ name: 'window', node:Cwindow__type__model_binding, init:Twindow__type__model_binding}> {
		protected initializer(state: 'action'|'collection'|'command'|'current node'|'entity'|'file'|'group'|'joined entry'|'number'|'ordered graph'|'query'|'query file'|'query number'|'query stategroup'|'query text'|'report'|'report number parameter'|'report text parameter'|'sort property'|'state'|'state group'|'state rule'|'text'|'user'|'window') {
			switch (state) {
				case 'action': return (init:Taction, parent:Cmodel_binding) => new Caction(init, parent);
				case 'collection': return (init:Tcollection__type__model_binding, parent:Cmodel_binding) => new Ccollection__type__model_binding(init, parent);
				case 'command': return (init:Tcommand, parent:Cmodel_binding) => new Ccommand(init, parent);
				case 'current node': return (init:Tcurrent_node__type, parent:Cmodel_binding) => new Ccurrent_node__type(init, parent);
				case 'entity': return (init:Tentity__type__model_binding, parent:Cmodel_binding) => new Centity__type__model_binding(init, parent);
				case 'file': return (init:Tfile__type__model_binding, parent:Cmodel_binding) => new Cfile__type__model_binding(init, parent);
				case 'group': return (init:Tgroup__type__model_binding, parent:Cmodel_binding) => new Cgroup__type__model_binding(init, parent);
				case 'joined entry': return (init:Tjoined_entry, parent:Cmodel_binding) => new Cjoined_entry(init, parent);
				case 'number': return (init:Tnumber__type__model_binding, parent:Cmodel_binding) => new Cnumber__type__model_binding(init, parent);
				case 'ordered graph': return (init:Tordered_graph, parent:Cmodel_binding) => new Cordered_graph(init, parent);
				case 'query': return (init:Tquery, parent:Cmodel_binding) => new Cquery(init, parent);
				case 'query file': return (init:Tquery_file, parent:Cmodel_binding) => new Cquery_file(init, parent);
				case 'query number': return (init:Tquery_number, parent:Cmodel_binding) => new Cquery_number(init, parent);
				case 'query stategroup': return (init:Tquery_stategroup, parent:Cmodel_binding) => new Cquery_stategroup(init, parent);
				case 'query text': return (init:Tquery_text, parent:Cmodel_binding) => new Cquery_text(init, parent);
				case 'report': return (init:Treport, parent:Cmodel_binding) => new Creport(init, parent);
				case 'report number parameter': return (init:Treport_number_parameter, parent:Cmodel_binding) => new Creport_number_parameter(init, parent);
				case 'report text parameter': return (init:Treport_text_parameter, parent:Cmodel_binding) => new Creport_text_parameter(init, parent);
				case 'sort property': return (init:Tsort_property, parent:Cmodel_binding) => new Csort_property(init, parent);
				case 'state': return (init:Tstate__type__model_binding, parent:Cmodel_binding) => new Cstate__type__model_binding(init, parent);
				case 'state group': return (init:Tstate_group__type__model_binding, parent:Cmodel_binding) => new Cstate_group__type__model_binding(init, parent);
				case 'state rule': return (init:Tstate_rule__type__model_binding, parent:Cmodel_binding) => new Cstate_rule__type__model_binding(init, parent);
				case 'text': return (init:Ttext__type__model_binding, parent:Cmodel_binding) => new Ctext__type__model_binding(init, parent);
				case 'user': return (init:Tuser, parent:Cmodel_binding) => new Cuser(init, parent);
				case 'window': return (init:Twindow__type__model_binding, parent:Cmodel_binding) => new Cwindow__type__model_binding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'action'|'collection'|'command'|'current node'|'entity'|'file'|'group'|'joined entry'|'number'|'ordered graph'|'query'|'query file'|'query number'|'query stategroup'|'query text'|'report'|'report number parameter'|'report text parameter'|'sort property'|'state'|'state group'|'state rule'|'text'|'user'|'window') {
			switch (state) {
				case 'action': return finalize_action;
				case 'collection': return finalize_collection__type__model_binding;
				case 'command': return finalize_command;
				case 'current node': return finalize_current_node__type;
				case 'entity': return finalize_entity__type__model_binding;
				case 'file': return finalize_file__type__model_binding;
				case 'group': return finalize_group__type__model_binding;
				case 'joined entry': return finalize_joined_entry;
				case 'number': return finalize_number__type__model_binding;
				case 'ordered graph': return finalize_ordered_graph;
				case 'query': return finalize_query;
				case 'query file': return finalize_query_file;
				case 'query number': return finalize_query_number;
				case 'query stategroup': return finalize_query_stategroup;
				case 'query text': return finalize_query_text;
				case 'report': return finalize_report;
				case 'report number parameter': return finalize_report_number_parameter;
				case 'report text parameter': return finalize_report_text_parameter;
				case 'sort property': return finalize_sort_property;
				case 'state': return finalize_state__type__model_binding;
				case 'state group': return finalize_state_group__type__model_binding;
				case 'state rule': return finalize_state_rule__type__model_binding;
				case 'text': return finalize_text__type__model_binding;
				case 'user': return finalize_user;
				case 'window': return finalize_window__type__model_binding;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmodel_binding['type'], parent:Cmodel_binding) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Caction {
	export class Daction extends Reference<gui_model.Caction__type,string> {

		constructor(data:string, $this:Caction) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'action') {
							return context.properties.type.state.node as gui_model.Caction__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'action'`; }
	}
	export class Dcan_use_containing_view extends StateGroup<
		{ name: 'no', node:Cno__can_use_containing_view, init:Tno__can_use_containing_view}|
		{ name: 'yes', node:Cyes__can_use_containing_view, init:Tyes__can_use_containing_view}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_use_containing_view, parent:Caction) => new Cno__can_use_containing_view(init, parent);
				case 'yes': return (init:Tyes__can_use_containing_view, parent:Caction) => new Cyes__can_use_containing_view(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__can_use_containing_view;
				case 'yes': return finalize_yes__can_use_containing_view;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Taction['can use containing view'], parent:Caction) {
			super(data, parent, 'can use containing view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can use containing view'`; }
	}
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Taction['configuration'], parent:Caction) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.model_context)
					.then(context => context?.definitions.entry_or_node_context)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Taction['property path'], parent:Caction) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
	export class Dview_bindings extends AlanDictionary<{ node:Cview_bindings, init:Tview_bindings},Caction> {
		protected initialize(parent:Caction, key:string, entry_init:Tview_bindings) { return new Cview_bindings(key, entry_init, parent); }
		protected finalize = finalize_view_bindings
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(views__gui_widget_configuration_attribute__type__model_binding__type__action__view_bindings_key_nval:gui_model.Cviews):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.action?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.views) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view bindings'`; }
		constructor(data:Taction['view bindings'], parent:Caction) {
			super(data, parent);
		}
	}
}
export namespace Cview_bindings {
	export class Dcan_open_entry extends StateGroup<
		{ name: 'no', node:Cno__can_open_entry, init:Tno__can_open_entry}|
		{ name: 'yes', node:Cyes__can_open_entry, init:Tyes__can_open_entry}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_open_entry, parent:Cview_bindings) => new Cno__can_open_entry(init, parent);
				case 'yes': return (init:Tyes__can_open_entry, parent:Cview_bindings) => new Cyes__can_open_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__can_open_entry;
				case 'yes': return finalize_yes__can_open_entry;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_bindings['can open entry'], parent:Cview_bindings) {
			super(data, parent, 'can open entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can open entry'`; }
	}
	export class Dusing_views extends StateGroup<
		{ name: 'external', node:Cexternal__using_views__view_bindings, init:Texternal__using_views__view_bindings}|
		{ name: 'internal', node:Cinternal__using_views__view_bindings, init:Tinternal__using_views__view_bindings}> {
		protected initializer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return (init:Texternal__using_views__view_bindings, parent:Cview_bindings) => new Cexternal__using_views__view_bindings(init, parent);
				case 'internal': return (init:Tinternal__using_views__view_bindings, parent:Cview_bindings) => new Cinternal__using_views__view_bindings(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return finalize_external__using_views__view_bindings;
				case 'internal': return finalize_internal__using_views__view_bindings;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_bindings['using views'], parent:Cview_bindings) {
			super(data, parent, 'using views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'using views'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cview_bindings) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.using_views?.state.node.output.definition())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__action__view_bindings__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__action__view_bindings__view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_gui_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cview_bindings) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cyes__can_open_entry {
	export class Dusing_views extends StateGroup<
		{ name: 'external', node:Cexternal__using_views__yes, init:Texternal__using_views__yes}|
		{ name: 'internal', node:Cinternal__using_views__yes, init:Tinternal__using_views__yes}> {
		protected initializer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return (init:Texternal__using_views__yes, parent:Cyes__can_open_entry) => new Cexternal__using_views__yes(init, parent);
				case 'internal': return (init:Tinternal__using_views__yes, parent:Cyes__can_open_entry) => new Cinternal__using_views__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return finalize_external__using_views__yes;
				case 'internal': return finalize_internal__using_views__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__can_open_entry['using views'], parent:Cyes__can_open_entry) {
			super(data, parent, 'using views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'using views'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cyes__can_open_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.using_views?.state.node.output.definition())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__action__view_bindings__can_open_entry__yes__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__action__view_bindings__can_open_entry__yes__view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_gui_node())
								.then(context => context?.component_root.output.gui_entity()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cyes__can_open_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cexternal__using_views__yes {
	export class Dviews extends Reference<views.Cdependencies,string> {

		constructor(data:string, $this:Cexternal__using_views__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.dependencies.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Cexternal__using_views__view_bindings {
	export class Dviews extends Reference<views.Cdependencies,string> {

		constructor(data:string, $this:Cexternal__using_views__view_bindings) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.dependencies.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Ccollection__type__model_binding {
	export class Dcollection extends Reference<gui_model.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as gui_model.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tcollection__type__model_binding['configuration'], parent:Ccollection__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.join?.state.node.output.context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Djoin extends StateGroup<
		{ name: 'no', node:Cno__join, init:Tno__join}|
		{ name: 'yes', node:Cyes__join, init:Tyes__join}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__join, parent:Ccollection__type__model_binding) => new Cno__join(init, parent);
				case 'yes': return (init:Tyes__join, parent:Ccollection__type__model_binding) => new Cyes__join(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__join;
				case 'yes': return finalize_yes__join;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__model_binding['join'], parent:Ccollection__type__model_binding) {
			super(data, parent, 'join');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'join'`; }
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tcollection__type__model_binding['property path'], parent:Ccollection__type__model_binding) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
}
export namespace Ccommand {
	export class Dcommand extends Reference<gui_model.Cglobal,string> {

		constructor(data:string, $this:Ccommand) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'command') {
							return context.properties.type.state.node as gui_model.Ccommand__type;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'global') {
							return context.properties.type.state.node as gui_model.Cglobal;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'command'`; }
	}
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tcommand['configuration'], parent:Ccommand) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.command?.ref)
							.then(context => context?.properties.parameters).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tcommand['property path'], parent:Ccommand) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
}
export namespace Ccurrent_node__type {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tcurrent_node__type['configuration'], parent:Ccurrent_node__type) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.property_path)
							.then(context => context?.component_root.output.result_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tcurrent_node__type['property path'], parent:Ccurrent_node__type) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
}
export namespace Centity__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tentity__type__model_binding['configuration'], parent:Centity__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.inferences.gui_entity()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
}
export namespace Cfile__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tfile__type__model_binding['configuration'], parent:Cfile__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.property_path)
							.then(context => context?.component_root.output.result_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty extends Reference<gui_model.Cfile__type__property,string> {

		constructor(data:string, $this:Cfile__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'file') {
							return context.properties.type.state.node as gui_model.Cfile__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tfile__type__model_binding['property path'], parent:Cfile__type__model_binding) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
}
export namespace Cgroup__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tgroup__type__model_binding['configuration'], parent:Cgroup__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.group?.ref)
							.then(context => context?.properties.gui_node).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cjoined_entry {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tjoined_entry['configuration'], parent:Cjoined_entry) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.join())
					.then(context => context?.component_root.output.referenced_model_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
}
export namespace Cnumber__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tnumber__type__model_binding['configuration'], parent:Cnumber__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.property_path)
							.then(context => context?.component_root.output.result_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty extends Reference<gui_model.Cnumber__type__property__type__attributes,string> {

		constructor(data:string, $this:Cnumber__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tnumber__type__model_binding['property path'], parent:Cnumber__type__model_binding) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'property path')
		}
	}
}
export namespace Cordered_graph {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tordered_graph['configuration'], parent:Cordered_graph) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dordered_graph extends Reference<gui_model.Cordered_graphs,string> {

		constructor(data:string, $this:Cordered_graph) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.has_constraints())
				.then(context => {
					const entry = context?.properties.ordered_graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graph'`; }
	}
}
export namespace Cquery {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tquery['configuration'], parent:Cquery) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.query_context?.state.node.output.query())
					.then(context => context?.definitions.entry_or_node_context)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dhas_refresh_interval extends StateGroup<
		{ name: 'no', node:Cno__has_refresh_interval, init:Tno__has_refresh_interval}|
		{ name: 'yes', node:Cyes__has_refresh_interval, init:Tyes__has_refresh_interval}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_refresh_interval, parent:Cquery) => new Cno__has_refresh_interval(init, parent);
				case 'yes': return (init:Tyes__has_refresh_interval, parent:Cquery) => new Cyes__has_refresh_interval(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_refresh_interval;
				case 'yes': return finalize_yes__has_refresh_interval;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery['has refresh interval'], parent:Cquery) {
			super(data, parent, 'has refresh interval');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has refresh interval'`; }
	}
	export class Dquery_context extends StateGroup<
		{ name: 'candidates', node:Ccandidates__query_context, init:Tcandidates__query_context}|
		{ name: 'node', node:Cnode__query_context, init:Tnode__query_context}> {
		protected initializer(state: 'candidates'|'node') {
			switch (state) {
				case 'candidates': return (init:Tcandidates__query_context, parent:Cquery) => new Ccandidates__query_context(init, parent);
				case 'node': return (init:Tnode__query_context, parent:Cquery) => new Cnode__query_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'candidates'|'node') {
			switch (state) {
				case 'candidates': return finalize_candidates__query_context;
				case 'node': return finalize_node__query_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery['query context'], parent:Cquery) {
			super(data, parent, 'query context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query context'`; }
	}
}
export namespace Cyes__has_refresh_interval {
	export class Dinterval extends AlanInteger {
		constructor(data:Tyes__has_refresh_interval['interval'], parent:Cyes__has_refresh_interval) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'interval'`; }
	}
}
export namespace Ccandidates__query_context {
	export class Dquery extends Reference<views.Cqueries,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node,
			property_match: () => gui_model.Cyes__navigable,
			query: () => views.Ccandidates__context
		}

		constructor(data:string, $this:Ccandidates__query_context) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.view())
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates__query_nval)
								.then(context => context?.properties.context?.state.node.output.selected_context())
								.then(context => context?.component_root.output.gui_entity()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.model())
								.then(context => context?.component_root.output.model_context())
								.then(context => context?.component_root.output.gui_entity()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				property_match: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.navigable()).result;
							const right = resolve(context)
								.then(() => $this.properties.query.inferences.query())
								.then(context => context?.properties.property?.ref).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				query: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__candidates__query_nval)
						.then(context => {
							if (context?.properties.context.state.name === 'candidates') {
								return resolve(context.properties.context.state.node as views.Ccandidates__context).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	}
}
export namespace Cnode__query_context {
	export class Dswitch_ extends StateGroup<
		{ name: 'current node', node:Ccurrent_node__switch, init:Tcurrent_node__switch}|
		{ name: 'root', node:Croot__switch__node__query_context, init:Troot__switch__node__query_context}> {
		protected initializer(state: 'current node'|'root') {
			switch (state) {
				case 'current node': return (init:Tcurrent_node__switch, parent:Cnode__query_context) => new Ccurrent_node__switch(init, parent);
				case 'root': return (init:Troot__switch__node__query_context, parent:Cnode__query_context) => new Croot__switch__node__query_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'current node'|'root') {
			switch (state) {
				case 'current node': return finalize_current_node__switch;
				case 'root': return finalize_root__switch__node__query_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__query_context['switch'], parent:Cnode__query_context) {
			super(data, parent, 'switch');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'switch'`; }
	}
}
export namespace Ccurrent_node__switch {
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tcurrent_node__switch['property path'], parent:Ccurrent_node__switch) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
	export class Dquery extends Reference<views.Cqueries,string> {
		public readonly inferences:{
			query: () => views.Ccurrent,
			valid_node_type: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Ccurrent_node__switch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.view())
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				query: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node__switch__current_node__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node__switch__current_node__query_nval)
						.then(context => {
							if (context?.properties.context.state.name === 'node') {
								return resolve(context.properties.context.state.node as views.Cnode__context)
								.then(context => {
									if (context?.properties.switch_.state.name === 'current') {
										return context.properties.switch_.state.node as views.Ccurrent;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				valid_node_type: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node__switch__current_node__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.query.inferences.query())
								.then(context => context?.properties.query_context)
								.then(context => context?.component_root.output.result_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.properties.property_path)
								.then(context => context?.component_root.output.result_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	}
}
export namespace Croot__switch__node__query_context {
	export class Dquery extends Reference<views.Cqueries,string> {
		public readonly inferences:{
			query: () => views.Croot__switch__node__context
		}

		constructor(data:string, $this:Croot__switch__node__query_context) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.view())
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				query: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node__switch__root__query_nval = $this.properties.query.ref;
					return resolve($this.properties.query.ref)
						.then(context => views__gui_widget_configuration_attribute__type__model_binding__type__query__query_context__node__switch__root__query_nval)
						.then(context => {
							if (context?.properties.context.state.name === 'node') {
								return resolve(context.properties.context.state.node as views.Cnode__context)
								.then(context => {
									if (context?.properties.switch_.state.name === 'root') {
										return context.properties.switch_.state.node as views.Croot__switch__node__context;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	}
}
export namespace Cquery_file {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tquery_file['configuration'], parent:Cquery_file) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dfile extends Reference<views.Cfile__type__columns,string> {

		constructor(data:string, $this:Cquery_file) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.query())
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'file') {
							return context.properties.type.state.node as views.Cfile__type__columns;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'file'`; }
	}
}
export namespace Cquery_number {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tquery_number['configuration'], parent:Cquery_number) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dnumber extends Reference<views.Cnumber__type__columns,string> {

		constructor(data:string, $this:Cquery_number) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.query())
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as views.Cnumber__type__columns;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cquery_stategroup {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tquery_stategroup['configuration'], parent:Cquery_stategroup) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dstategroup extends Reference<views.Cstate_group__type__columns,string> {

		constructor(data:string, $this:Cquery_stategroup) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.query())
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as views.Cstate_group__type__columns;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'stategroup'`; }
	}
}
export namespace Cquery_text {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tquery_text['configuration'], parent:Cquery_text) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dtext extends Reference<views.Ctext__type__columns,string> {

		constructor(data:string, $this:Cquery_text) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.query())
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as views.Ctext__type__columns;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creport {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Treport['configuration'], parent:Creport) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dreport extends Reference<parameters.Cparameters,string> {

		constructor(data:string, $this:Creport) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.reports[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'report'`; }
	}
}
export namespace Creport_number_parameter {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Treport_number_parameter['configuration'], parent:Creport_number_parameter) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dnumber extends Reference<parameters.Cnumber,string> {

		constructor(data:string, $this:Creport_number_parameter) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.report())
				.then(context => context?.properties.report?.ref)
				.then(context => {
					const entry = context?.properties.parameters.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as parameters.Cnumber;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Creport_text_parameter {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Treport_text_parameter['configuration'], parent:Creport_text_parameter) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dtext extends Reference<parameters.Ctext,string> {

		constructor(data:string, $this:Creport_text_parameter) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.report())
				.then(context => context?.properties.report?.ref)
				.then(context => {
					const entry = context?.properties.parameters.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as parameters.Ctext;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Csort_property {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tsort_property['configuration'], parent:Csort_property) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.property_path)
							.then(context => context?.component_root.output.result_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Don extends StateGroup<
		{ name: 'number', node:Cnumber__on, init:Tnumber__on}|
		{ name: 'state group', node:Cstate_group__on, init:Tstate_group__on}|
		{ name: 'text', node:Ctext__on, init:Ttext__on}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__on, parent:Csort_property) => new Cnumber__on(init, parent);
				case 'state group': return (init:Tstate_group__on, parent:Csort_property) => new Cstate_group__on(init, parent);
				case 'text': return (init:Ttext__on, parent:Csort_property) => new Ctext__on(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return finalize_number__on;
				case 'state group': return finalize_state_group__on;
				case 'text': return finalize_text__on;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsort_property['on'], parent:Csort_property) {
			super(data, parent, 'on');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'on'`; }
	}
	export class Dproperty extends Reference<gui_model.Cattributes,string> {

		constructor(data:string, $this:Csort_property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tsort_property['property path'], parent:Csort_property) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'property path')
		}
	}
}
export namespace Cstate__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tstate__type__model_binding['configuration'], parent:Cstate__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.state_group_context?.state.node.output.state_node_context()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dstate_group_context extends StateGroup<
		{ name: 'state group binding', node:Cstate_group_binding, init:Tstate_group_binding}|
		{ name: 'states list', node:Cstates_list__state_group_context, init:Tstates_list__state_group_context}> {
		protected initializer(state: 'state group binding'|'states list') {
			switch (state) {
				case 'state group binding': return (init:Tstate_group_binding, parent:Cstate__type__model_binding) => new Cstate_group_binding(init, parent);
				case 'states list': return (init:Tstates_list__state_group_context, parent:Cstate__type__model_binding) => new Cstates_list__state_group_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'state group binding'|'states list') {
			switch (state) {
				case 'state group binding': return finalize_state_group_binding;
				case 'states list': return finalize_states_list__state_group_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate__type__model_binding['state group context'], parent:Cstate__type__model_binding) {
			super(data, parent, 'state group context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group context'`; }
	}
}
export namespace Cstate_group_binding {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group_binding())
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstate_group__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tstate_group__type__model_binding['configuration'], parent:Cstate_group__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Tstate_group__type__model_binding['property path'], parent:Cstate_group__type__model_binding) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_rule__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tstate_rule__type__model_binding['configuration'], parent:Cstate_rule__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.rule?.ref)
							.then(context => context?.properties.referenced_node)
							.then(context => context?.component_root.output.result_gui_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Cstate_rule__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Ctext__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Ttext__type__model_binding['configuration'], parent:Ctext__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.model_context)
					.then(context => context?.definitions.entry_or_node_context)
					.then(context => context?.definitions.context).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dproperty extends Reference<gui_model.Ctext__type__property__type__attributes,string> {

		constructor(data:string, $this:Ctext__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dproperty_path extends Cmodel_binding_property_path {
		constructor(data:Ttext__type__model_binding['property path'], parent:Ctext__type__model_binding) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.gui_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'property path')
		}
	}
}
export namespace Cuser {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tuser['configuration'], parent:Cuser) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.inferences.has_user_node())
							.then(context => context?.properties.users?.ref)
							.then(context => context?.properties.node).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
}
export namespace Cwindow__type__model_binding {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Twindow__type__model_binding['configuration'], parent:Cwindow__type__model_binding) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.model_binding())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cwindow__type__model_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cview__type__gui_widget_configuration_attribute {
	export class Drender extends StateGroup<
		{ name: 'in window', node:Cin_window, init:Tin_window}> {
		protected initializer(state: 'in window') {
			switch (state) {
				case 'in window': return (init:Tin_window, parent:Cview__type__gui_widget_configuration_attribute) => new Cin_window(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'in window') {
			switch (state) {
				case 'in window': return finalize_in_window;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview__type__gui_widget_configuration_attribute['render'], parent:Cview__type__gui_widget_configuration_attribute) {
			super(data, parent, 'render');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'render'`; }
	}
	export class Dusing_views extends StateGroup<
		{ name: 'external', node:Cexternal__using_views__view__type__gui_widget_configuration_attribute, init:Texternal__using_views__view__type__gui_widget_configuration_attribute}|
		{ name: 'internal', node:Cinternal__using_views__view__type__gui_widget_configuration_attribute, init:Tinternal__using_views__view__type__gui_widget_configuration_attribute}> {
		protected initializer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return (init:Texternal__using_views__view__type__gui_widget_configuration_attribute, parent:Cview__type__gui_widget_configuration_attribute) => new Cexternal__using_views__view__type__gui_widget_configuration_attribute(init, parent);
				case 'internal': return (init:Tinternal__using_views__view__type__gui_widget_configuration_attribute, parent:Cview__type__gui_widget_configuration_attribute) => new Cinternal__using_views__view__type__gui_widget_configuration_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return finalize_external__using_views__view__type__gui_widget_configuration_attribute;
				case 'internal': return finalize_internal__using_views__view__type__gui_widget_configuration_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview__type__gui_widget_configuration_attribute['using views'], parent:Cview__type__gui_widget_configuration_attribute) {
			super(data, parent, 'using views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'using views'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cview__type__gui_widget_configuration_attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.using_views?.state.node.output.definition())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const views__gui_widget_configuration_attribute__type__view__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__gui_widget_configuration_attribute__type__view__view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
	export class Dview_context extends Cview_context_path {
		constructor(data:Tview__type__gui_widget_configuration_attribute['view context'], parent:Cview__type__gui_widget_configuration_attribute) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!)
			}, 'view context')
		}
	}
}
export namespace Cin_window {
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cin_window) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cexternal__using_views__view__type__gui_widget_configuration_attribute {
	export class Dviews extends Reference<views.Cdependencies,string> {

		constructor(data:string, $this:Cexternal__using_views__view__type__gui_widget_configuration_attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.dependencies.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Cwidget__type__gui_widget_configuration_attribute {
	export class Dinstance extends Cwidget_expression {
		constructor(data:Twidget__type__gui_widget_configuration_attribute['instance'], parent:Cwidget__type__gui_widget_configuration_attribute) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'instance')
		}
	}
}
export namespace Cwindow__type__gui_widget_configuration_attribute {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Twindow__type__gui_widget_configuration_attribute['configuration'], parent:Cwindow__type__gui_widget_configuration_attribute) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.default_context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.window())
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dwindow extends Reference<settings.Cwindows,string> {

		constructor(data:string, $this:Cwindow__type__gui_widget_configuration_attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.settings)
				.then(context => {
					const entry = context?.properties.windows.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cgui_widget_configuration_list {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__gui_widget_configuration_list, init:Tno__has_steps__gui_widget_configuration_list}|
		{ name: 'yes', node:Cyes__has_steps__gui_widget_configuration_list, init:Tyes__has_steps__gui_widget_configuration_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__gui_widget_configuration_list, parent:Cgui_widget_configuration_list) => new Cno__has_steps__gui_widget_configuration_list(init, parent);
				case 'yes': return (init:Tyes__has_steps__gui_widget_configuration_list, parent:Cgui_widget_configuration_list) => new Cyes__has_steps__gui_widget_configuration_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__gui_widget_configuration_list;
				case 'yes': return finalize_yes__has_steps__gui_widget_configuration_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_widget_configuration_list['has steps'], parent:Cgui_widget_configuration_list) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__gui_widget_configuration_list {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Tyes__has_steps__gui_widget_configuration_list['configuration'], parent:Cyes__has_steps__gui_widget_configuration_list) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_configuration_node()).result!)
			}, 'configuration')
		}
	}
	export class Dtail extends Cgui_widget_configuration_list {
		constructor(data:Tyes__has_steps__gui_widget_configuration_list['tail'], parent:Cyes__has_steps__gui_widget_configuration_list) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				previous_entry: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.list_counter).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_configuration_node()).result!)
			}, 'tail')
		}
	}
}
export namespace Cgui_widget_configuration_node {
	export class Dconfiguration extends AlanDictionary<{ node:Cconfiguration__gui_widget_configuration_node, init:Tconfiguration__gui_widget_configuration_node},Cgui_widget_configuration_node> {
		protected initialize(parent:Cgui_widget_configuration_node, key:string, entry_init:Tconfiguration__gui_widget_configuration_node) { return new Cconfiguration__gui_widget_configuration_node(key, entry_init, parent); }
		protected finalize = finalize_configuration__gui_widget_configuration_node
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(views__gui_widget_configuration_node__configuration_key_nval:widget.Cattributes__widget_configuration_node):boolean {
				const inferences:{
					client_binding_context: (detach?:boolean) => client_bindings.Cbinding_context|undefined;} = {

					client_binding_context: maybe_cache((detach:boolean) => resolve(views__gui_widget_configuration_node__configuration_key_nval)
						.then(context => evaluate__attribute_client_binding_check(detach, 
							resolve(context)
								.then(context => views__gui_widget_configuration_node__configuration_key_nval).result!, {
							inherited_binding: () => resolve(this_obj)
								.then(() => this_obj)
								.then(context => context?.component_root.input.client_binding()).result!

							}))?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.client_binding_context() === undefined)
					return false;
				const views__gui_widget_configuration_node__configuration_cond_nval = views__gui_widget_configuration_node__configuration_key_nval;
				const condition_context = resolve(views__gui_widget_configuration_node__configuration_cond_nval)
				.then(context => views__gui_widget_configuration_node__configuration_cond_nval)
				.then(context => context?.properties.type?.state.node.output.requirement())
				.then(context => context?.variant.name === 'required' ? context.variant.definition as widget.Crequired : undefined).result;
				if (condition_context === undefined) {
					return false;
				}
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.widget_configuration_node())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'configuration'`; }
		constructor(data:Tgui_widget_configuration_node['configuration'], parent:Cgui_widget_configuration_node) {
			super(data, parent);
		}
	}
}
export namespace Cconfiguration__gui_widget_configuration_node {
	export class Dattribute extends Cgui_widget_configuration_attribute {
		constructor(data:Tconfiguration__gui_widget_configuration_node['attribute'], parent:Cconfiguration__gui_widget_configuration_node) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.client_binding_context()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				gui_widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_attribute: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref).result!)
			}, 'attribute')
		}
	}
}
export namespace Cmodel_binding_property_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__model_binding_property_path, init:Tno__has_steps__model_binding_property_path}|
		{ name: 'yes', node:Cyes__has_steps__model_binding_property_path, init:Tyes__has_steps__model_binding_property_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__model_binding_property_path, parent:Cmodel_binding_property_path) => new Cno__has_steps__model_binding_property_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__model_binding_property_path, parent:Cmodel_binding_property_path) => new Cyes__has_steps__model_binding_property_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__model_binding_property_path;
				case 'yes': return finalize_yes__has_steps__model_binding_property_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmodel_binding_property_path['has steps'], parent:Cmodel_binding_property_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__model_binding_property_path {
	export class Dtail extends Cmodel_binding_property_path {
		constructor(data:Tyes__has_steps__model_binding_property_path['tail'], parent:Cyes__has_steps__model_binding_property_path) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!),
				participation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.participation()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__yes__has_steps__model_binding_property_path, init:Tgroup__type__yes__has_steps__model_binding_property_path}|
		{ name: 'parent', node:Cparent__type__yes__has_steps__model_binding_property_path, init:Tparent__type__yes__has_steps__model_binding_property_path}|
		{ name: 'state group', node:Cstate_group__type__yes, init:Tstate_group__type__yes}> {
		protected initializer(state: 'group'|'parent'|'state group') {
			switch (state) {
				case 'group': return (init:Tgroup__type__yes__has_steps__model_binding_property_path, parent:Cyes__has_steps__model_binding_property_path) => new Cgroup__type__yes__has_steps__model_binding_property_path(init, parent);
				case 'parent': return (init:Tparent__type__yes__has_steps__model_binding_property_path, parent:Cyes__has_steps__model_binding_property_path) => new Cparent__type__yes__has_steps__model_binding_property_path(init, parent);
				case 'state group': return (init:Tstate_group__type__yes, parent:Cyes__has_steps__model_binding_property_path) => new Cstate_group__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'parent'|'state group') {
			switch (state) {
				case 'group': return finalize_group__type__yes__has_steps__model_binding_property_path;
				case 'parent': return finalize_parent__type__yes__has_steps__model_binding_property_path;
				case 'state group': return finalize_state_group__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__model_binding_property_path['type'], parent:Cyes__has_steps__model_binding_property_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__model_binding_property_path {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__model_binding_property_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate_group__type__yes {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cnode_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__node_path, init:Tno__has_steps__node_path}|
		{ name: 'yes', node:Cyes__has_steps__node_path, init:Tyes__has_steps__node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__node_path, parent:Cnode_path) => new Cno__has_steps__node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__node_path, parent:Cnode_path) => new Cyes__has_steps__node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__node_path;
				case 'yes': return finalize_yes__has_steps__node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_path['has steps'], parent:Cnode_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__node_path {
	export class Dtail extends Cnode_path {
		constructor(data:Tyes__has_steps__node_path['tail'], parent:Cyes__has_steps__node_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry, init:Tcollection_entry}|
		{ name: 'group', node:Cgroup__type__yes__has_steps__node_path, init:Tgroup__type__yes__has_steps__node_path}|
		{ name: 'parent', node:Cparent__type__yes__has_steps__node_path, init:Tparent__type__yes__has_steps__node_path}|
		{ name: 'reference', node:Creference__type__yes__has_steps__node_path, init:Treference__type__yes__has_steps__node_path}|
		{ name: 'reference rule', node:Creference_rule__type__yes, init:Treference_rule__type__yes}|
		{ name: 'state', node:Cstate__type__yes__has_steps__node_path, init:Tstate__type__yes__has_steps__node_path}|
		{ name: 'state rule', node:Cstate_rule__type__yes__has_steps__node_path, init:Tstate_rule__type__yes__has_steps__node_path}> {
		protected initializer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry, parent:Cyes__has_steps__node_path) => new Ccollection_entry(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cgroup__type__yes__has_steps__node_path(init, parent);
				case 'parent': return (init:Tparent__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cparent__type__yes__has_steps__node_path(init, parent);
				case 'reference': return (init:Treference__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Creference__type__yes__has_steps__node_path(init, parent);
				case 'reference rule': return (init:Treference_rule__type__yes, parent:Cyes__has_steps__node_path) => new Creference_rule__type__yes(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cstate__type__yes__has_steps__node_path(init, parent);
				case 'state rule': return (init:Tstate_rule__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cstate_rule__type__yes__has_steps__node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry;
				case 'group': return finalize_group__type__yes__has_steps__node_path;
				case 'parent': return finalize_parent__type__yes__has_steps__node_path;
				case 'reference': return finalize_reference__type__yes__has_steps__node_path;
				case 'reference rule': return finalize_reference_rule__type__yes;
				case 'state': return finalize_state__type__yes__has_steps__node_path;
				case 'state rule': return finalize_state_rule__type__yes__has_steps__node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__node_path['type'], parent:Cyes__has_steps__node_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry {
	export class Dcollection extends Reference<gui_model.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as gui_model.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__node_path {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Creference__type__yes__has_steps__node_path {
	export class Dtext extends Reference<gui_model.Ctext__type__property__type__attributes,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			navigable: () => gui_model.Cyes__navigable
		}

		constructor(data:string, $this:Creference__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.text.inferences.navigable())
								.then(context => context?.inferences.reference())
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				navigable: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => views__node_path__has_steps__yes__type__reference__text_nval)
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creference_rule__type__yes {
	export class Dproperty extends Reference<gui_model.Ctext__type__property__type__attributes,string> {
		public readonly inferences:{
			navigable: () => gui_model.Cyes__navigable
		}

		constructor(data:string, $this:Creference_rule__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				navigable: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__reference_rule__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => views__node_path__has_steps__yes__type__reference_rule__property_nval)
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Drule extends Reference<gui_model.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application
		}

		constructor(data:string, $this:Creference_rule__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property?.inferences.navigable())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__reference_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__node_path__has_steps__yes__type__reference_rule__rule_nval)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cstate__type__yes__has_steps__node_path {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_rule__type__yes__has_steps__node_path {
	export class Drule extends Reference<gui_model.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			mandatory_reference: () => application.Cmandatory__type
		}

		constructor(data:string, $this:Cstate_rule__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__node_path__has_steps__yes__type__state_rule__rule_nval)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				mandatory_reference: cache((detach:boolean) => {
					const views__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => views__node_path__has_steps__yes__type__state_rule__rule_nval)
						.then(context => context?.key?.ref)
						.then(context => context?.properties.behaviour)
						.then(context => {
							if (context?.properties.type.state.name === 'mandatory') {
								return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cnumber_filter_expression {
	export class Doperator extends StateGroup<
		{ name: 'equals', node:Cequals, init:Tequals}|
		{ name: 'range', node:Crange, init:Trange}> {
		protected initializer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return (init:Tequals, parent:Cnumber_filter_expression) => new Cequals(init, parent);
				case 'range': return (init:Trange, parent:Cnumber_filter_expression) => new Crange(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return finalize_equals;
				case 'range': return finalize_range;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_filter_expression['operator'], parent:Cnumber_filter_expression) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cequals {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tequals['criterium'], parent:Cequals) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Crange {
	export class Dgreater_than extends StateGroup<
		{ name: 'no', node:Cno__greater_than, init:Tno__greater_than}|
		{ name: 'yes', node:Cyes__greater_than, init:Tyes__greater_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__greater_than, parent:Crange) => new Cno__greater_than(init, parent);
				case 'yes': return (init:Tyes__greater_than, parent:Crange) => new Cyes__greater_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__greater_than;
				case 'yes': return finalize_yes__greater_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['greater than'], parent:Crange) {
			super(data, parent, 'greater than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'greater than'`; }
	}
	export class Dless_than extends StateGroup<
		{ name: 'no', node:Cno__less_than, init:Tno__less_than}|
		{ name: 'yes', node:Cyes__less_than, init:Tyes__less_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__less_than, parent:Crange) => new Cno__less_than(init, parent);
				case 'yes': return (init:Tyes__less_than, parent:Crange) => new Cyes__less_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__less_than;
				case 'yes': return finalize_yes__less_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['less than'], parent:Crange) {
			super(data, parent, 'less than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'less than'`; }
	}
}
export namespace Cyes__greater_than {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tyes__greater_than['criterium'], parent:Cyes__greater_than) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Cyes__less_than {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tyes__less_than['criterium'], parent:Cyes__less_than) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Cquery_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__query_path, init:Tno__has_steps__query_path}|
		{ name: 'yes', node:Cyes__has_steps__query_path, init:Tyes__has_steps__query_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__query_path, parent:Cquery_path) => new Cno__has_steps__query_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__query_path, parent:Cquery_path) => new Cyes__has_steps__query_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__query_path;
				case 'yes': return finalize_yes__has_steps__query_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery_path['has steps'], parent:Cquery_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__query_path {
	export class Dcollection extends Reference<gui_model.Ccollection__type__property,string> {

		constructor(data:string, $this:Cyes__has_steps__query_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as gui_model.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dhead extends Cquery_path_step {
		constructor(data:Tyes__has_steps__query_path['head'], parent:Cyes__has_steps__query_path) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_node()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Cquery_path {
		constructor(data:Tyes__has_steps__query_path['tail'], parent:Cyes__has_steps__query_path) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!)
			}, 'tail')
		}
	}
}
export namespace Cquery_path_step {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__query_path_step, init:Tno__has_steps__query_path_step}|
		{ name: 'yes', node:Cyes__has_steps__query_path_step, init:Tyes__has_steps__query_path_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__query_path_step, parent:Cquery_path_step) => new Cno__has_steps__query_path_step(init, parent);
				case 'yes': return (init:Tyes__has_steps__query_path_step, parent:Cquery_path_step) => new Cyes__has_steps__query_path_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__query_path_step;
				case 'yes': return finalize_yes__has_steps__query_path_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery_path_step['has steps'], parent:Cquery_path_step) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__query_path_step {
	export class Dtail extends Cquery_path_step {
		constructor(data:Tyes__has_steps__query_path_step['tail'], parent:Cyes__has_steps__query_path_step) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__yes__has_steps__query_path_step, init:Tgroup__type__yes__has_steps__query_path_step}|
		{ name: 'state', node:Cstate__type__yes__has_steps__query_path_step, init:Tstate__type__yes__has_steps__query_path_step}> {
		protected initializer(state: 'group'|'state') {
			switch (state) {
				case 'group': return (init:Tgroup__type__yes__has_steps__query_path_step, parent:Cyes__has_steps__query_path_step) => new Cgroup__type__yes__has_steps__query_path_step(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__query_path_step, parent:Cyes__has_steps__query_path_step) => new Cstate__type__yes__has_steps__query_path_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'state') {
			switch (state) {
				case 'group': return finalize_group__type__yes__has_steps__query_path_step;
				case 'state': return finalize_state__type__yes__has_steps__query_path_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__query_path_step['type'], parent:Cyes__has_steps__query_path_step) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__query_path_step {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__query_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate__type__yes__has_steps__query_path_step {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__query_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__query_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cview_context_parent_path {
	export class Dhas_tail extends StateGroup<
		{ name: 'no', node:Cno__has_tail, init:Tno__has_tail}|
		{ name: 'yes', node:Cyes__has_tail, init:Tyes__has_tail}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_tail, parent:Cview_context_parent_path) => new Cno__has_tail(init, parent);
				case 'yes': return (init:Tyes__has_tail, parent:Cview_context_parent_path) => new Cyes__has_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_tail;
				case 'yes': return finalize_yes__has_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_context_parent_path['has tail'], parent:Cview_context_parent_path) {
			super(data, parent, 'has tail');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has tail'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'collection parent', node:Ccollection_parent, init:Tcollection_parent}|
		{ name: 'group parent', node:Cgroup_parent, init:Tgroup_parent}|
		{ name: 'state parent', node:Cstate_parent, init:Tstate_parent}> {
		protected initializer(state: 'collection parent'|'group parent'|'state parent') {
			switch (state) {
				case 'collection parent': return (init:Tcollection_parent, parent:Cview_context_parent_path) => new Ccollection_parent(init, parent);
				case 'group parent': return (init:Tgroup_parent, parent:Cview_context_parent_path) => new Cgroup_parent(init, parent);
				case 'state parent': return (init:Tstate_parent, parent:Cview_context_parent_path) => new Cstate_parent(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection parent'|'group parent'|'state parent') {
			switch (state) {
				case 'collection parent': return finalize_collection_parent;
				case 'group parent': return finalize_group_parent;
				case 'state parent': return finalize_state_parent;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_context_parent_path['type'], parent:Cview_context_parent_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_tail {
	export class Dtail extends Cview_context_parent_path {
		constructor(data:Tyes__has_tail['tail'], parent:Cyes__has_tail) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.selected_node()).result!)
			}, 'tail')
		}
	}
}
export namespace Cview_context_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__view_context_path, init:Tno__has_steps__view_context_path}|
		{ name: 'yes', node:Cyes__has_steps__view_context_path, init:Tyes__has_steps__view_context_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__view_context_path, parent:Cview_context_path) => new Cno__has_steps__view_context_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__view_context_path, parent:Cview_context_path) => new Cyes__has_steps__view_context_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__view_context_path;
				case 'yes': return finalize_yes__has_steps__view_context_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview_context_path['has steps'], parent:Cview_context_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__view_context_path {
	export class Dtype extends StateGroup<
		{ name: 'entity', node:Centity__type__yes, init:Tentity__type__yes}|
		{ name: 'parent', node:Cparent__type__yes__has_steps__view_context_path, init:Tparent__type__yes__has_steps__view_context_path}|
		{ name: 'query entry', node:Cquery_entry, init:Tquery_entry}|
		{ name: 'reference', node:Creference__type__yes__has_steps__view_context_path, init:Treference__type__yes__has_steps__view_context_path}|
		{ name: 'state rule', node:Cstate_rule__type__yes__has_steps__view_context_path, init:Tstate_rule__type__yes__has_steps__view_context_path}> {
		protected initializer(state: 'entity'|'parent'|'query entry'|'reference'|'state rule') {
			switch (state) {
				case 'entity': return (init:Tentity__type__yes, parent:Cyes__has_steps__view_context_path) => new Centity__type__yes(init, parent);
				case 'parent': return (init:Tparent__type__yes__has_steps__view_context_path, parent:Cyes__has_steps__view_context_path) => new Cparent__type__yes__has_steps__view_context_path(init, parent);
				case 'query entry': return (init:Tquery_entry, parent:Cyes__has_steps__view_context_path) => new Cquery_entry(init, parent);
				case 'reference': return (init:Treference__type__yes__has_steps__view_context_path, parent:Cyes__has_steps__view_context_path) => new Creference__type__yes__has_steps__view_context_path(init, parent);
				case 'state rule': return (init:Tstate_rule__type__yes__has_steps__view_context_path, parent:Cyes__has_steps__view_context_path) => new Cstate_rule__type__yes__has_steps__view_context_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entity'|'parent'|'query entry'|'reference'|'state rule') {
			switch (state) {
				case 'entity': return finalize_entity__type__yes;
				case 'parent': return finalize_parent__type__yes__has_steps__view_context_path;
				case 'query entry': return finalize_query_entry;
				case 'reference': return finalize_reference__type__yes__has_steps__view_context_path;
				case 'state rule': return finalize_state_rule__type__yes__has_steps__view_context_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__view_context_path['type'], parent:Cyes__has_steps__view_context_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cparent__type__yes__has_steps__view_context_path {
	export class Dpath extends Cview_context_parent_path {
		constructor(data:Tparent__type__yes__has_steps__view_context_path['path'], parent:Cparent__type__yes__has_steps__view_context_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node()).result!)
			}, 'path')
		}
	}
}
export namespace Cwidget_expression {
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__widget_expression, init:Tgroup__type__widget_expression}|
		{ name: 'parent', node:Cparent__type__widget_expression, init:Tparent__type__widget_expression}|
		{ name: 'reference', node:Creference__type__widget_expression, init:Treference__type__widget_expression}|
		{ name: 'reference rule', node:Creference_rule__type__widget_expression, init:Treference_rule__type__widget_expression}|
		{ name: 'state', node:Cstate__type__widget_expression, init:Tstate__type__widget_expression}|
		{ name: 'state context rule', node:Cstate_context_rule, init:Tstate_context_rule}|
		{ name: 'switch', node:Cswitch, init:Tswitch}|
		{ name: 'view', node:Cview__type__widget_expression, init:Tview__type__widget_expression}|
		{ name: 'widget', node:Cwidget__type__widget_expression, init:Twidget__type__widget_expression}> {
		protected initializer(state: 'group'|'parent'|'reference'|'reference rule'|'state'|'state context rule'|'switch'|'view'|'widget') {
			switch (state) {
				case 'group': return (init:Tgroup__type__widget_expression, parent:Cwidget_expression) => new Cgroup__type__widget_expression(init, parent);
				case 'parent': return (init:Tparent__type__widget_expression, parent:Cwidget_expression) => new Cparent__type__widget_expression(init, parent);
				case 'reference': return (init:Treference__type__widget_expression, parent:Cwidget_expression) => new Creference__type__widget_expression(init, parent);
				case 'reference rule': return (init:Treference_rule__type__widget_expression, parent:Cwidget_expression) => new Creference_rule__type__widget_expression(init, parent);
				case 'state': return (init:Tstate__type__widget_expression, parent:Cwidget_expression) => new Cstate__type__widget_expression(init, parent);
				case 'state context rule': return (init:Tstate_context_rule, parent:Cwidget_expression) => new Cstate_context_rule(init, parent);
				case 'switch': return (init:Tswitch, parent:Cwidget_expression) => new Cswitch(init, parent);
				case 'view': return (init:Tview__type__widget_expression, parent:Cwidget_expression) => new Cview__type__widget_expression(init, parent);
				case 'widget': return (init:Twidget__type__widget_expression, parent:Cwidget_expression) => new Cwidget__type__widget_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'parent'|'reference'|'reference rule'|'state'|'state context rule'|'switch'|'view'|'widget') {
			switch (state) {
				case 'group': return finalize_group__type__widget_expression;
				case 'parent': return finalize_parent__type__widget_expression;
				case 'reference': return finalize_reference__type__widget_expression;
				case 'reference rule': return finalize_reference_rule__type__widget_expression;
				case 'state': return finalize_state__type__widget_expression;
				case 'state context rule': return finalize_state_context_rule;
				case 'switch': return finalize_switch;
				case 'view': return finalize_view__type__widget_expression;
				case 'widget': return finalize_widget__type__widget_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twidget_expression['type'], parent:Cwidget_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__widget_expression {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
	export class Dnext extends Cwidget_expression {
		constructor(data:Tgroup__type__widget_expression['next'], parent:Cgroup__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.group?.ref)
							.then(context => context?.properties.gui_node).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
}
export namespace Cparent__type__widget_expression {
	export class Dnext extends Cwidget_expression {
		constructor(data:Tparent__type__widget_expression['next'], parent:Cparent__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.inferences.parent()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
}
export namespace Creference__type__widget_expression {
	export class Dnext extends Cwidget_expression {
		constructor(data:Treference__type__widget_expression['next'], parent:Creference__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.text?.inferences.reference())
							.then(context => context?.properties.referenced_node)
							.then(context => context?.component_root.output.result_gui_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
	export class Dtext extends Reference<gui_model.Ctext__type__property__type__attributes,string> {
		public readonly inferences:{
			reference: () => gui_model.Cyes__navigable
		}

		constructor(data:string, $this:Creference__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const views__widget_expression__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => views__widget_expression__type__reference__text_nval)
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creference_rule__type__widget_expression {
	export class Dnext extends Cwidget_expression {
		constructor(data:Treference_rule__type__widget_expression['next'], parent:Creference_rule__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.rule?.ref)
							.then(context => context?.properties.referenced_node)
							.then(context => context?.component_root.output.result_gui_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Creference_rule__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.text?.inferences.reference())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
	export class Dtext extends Reference<gui_model.Ctext__type__property__type__attributes,string> {
		public readonly inferences:{
			reference: () => gui_model.Cyes__navigable
		}

		constructor(data:string, $this:Creference_rule__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const views__widget_expression__type__reference_rule__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => views__widget_expression__type__reference_rule__text_nval)
						.then(context => {
							if (context?.properties.navigable.state.name === 'yes') {
								return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cstate__type__widget_expression {
	export class Dnext extends Cwidget_expression {
		constructor(data:Tstate__type__widget_expression['next'], parent:Cstate__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.state?.ref)
							.then(context => context?.properties.gui_node).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_context_rule {
	export class Dnext extends Cwidget_expression {
		constructor(data:Tstate_context_rule['next'], parent:Cstate_context_rule) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.rule?.ref)
							.then(context => context?.properties.referenced_node)
							.then(context => context?.component_root.output.result_gui_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
	export class Drule extends Reference<gui_model.Crules,string> {

		constructor(data:string, $this:Cstate_context_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cswitch {
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cswitch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.gui_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__switch, init:Tstates__switch},Cswitch> {
		protected initialize(parent:Cswitch, key:string, entry_init:Tstates__switch) { return new Cstates__switch(key, entry_init, parent); }
		protected finalize = finalize_states__switch
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(views__widget_expression__type__switch__states_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tswitch['states'], parent:Cswitch) {
			super(data, parent);
		}
	}
}
export namespace Cstates__switch {
	export class Dnext extends Cwidget_expression {
		constructor(data:Tstates__switch['next'], parent:Cstates__switch) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.key?.ref)
							.then(context => context?.properties.gui_node).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'next')
		}
	}
}
export namespace Cview__type__widget_expression {
	export class Dusing_views extends StateGroup<
		{ name: 'external', node:Cexternal__using_views__view__type__widget_expression, init:Texternal__using_views__view__type__widget_expression}|
		{ name: 'internal', node:Cinternal__using_views__view__type__widget_expression, init:Tinternal__using_views__view__type__widget_expression}> {
		protected initializer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return (init:Texternal__using_views__view__type__widget_expression, parent:Cview__type__widget_expression) => new Cexternal__using_views__view__type__widget_expression(init, parent);
				case 'internal': return (init:Tinternal__using_views__view__type__widget_expression, parent:Cview__type__widget_expression) => new Cinternal__using_views__view__type__widget_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return finalize_external__using_views__view__type__widget_expression;
				case 'internal': return finalize_internal__using_views__view__type__widget_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tview__type__widget_expression['using views'], parent:Cview__type__widget_expression) {
			super(data, parent, 'using views');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'using views'`; }
	}
	export class Dview extends Reference<views.Cviews__,string> {
		public readonly inferences:{
			constraint: () => gui_model.Cgui_node
		}

		constructor(data:string, $this:Cview__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.using_views?.state.node.output.definition())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const views__widget_expression__type__view__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => views__widget_expression__type__view__view_nval)
								.then(context => context?.properties.node_type)
								.then(context => context?.component_root.output.result_gui_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.gui_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
}
export namespace Cexternal__using_views__view__type__widget_expression {
	export class Dviews extends Reference<views.Cdependencies,string> {

		constructor(data:string, $this:Cexternal__using_views__view__type__widget_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.dependencies.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
	}
}
export namespace Cwidget__type__widget_expression {
	export class Dlazy extends StateGroup<
		{ name: 'no', node:Cno__lazy, init:Tno__lazy}|
		{ name: 'yes', node:Cyes__lazy, init:Tyes__lazy}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__lazy, parent:Cwidget__type__widget_expression) => new Cno__lazy(init, parent);
				case 'yes': return (init:Tyes__lazy, parent:Cwidget__type__widget_expression) => new Cyes__lazy(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__lazy;
				case 'yes': return finalize_yes__lazy;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twidget__type__widget_expression['lazy'], parent:Cwidget__type__widget_expression) {
			super(data, parent, 'lazy');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'lazy'`; }
	}
	export class Dwidget extends Cwidget_instance {
		constructor(data:Twidget__type__widget_expression['widget'], parent:Cwidget__type__widget_expression) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!)
			}, 'widget')
		}
	}
}
export namespace Cwidget_instance {
	export class Dconfiguration extends Cgui_widget_configuration_node {
		constructor(data:Twidget_instance['configuration'], parent:Cwidget_instance) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.widget?.inferences.client_binding_context()).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.view()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.widget?.ref)
					.then(context => context?.properties.widget).result!)
			}, 'configuration')
		}
	}
	export class Dwidget extends Reference<widget.Cwidget,string> {
		public readonly inferences:{
			client_binding_check: () => client_bindings.Cbinding_context,
			client_binding_context: () => client_bindings.Cbinding_context
		}

		constructor(data:string, $this:Cwidget_instance) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.widgets[this.entry]).result!), $this)
			this.inferences = {
				client_binding_check: cache((detach:boolean) => {
					const views__widget_instance__widget_nval = $this.properties.widget.ref;
					return resolve($this.properties.widget.ref)
						.then(context => evaluate__client_binding_check(detach, 
							resolve(context)
								.then(context => views__widget_instance__widget_nval).result!, {
							client_binding: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.client_binding()).result!

							})).result!;
				})
				,
				client_binding_context: cache((detach:boolean) => {
					const views__widget_instance__widget_nval = $this.properties.widget.ref;
					return resolve($this.properties.widget.ref)
						.then(context => evaluate__client_binding_switch(detach, 
							resolve(context)
								.then(context => views__widget_instance__widget_nval).result!, {
							client_binding: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.client_binding()).result!

							})).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'widget'`; }
	}
}
export namespace Cviews {
	export class Ddependencies extends AlanDictionary<{ node:Cdependencies, init:Tdependencies},Cviews> {
		protected initialize(parent:Cviews, key:string) { return new Cdependencies(key, {}, parent); }
		protected finalize = finalize_dependencies
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'dependencies'`; }
		constructor(data:Tviews['dependencies'], parent:Cviews) {
			super(data, parent);
		}
	}
	export class Dviews extends AlanDictionary<{ node:Cviews__, init:Tviews__},Cviews> {
		protected initialize(parent:Cviews, key:string, entry_init:Tviews__) { return new Cviews__(key, entry_init, parent); }
		protected finalize = finalize_views__
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
		constructor(data:Tviews['views'], parent:Cviews) {
			super(data, parent);
		}
	}
}
export namespace Cviews__ {
	export class Dinstance extends Cwidget_expression {
		constructor(data:Tviews__['instance'], parent:Cviews__) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.client_bindings)
					.then(context => context?.properties.initial_view_binding?.ref)
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.node_type)
							.then(context => context?.component_root.output.result_gui_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'instance')
		}
	}
	export class Dnode_type extends Cgui_node_type_path {
		constructor(data:Tviews__['node type'], parent:Cviews__) {
			super(data, parent, 'node type')
		}
	}
	export class Dqueries extends AlanDictionary<{ node:Cqueries, init:Tqueries},Cviews__> {
		protected initialize(parent:Cviews__, key:string, entry_init:Tqueries) { return new Cqueries(key, entry_init, parent); }
		protected finalize = finalize_queries
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'queries'`; }
		constructor(data:Tviews__['queries'], parent:Cviews__) {
			super(data, parent);
		}
	}
	export class Dtranslate_title extends StateGroup<
		{ name: 'no', node:Cno__translate_title, init:Tno__translate_title}|
		{ name: 'yes', node:Cyes__translate_title, init:Tyes__translate_title}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__translate_title, parent:Cviews__) => new Cno__translate_title(init, parent);
				case 'yes': return (init:Tyes__translate_title, parent:Cviews__) => new Cyes__translate_title(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__translate_title;
				case 'yes': return finalize_yes__translate_title;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tviews__['translate title'], parent:Cviews__) {
			super(data, parent, 'translate title');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'translate title'`; }
	}
}
export namespace Cqueries {
	export class Dcolumns extends AlanTopology<{ node:Ccolumns, init:Tcolumns},Cqueries,'column order'> {
		protected graph_iterator(graph:('column order')):(node:Ccolumns) => Ccolumns {
			switch (graph) {
				case 'column order': return (entry:Ccolumns) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as views.Cyes__has_successor__columns;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cqueries, key:string, entry_init:Tcolumns) { return new Ccolumns(key, entry_init, parent); }
		protected finalize = finalize_columns
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'columns'`; }
		constructor(data:Tqueries['columns'], parent:Cqueries) {
			super(data, parent);
		}
	}
	export class Dcontext extends StateGroup<
		{ name: 'candidates', node:Ccandidates__context, init:Tcandidates__context}|
		{ name: 'node', node:Cnode__context, init:Tnode__context}> {
		protected initializer(state: 'candidates'|'node') {
			switch (state) {
				case 'candidates': return (init:Tcandidates__context, parent:Cqueries) => new Ccandidates__context(init, parent);
				case 'node': return (init:Tnode__context, parent:Cqueries) => new Cnode__context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'candidates'|'node') {
			switch (state) {
				case 'candidates': return finalize_candidates__context;
				case 'node': return finalize_node__context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tqueries['context'], parent:Cqueries) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
	export class Dhas_columns extends StateGroup<
		{ name: 'no', node:Cno__has_columns, init:Tno__has_columns}|
		{ name: 'yes', node:Cyes__has_columns, init:Tyes__has_columns}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_columns, parent:Cqueries) => new Cno__has_columns(init, parent);
				case 'yes': return (init:Tyes__has_columns, parent:Cqueries) => new Cyes__has_columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_columns;
				case 'yes': return finalize_yes__has_columns;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tqueries['has columns'], parent:Cqueries) {
			super(data, parent, 'has columns');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has columns'`; }
	}
}
export namespace Ccolumns {
	export class Dcolumn_type extends StateGroup<
		{ name: 'content', node:Ccontent, init:Tcontent}|
		{ name: 'id', node:Cid, init:Tid}> {
		protected initializer(state: 'content'|'id') {
			switch (state) {
				case 'content': return (init:Tcontent, parent:Ccolumns) => new Ccontent(init, parent);
				case 'id': return (init:Tid, parent:Ccolumns) => new Cid(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'content'|'id') {
			switch (state) {
				case 'content': return finalize_content;
				case 'id': return finalize_id;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['column type'], parent:Ccolumns) {
			super(data, parent, 'column type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'column type'`; }
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__columns, init:Tno__has_successor__columns}|
		{ name: 'yes', node:Cyes__has_successor__columns, init:Tyes__has_successor__columns}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__columns, parent:Ccolumns) => new Cno__has_successor__columns(init, parent);
				case 'yes': return (init:Tyes__has_successor__columns, parent:Ccolumns) => new Cyes__has_successor__columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__columns;
				case 'yes': return finalize_yes__has_successor__columns;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['has successor'], parent:Ccolumns) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dname extends Reference<phrases.Cphrases__,string> {

		constructor(data:string, $this:Ccolumns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.phrases)
				.then(context => {
					const entry = context?.properties.phrases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
	export class Dpath extends Cnode_path {
		constructor(data:Tcolumns['path'], parent:Ccolumns) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context?.state.node.output.selected_context())
					.then(context => context?.component_root.output.gui_entity()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile__type__columns, init:Tfile__type__columns}|
		{ name: 'number', node:Cnumber__type__columns, init:Tnumber__type__columns}|
		{ name: 'state group', node:Cstate_group__type__columns, init:Tstate_group__type__columns}|
		{ name: 'text', node:Ctext__type__columns, init:Ttext__type__columns}|
		{ name: 'widget', node:Cwidget__type__columns, init:Twidget__type__columns}> {
		protected initializer(state: 'file'|'number'|'state group'|'text'|'widget') {
			switch (state) {
				case 'file': return (init:Tfile__type__columns, parent:Ccolumns) => new Cfile__type__columns(init, parent);
				case 'number': return (init:Tnumber__type__columns, parent:Ccolumns) => new Cnumber__type__columns(init, parent);
				case 'state group': return (init:Tstate_group__type__columns, parent:Ccolumns) => new Cstate_group__type__columns(init, parent);
				case 'text': return (init:Ttext__type__columns, parent:Ccolumns) => new Ctext__type__columns(init, parent);
				case 'widget': return (init:Twidget__type__columns, parent:Ccolumns) => new Cwidget__type__columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'file'|'number'|'state group'|'text'|'widget') {
			switch (state) {
				case 'file': return finalize_file__type__columns;
				case 'number': return finalize_number__type__columns;
				case 'state group': return finalize_state_group__type__columns;
				case 'text': return finalize_text__type__columns;
				case 'widget': return finalize_widget__type__columns;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['type'], parent:Ccolumns) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_successor__columns {
	export class Dsuccessor extends Reference<views.Ccolumns,string> {

		constructor(data:string, $this:Cyes__has_successor__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.columns.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cfile__type__columns {
	export class Dfile extends Reference<gui_model.Cfile__type__property,string> {

		constructor(data:string, $this:Cfile__type__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'file') {
							return context.properties.type.state.node as gui_model.Cfile__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'file'`; }
	}
}
export namespace Cnumber__type__columns {
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter__number, init:Tno__has_filter__number}|
		{ name: 'yes', node:Cyes__has_filter__number, init:Tyes__has_filter__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter__number, parent:Cnumber__type__columns) => new Cno__has_filter__number(init, parent);
				case 'yes': return (init:Tyes__has_filter__number, parent:Cnumber__type__columns) => new Cyes__has_filter__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_filter__number;
				case 'yes': return finalize_yes__has_filter__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__columns['has filter'], parent:Cnumber__type__columns) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
	export class Dnumber extends Reference<gui_model.Cnumber__type__property__type__attributes,string> {

		constructor(data:string, $this:Cnumber__type__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cyes__has_filter__number {
	export class Dinitial_criteria extends StateGroup<
		{ name: 'none', node:Cnone__initial_criteria, init:Tnone__initial_criteria}|
		{ name: 'yes', node:Cyes__initial_criteria, init:Tyes__initial_criteria}> {
		protected initializer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return (init:Tnone__initial_criteria, parent:Cyes__has_filter__number) => new Cnone__initial_criteria(init, parent);
				case 'yes': return (init:Tyes__initial_criteria, parent:Cyes__has_filter__number) => new Cyes__initial_criteria(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return finalize_none__initial_criteria;
				case 'yes': return finalize_yes__initial_criteria;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['initial criteria'], parent:Cyes__has_filter__number) {
			super(data, parent, 'initial criteria');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initial criteria'`; }
	}
	export class Doperator_selected extends StateGroup<
		{ name: 'no', node:Cno__operator_selected, init:Tno__operator_selected}|
		{ name: 'yes', node:Cyes__operator_selected, init:Tyes__operator_selected}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__operator_selected, parent:Cyes__has_filter__number) => new Cno__operator_selected(init, parent);
				case 'yes': return (init:Tyes__operator_selected, parent:Cyes__has_filter__number) => new Cyes__operator_selected(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__operator_selected;
				case 'yes': return finalize_yes__operator_selected;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['operator selected'], parent:Cyes__has_filter__number) {
			super(data, parent, 'operator selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator selected'`; }
	}
}
export namespace Cyes__initial_criteria {
	export class Dsource extends StateGroup<
		{ name: 'now', node:Cnow__source__yes, init:Tnow__source__yes}|
		{ name: 'static', node:Cstatic__source__yes, init:Tstatic__source__yes}> {
		protected initializer(state: 'now'|'static') {
			switch (state) {
				case 'now': return (init:Tnow__source__yes, parent:Cyes__initial_criteria) => new Cnow__source__yes(init, parent);
				case 'static': return (init:Tstatic__source__yes, parent:Cyes__initial_criteria) => new Cstatic__source__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'now'|'static') {
			switch (state) {
				case 'now': return finalize_now__source__yes;
				case 'static': return finalize_static__source__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__initial_criteria['source'], parent:Cyes__initial_criteria) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cnow__source__yes {
	export class Dhas_offset extends StateGroup<
		{ name: 'none', node:Cnone__has_offset__now__source__yes, init:Tnone__has_offset__now__source__yes}|
		{ name: 'yes', node:Cyes__has_offset__now__source__yes, init:Tyes__has_offset__now__source__yes}> {
		protected initializer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return (init:Tnone__has_offset__now__source__yes, parent:Cnow__source__yes) => new Cnone__has_offset__now__source__yes(init, parent);
				case 'yes': return (init:Tyes__has_offset__now__source__yes, parent:Cnow__source__yes) => new Cyes__has_offset__now__source__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'yes') {
			switch (state) {
				case 'none': return finalize_none__has_offset__now__source__yes;
				case 'yes': return finalize_yes__has_offset__now__source__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnow__source__yes['has offset'], parent:Cnow__source__yes) {
			super(data, parent, 'has offset');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has offset'`; }
	}
}
export namespace Cyes__has_offset__now__source__yes {
	export class Doffset extends AlanInteger {
		constructor(data:Tyes__has_offset__now__source__yes['offset'], parent:Cyes__has_offset__now__source__yes) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'offset'`; }
	}
}
export namespace Cstatic__source__yes {
	export class Dnumber extends AlanInteger {
		constructor(data:Tstatic__source__yes['number'], parent:Cstatic__source__yes) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cyes__operator_selected {
	export class Doperator extends StateGroup<
		{ name: 'equal to', node:Cequal_to, init:Tequal_to}|
		{ name: 'greater than', node:Cgreater_than, init:Tgreater_than}|
		{ name: 'greater than or equal to', node:Cgreater_than_or_equal_to, init:Tgreater_than_or_equal_to}|
		{ name: 'smaller than', node:Csmaller_than, init:Tsmaller_than}|
		{ name: 'smaller than or equal to', node:Csmaller_than_or_equal_to, init:Tsmaller_than_or_equal_to}> {
		protected initializer(state: 'equal to'|'greater than'|'greater than or equal to'|'smaller than'|'smaller than or equal to') {
			switch (state) {
				case 'equal to': return (init:Tequal_to, parent:Cyes__operator_selected) => new Cequal_to(init, parent);
				case 'greater than': return (init:Tgreater_than, parent:Cyes__operator_selected) => new Cgreater_than(init, parent);
				case 'greater than or equal to': return (init:Tgreater_than_or_equal_to, parent:Cyes__operator_selected) => new Cgreater_than_or_equal_to(init, parent);
				case 'smaller than': return (init:Tsmaller_than, parent:Cyes__operator_selected) => new Csmaller_than(init, parent);
				case 'smaller than or equal to': return (init:Tsmaller_than_or_equal_to, parent:Cyes__operator_selected) => new Csmaller_than_or_equal_to(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equal to'|'greater than'|'greater than or equal to'|'smaller than'|'smaller than or equal to') {
			switch (state) {
				case 'equal to': return finalize_equal_to;
				case 'greater than': return finalize_greater_than;
				case 'greater than or equal to': return finalize_greater_than_or_equal_to;
				case 'smaller than': return finalize_smaller_than;
				case 'smaller than or equal to': return finalize_smaller_than_or_equal_to;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__operator_selected['operator'], parent:Cyes__operator_selected) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cstate_group__type__columns {
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter__state_group, init:Tno__has_filter__state_group}|
		{ name: 'yes', node:Cyes__has_filter__state_group, init:Tyes__has_filter__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter__state_group, parent:Cstate_group__type__columns) => new Cno__has_filter__state_group(init, parent);
				case 'yes': return (init:Tyes__has_filter__state_group, parent:Cstate_group__type__columns) => new Cyes__has_filter__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_filter__state_group;
				case 'yes': return finalize_yes__has_filter__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__columns['has filter'], parent:Cstate_group__type__columns) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group__type__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cyes__has_filter__state_group {
	export class Dfilter_enabled extends StateGroup<
		{ name: 'no', node:Cno__filter_enabled, init:Tno__filter_enabled}|
		{ name: 'yes', node:Cyes__filter_enabled, init:Tyes__filter_enabled}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_enabled, parent:Cyes__has_filter__state_group) => new Cno__filter_enabled(init, parent);
				case 'yes': return (init:Tyes__filter_enabled, parent:Cyes__has_filter__state_group) => new Cyes__filter_enabled(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__filter_enabled;
				case 'yes': return finalize_yes__filter_enabled;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__state_group['filter enabled'], parent:Cyes__has_filter__state_group) {
			super(data, parent, 'filter enabled');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter enabled'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__yes, init:Tstates__yes},Cyes__has_filter__state_group> {
		protected initialize(parent:Cyes__has_filter__state_group, key:string, entry_init:Tstates__yes) { return new Cstates__yes(key, entry_init, parent); }
		protected finalize = finalize_states__yes
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(views__views__queries__columns__type__state_group__has_filter__yes__states_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tyes__has_filter__state_group['states'], parent:Cyes__has_filter__state_group) {
			super(data, parent);
		}
	}
}
export namespace Cstates__yes {
	export class Dis_selected extends StateGroup<
		{ name: 'no', node:Cno__is_selected, init:Tno__is_selected}|
		{ name: 'yes', node:Cyes__is_selected, init:Tyes__is_selected}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_selected, parent:Cstates__yes) => new Cno__is_selected(init, parent);
				case 'yes': return (init:Tyes__is_selected, parent:Cstates__yes) => new Cyes__is_selected(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__is_selected;
				case 'yes': return finalize_yes__is_selected;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__yes['is selected'], parent:Cstates__yes) {
			super(data, parent, 'is selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is selected'`; }
	}
}
export namespace Ctext__type__columns {
	export class Dfilter extends StateGroup<
		{ name: 'current id path', node:Ccurrent_id_path, init:Tcurrent_id_path}|
		{ name: 'none', node:Cnone__filter, init:Tnone__filter}|
		{ name: 'simple', node:Csimple, init:Tsimple}> {
		protected initializer(state: 'current id path'|'none'|'simple') {
			switch (state) {
				case 'current id path': return (init:Tcurrent_id_path, parent:Ctext__type__columns) => new Ccurrent_id_path(init, parent);
				case 'none': return (init:Tnone__filter, parent:Ctext__type__columns) => new Cnone__filter(init, parent);
				case 'simple': return (init:Tsimple, parent:Ctext__type__columns) => new Csimple(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'current id path'|'none'|'simple') {
			switch (state) {
				case 'current id path': return finalize_current_id_path;
				case 'none': return finalize_none__filter;
				case 'simple': return finalize_simple;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__columns['filter'], parent:Ctext__type__columns) {
			super(data, parent, 'filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter'`; }
	}
	export class Dtext extends Reference<gui_model.Ctext__type__property__type__attributes,string> {

		constructor(data:string, $this:Ctext__type__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Csimple {
}
export namespace Cwidget__type__columns {
	export class Dinstance extends Cwidget_expression {
		constructor(data:Twidget__type__columns['instance'], parent:Cwidget__type__columns) {
			super(data, parent, {
				client_binding: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.client_bindings)
					.then(context => context?.properties.initial_view_binding?.ref)
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				context: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__gui_node_to_context(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.result_node()).result!, )).result!),
				view: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'instance')
		}
	}
}
export namespace Ccandidates__context {
	export class Dproperty extends Reference<gui_model.Cyes__navigable,string> {

		constructor(data:string, $this:Ccandidates__context) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property_context)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.navigable.state.name === 'yes') {
							return context.properties.navigable.state.node as gui_model.Cyes__navigable;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dproperty_context extends Cgui_node_type_path_step {
		constructor(data:Tcandidates__context['property context'], parent:Ccandidates__context) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node_type)
					.then(context => context?.component_root.output.result_gui_node()).result!)
			}, 'property context')
		}
	}
}
export namespace Cnode__context {
	export class Dpath extends Cquery_path {
		constructor(data:Tnode__context['path'], parent:Cnode__context) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.switch_?.state.node.output.selected_context()).result!)
			}, 'path')
		}
	}
	export class Dswitch_ extends StateGroup<
		{ name: 'current', node:Ccurrent, init:Tcurrent}|
		{ name: 'root', node:Croot__switch__node__context, init:Troot__switch__node__context}> {
		protected initializer(state: 'current'|'root') {
			switch (state) {
				case 'current': return (init:Tcurrent, parent:Cnode__context) => new Ccurrent(init, parent);
				case 'root': return (init:Troot__switch__node__context, parent:Cnode__context) => new Croot__switch__node__context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'current'|'root') {
			switch (state) {
				case 'current': return finalize_current;
				case 'root': return finalize_root__switch__node__context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__context['switch'], parent:Cnode__context) {
			super(data, parent, 'switch');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'switch'`; }
	}
}
export namespace Ccurrent {
	export class Dquery_context extends Cgui_node_type_path_step {
		constructor(data:Tcurrent['query context'], parent:Ccurrent) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node_type)
					.then(context => context?.component_root.output.result_gui_node()).result!)
			}, 'query context')
		}
	}
}
export namespace Cfilters__queries {
	export class Dfilters extends AlanDictionary<{ node:Cfilters__filters, init:Tfilters__filters},Cfilters__queries> {
		protected initialize(parent:Cfilters__queries, key:string, entry_init:Tfilters__filters) { return new Cfilters__filters(key, entry_init, parent); }
		protected finalize = finalize_filters__filters
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
		constructor(data:Tfilters__queries['filters'], parent:Cfilters__queries) {
			super(data, parent);
		}
	}
	export class Dtodo_filter extends StateGroup<
		{ name: 'no', node:Cno__todo_filter, init:Tno__todo_filter}|
		{ name: 'yes', node:Cyes__todo_filter, init:Tyes__todo_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__todo_filter, parent:Cfilters__queries) => new Cno__todo_filter(init, parent);
				case 'yes': return (init:Tyes__todo_filter, parent:Cfilters__queries) => new Cyes__todo_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__todo_filter;
				case 'yes': return finalize_yes__todo_filter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters__queries['todo filter'], parent:Cfilters__queries) {
			super(data, parent, 'todo filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'todo filter'`; }
	}
}
export namespace Cfilters__filters {
	export class Dpath extends Cnode_path {
		constructor(data:Tfilters__filters['path'], parent:Cfilters__filters) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context?.state.node.output.selected_context())
					.then(context => context?.component_root.output.gui_entity()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode__type__filters, init:Tnode__type__filters}|
		{ name: 'property', node:Cproperty, init:Tproperty}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode__type__filters, parent:Cfilters__filters) => new Cnode__type__filters(init, parent);
				case 'property': return (init:Tproperty, parent:Cfilters__filters) => new Cproperty(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node__type__filters;
				case 'property': return finalize_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters__filters['type'], parent:Cfilters__filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnode__type__filters {
	export class Dtype extends StateGroup<
		{ name: 'existence', node:Cexistence, init:Texistence}|
		{ name: 'view context', node:Cview_context, init:Tview_context}> {
		protected initializer(state: 'existence'|'view context') {
			switch (state) {
				case 'existence': return (init:Texistence, parent:Cnode__type__filters) => new Cexistence(init, parent);
				case 'view context': return (init:Tview_context, parent:Cnode__type__filters) => new Cview_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'existence'|'view context') {
			switch (state) {
				case 'existence': return finalize_existence;
				case 'view context': return finalize_view_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__type__filters['type'], parent:Cnode__type__filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cexistence {
	export class Doperator extends StateGroup<
		{ name: 'exists', node:Cexists, init:Texists}|
		{ name: 'not exists', node:Cnot_exists, init:Tnot_exists}> {
		protected initializer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return (init:Texists, parent:Cexistence) => new Cexists(init, parent);
				case 'not exists': return (init:Tnot_exists, parent:Cexistence) => new Cnot_exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return finalize_exists;
				case 'not exists': return finalize_not_exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texistence['operator'], parent:Cexistence) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cproperty {
	export class Dproperty extends Reference<gui_model.Cproperty__type__attributes,string> {

		constructor(data:string, $this:Cproperty) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property, init:Tcollection__type__property}|
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'state group', node:Cstate_group__type__property, init:Tstate_group__type__property}|
		{ name: 'text', node:Ctext__type__property, init:Ttext__type__property}> {
		protected initializer(state: 'collection'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property, parent:Cproperty) => new Ccollection__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property, parent:Cproperty) => new Cnumber__type__property(init, parent);
				case 'state group': return (init:Tstate_group__type__property, parent:Cproperty) => new Cstate_group__type__property(init, parent);
				case 'text': return (init:Ttext__type__property, parent:Cproperty) => new Ctext__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property;
				case 'number': return finalize_number__type__property;
				case 'state group': return finalize_state_group__type__property;
				case 'text': return finalize_text__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty['type'], parent:Cproperty) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property {
	export class Dkeys extends AlanDictionary<{ node:Ckeys, init:Tkeys},Ccollection__type__property> {
		protected initialize(parent:Ccollection__type__property, key:string) { return new Ckeys(key, {}, parent); }
		protected finalize = finalize_keys
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'keys'`; }
		constructor(data:Tcollection__type__property['keys'], parent:Ccollection__type__property) {
			super(data, parent);
		}
	}
	export class Doperator extends StateGroup<
		{ name: 'in', node:Cin, init:Tin}|
		{ name: 'not in', node:Cnot_in, init:Tnot_in}> {
		protected initializer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return (init:Tin, parent:Ccollection__type__property) => new Cin(init, parent);
				case 'not in': return (init:Tnot_in, parent:Ccollection__type__property) => new Cnot_in(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return finalize_in;
				case 'not in': return finalize_not_in;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['operator'], parent:Ccollection__type__property) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cnumber__type__property {
	export class Dexpression extends Cnumber_filter_expression {
		constructor(data:Tnumber__type__property['expression'], parent:Cnumber__type__property) {
			super(data, parent, 'expression')
		}
	}
}
export namespace Cstate_group__type__property {
	export class Dstates_to_include extends AlanDictionary<{ node:Cstates_to_include, init:Tstates_to_include},Cstate_group__type__property> {
		protected initialize(parent:Cstate_group__type__property, key:string) { return new Cstates_to_include(key, {}, parent); }
		protected finalize = finalize_states_to_include
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states to include'`; }
		constructor(data:Tstate_group__type__property['states to include'], parent:Cstate_group__type__property) {
			super(data, parent);
		}
	}
}
export namespace Ctext__type__property {
}
export namespace Cyes__todo_filter {
	export class Dpath extends Cnode_path {
		constructor(data:Tyes__todo_filter['path'], parent:Cyes__todo_filter) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context?.state.node.output.selected_context()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
}
export namespace Cyes__has_columns {
	export class Dfirst extends Reference<views.Ccolumns,string> {

		constructor(data:string, $this:Cyes__has_columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cquery_limits {
	export class Dabsolute_maximum extends AlanInteger {
		constructor(data:Tquery_limits['absolute maximum'], parent:Cquery_limits) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'absolute maximum'`; }
	}
	export class Dsample_size extends AlanInteger {
		constructor(data:Tquery_limits['sample size'], parent:Cquery_limits) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sample size'`; }
	}
	export class Dshow_all_limit extends AlanInteger {
		constructor(data:Tquery_limits['show all limit'], parent:Cquery_limits) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'show all limit'`; }
	}
}
export namespace Cyes__translate_title {
	export class Dtitle extends Reference<phrases.Cphrases__,string> {

		constructor(data:string, $this:Cyes__translate_title) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.phrases)
				.then(context => {
					const entry = context?.properties.phrases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'title'`; }
	}
}
function finalize_context(obj:Ccontext, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccontext>obj.output.default_context)(detach) !== undefined || detach);
}
function finalize_entry_or_node_context(obj:Centry_or_node_context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.input.model_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.output.model_context)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
}
function finalize_gui_model_context(obj:Cgui_model_context, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccontext>obj.output.context)(detach) !== undefined || detach);
	finalize_model_context(obj.definitions.model_context, detach);
}
function finalize_gui_node_type_path(obj:Cgui_node_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_gui_node)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.steps, detach);
}
function finalize_no__has_steps__gui_node_type_path_step(obj:Cno__has_steps__gui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_node__type__yes(obj:Cnode__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Coptional_node>obj.inferences.optional_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__gui_node_type_path_step(obj:Cstate__type__yes__has_steps__gui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__gui_node_type_path_step(obj:Cyes__has_steps__gui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node__type__yes(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__gui_node_type_path_step(obj.properties.type.state.node, detach); break;
	}
}
function finalize_gui_node_type_path_step(obj:Cgui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__gui_node_type_path_step(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__gui_node_type_path_step(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_list(obj:Clist, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Clist__type>obj.inferences.list)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_list(obj.properties.list, detach);
}
function finalize_none__has_offset__now__source__number(obj:Cnone__has_offset__now__source__number, detach:boolean = false) {
}
function finalize_yes__has_offset__now__source__number(obj:Cyes__has_offset__now__source__number, detach:boolean = false) {
}
function finalize_now__source__number(obj:Cnow__source__number, detach:boolean = false) {
	switch (obj.properties.has_offset.state.name) {
		case 'none': finalize_none__has_offset__now__source__number(obj.properties.has_offset.state.node, detach); break;
		case 'yes': finalize_yes__has_offset__now__source__number(obj.properties.has_offset.state.node, detach); break;
	}
}
function finalize_static__source__number(obj:Cstatic__source__number, detach:boolean = false) {
}
function finalize_number__type__configuration(obj:Cnumber__type__configuration, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node>obj.inferences.number)(detach) !== undefined || detach);
	switch (obj.properties.source.state.name) {
		case 'now': finalize_now__source__number(obj.properties.source.state.node, detach); break;
		case 'static': finalize_static__source__number(obj.properties.source.state.node, detach); break;
	}
}
function finalize_state_group__type__configuration(obj:Cstate_group__type__configuration, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node>obj.inferences.state_group)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => widget.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_states(obj:Cno__has_states, detach:boolean = false) {
}
function finalize_yes__has_states(obj:Cyes__has_states, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cstates__states_list>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_successor__states(obj:Cno__has_successor__states, detach:boolean = false) {
}
function finalize_yes__has_successor__states(obj:Cyes__has_successor__states, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cstates__states_list>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__states_list(obj:Cstates__states_list, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__states(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__states(obj.properties.has_successor.state.node, detach); break;
	}
}
function finalize_states_list__type(obj:Cstates_list__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Clist__type>obj.inferences.list)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cstate_group__type__model_binding>obj.inferences.state_group_binding)(detach) !== undefined || detach);
	switch (obj.properties.has_states.state.name) {
		case 'no': finalize_no__has_states(obj.properties.has_states.state.node, detach); break;
		case 'yes': finalize_yes__has_states(obj.properties.has_states.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__states_list(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_no__translate(obj:Cno__translate, detach:boolean = false) {
}
function finalize_yes__translate(obj:Cyes__translate, detach:boolean = false) {
}
function finalize_key(obj:Ckey, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ckey_context>obj.inferences.is_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Coptional_key_context>obj.inferences.key_context)(detach) !== undefined || detach);
	switch (obj.properties.translate.state.name) {
		case 'no': finalize_no__translate(obj.properties.translate.state.node, detach); break;
		case 'yes': finalize_yes__translate(obj.properties.translate.state.node, detach); break;
	}
}
function finalize_phrase(obj:Cphrase, detach:boolean = false) {
	assert((<(detach?:boolean) => phrases.Cphrases__>(obj.properties.value as any).resolve)(detach) !== undefined || detach);
}
function finalize_static__type(obj:Cstatic__type, detach:boolean = false) {
}
function finalize_text__type__configuration(obj:Ctext__type__configuration, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Ctext__type__configuration__type__attributes__widget_configuration_node>obj.inferences.text)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'key': finalize_key(obj.properties.type.state.node, detach); break;
		case 'phrase': finalize_phrase(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_configuration__type(obj:Cconfiguration__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration__type__attributes__widget_configuration_node>obj.inferences.configuration)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'list': finalize_list(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__configuration(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__configuration(obj.properties.type.state.node, detach); break;
		case 'states list': finalize_states_list__type(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__configuration(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__can_use_containing_view(obj:Cno__can_use_containing_view, detach:boolean = false) {
}
function finalize_yes__can_use_containing_view(obj:Cyes__can_use_containing_view, detach:boolean = false) {
}
function finalize_no__can_open_entry(obj:Cno__can_open_entry, detach:boolean = false) {
}
function finalize_external__using_views__yes(obj:Cexternal__using_views__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cdependencies>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_internal__using_views__yes(obj:Cinternal__using_views__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
}
function finalize_yes__can_open_entry(obj:Cyes__can_open_entry, detach:boolean = false) {
	switch (obj.properties.using_views.state.name) {
		case 'external': finalize_external__using_views__yes(obj.properties.using_views.state.node, detach); break;
		case 'internal': finalize_internal__using_views__yes(obj.properties.using_views.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.view.inferences.constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_external__using_views__view_bindings(obj:Cexternal__using_views__view_bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cdependencies>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_internal__using_views__view_bindings(obj:Cinternal__using_views__view_bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
}
function finalize_view_bindings(obj:Cview_bindings, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.can_open_entry.state.name) {
		case 'no': finalize_no__can_open_entry(obj.properties.can_open_entry.state.node, detach); break;
		case 'yes': finalize_yes__can_open_entry(obj.properties.can_open_entry.state.node, detach); break;
	}
	switch (obj.properties.using_views.state.name) {
		case 'external': finalize_external__using_views__view_bindings(obj.properties.using_views.state.node, detach); break;
		case 'internal': finalize_internal__using_views__view_bindings(obj.properties.using_views.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.view.inferences.constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_action(obj:Caction, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Coperation__type>obj.inferences.operation)(detach) !== undefined || detach);
	finalize_model_context(obj.definitions.model_context, detach);
	assert((<(detach?:boolean) => gui_model.Caction__type>(obj.properties.action as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.can_use_containing_view.state.name) {
		case 'no': finalize_no__can_use_containing_view(obj.properties.can_use_containing_view.state.node, detach); break;
		case 'yes': finalize_yes__can_use_containing_view(obj.properties.can_use_containing_view.state.node, detach); break;
	}
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
	for (const [_key, entry] of obj.properties.view_bindings) {
		finalize_view_bindings(entry, detach);
	}
	if (!detach) {
		(obj.properties.view_bindings as any).eval_required_keys(detach);
	}
}
function finalize_no__join(obj:Cno__join, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Ccollection__type__node>obj.inferences.collection_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.output.context)(detach) !== undefined || detach);
}
function finalize_yes__join(obj:Cyes__join, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cjoin__type>obj.inferences.join_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.navigable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.output.context)(detach) !== undefined || detach);
	finalize_join_context(obj.definitions.join_context, detach);
}
function finalize_collection__type__model_binding(obj:Ccollection__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	switch (obj.properties.join.state.name) {
		case 'no': finalize_no__join(obj.properties.join.state.node, detach); break;
		case 'yes': finalize_yes__join(obj.properties.join.state.node, detach); break;
	}
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_command(obj:Ccommand, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Coperation__type>obj.inferences.operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cglobal>(obj.properties.command as any).resolve)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_current_node__type(obj:Ccurrent_node__type, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__type__node>obj.inferences.node)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_entity__type__model_binding(obj:Centity__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Centity__type>obj.inferences.node)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
}
function finalize_file__type__model_binding(obj:Cfile__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cfile__type__node>obj.inferences.file)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Cfile__type__property>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_group__type__model_binding(obj:Cgroup__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cgroup__type>obj.inferences.group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_joined_entry(obj:Cjoined_entry, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cjoin_context>obj.inferences.join)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cjoined_entry__type>obj.inferences.join_binding)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
}
function finalize_number__type__model_binding(obj:Cnumber__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnumber__type__node>obj.inferences.number)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_ordered_graph(obj:Cordered_graph, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__has_ordered_graphs>obj.inferences.has_constraints)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cordered_graph__type>obj.inferences.ordered_graph)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Cordered_graphs>(obj.properties.ordered_graph as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_refresh_interval(obj:Cno__has_refresh_interval, detach:boolean = false) {
}
function finalize_yes__has_refresh_interval(obj:Cyes__has_refresh_interval, detach:boolean = false) {
}
function finalize_candidates__query_context(obj:Ccandidates__query_context, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.navigable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ctext__type__model_binding>obj.inferences.text)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>obj.output.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>(obj.properties.query as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.query.inferences.constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.query.inferences.property_match)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccandidates__context>obj.properties.query.inferences.query)(detach) !== undefined || detach);
}
function finalize_current_node__switch(obj:Ccurrent_node__switch, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.output.query)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
	assert((<(detach?:boolean) => views.Cqueries>(obj.properties.query as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccurrent>obj.properties.query.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.query.inferences.valid_node_type)(detach) !== undefined || detach);
}
function finalize_root__switch__node__query_context(obj:Croot__switch__node__query_context, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.output.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>(obj.properties.query as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Croot__switch__node__context>obj.properties.query.inferences.query)(detach) !== undefined || detach);
}
function finalize_node__query_context(obj:Cnode__query_context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cquery__type__node>obj.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cqueries>obj.output.query)(detach) !== undefined || detach);
	switch (obj.properties.switch_.state.name) {
		case 'current node': finalize_current_node__switch(obj.properties.switch_.state.node, detach); break;
		case 'root': finalize_root__switch__node__query_context(obj.properties.switch_.state.node, detach); break;
	}
}
function finalize_query(obj:Cquery, detach:boolean = false) {
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	switch (obj.properties.has_refresh_interval.state.name) {
		case 'no': finalize_no__has_refresh_interval(obj.properties.has_refresh_interval.state.node, detach); break;
		case 'yes': finalize_yes__has_refresh_interval(obj.properties.has_refresh_interval.state.node, detach); break;
	}
	switch (obj.properties.query_context.state.name) {
		case 'candidates': finalize_candidates__query_context(obj.properties.query_context.state.node, detach); break;
		case 'node': finalize_node__query_context(obj.properties.query_context.state.node, detach); break;
	}
}
function finalize_query_file(obj:Cquery_file, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cfile__type__query>obj.inferences.query_file)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => views.Cfile__type__columns>(obj.properties.file as any).resolve)(detach) !== undefined || detach);
}
function finalize_query_number(obj:Cquery_number, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnumber__type__query>obj.inferences.query_number)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => views.Cnumber__type__columns>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_query_stategroup(obj:Cquery_stategroup, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstategroup>obj.inferences.query_stategroup)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => views.Cstate_group__type__columns>(obj.properties.stategroup as any).resolve)(detach) !== undefined || detach);
}
function finalize_query_text(obj:Cquery_text, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.inferences.query)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ctext__type__query>obj.inferences.query_text)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => views.Ctext__type__columns>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_report(obj:Creport, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__type__report>obj.inferences.report)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => parameters.Cparameters>(obj.properties.report as any).resolve)(detach) !== undefined || detach);
}
function finalize_report_number_parameter(obj:Creport_number_parameter, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Creport>obj.inferences.report)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnumber__type__report>obj.inferences.report_number)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => parameters.Cnumber>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_report_text_parameter(obj:Creport_text_parameter, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Creport>obj.inferences.report)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ctext__type__report>obj.inferences.report_text)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => parameters.Ctext>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_number__on(obj:Cnumber__on, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_state_group__on(obj:Cstate_group__on, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_text__on(obj:Ctext__on, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_sort_property(obj:Csort_property, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Csort_property__type>obj.inferences.sort_property)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	switch (obj.properties.on.state.name) {
		case 'number': finalize_number__on(obj.properties.on.state.node, detach); break;
		case 'state group': finalize_state_group__on(obj.properties.on.state.node, detach); break;
		case 'text': finalize_text__on(obj.properties.on.state.node, detach); break;
	}
	assert((<(detach?:boolean) => gui_model.Cattributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_state_group_binding(obj:Cstate_group_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cstate_group__type__model_binding>obj.inferences.state_group_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.state_node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_states_list__state_group_context(obj:Cstates_list__state_group_context, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cstates__states_list>obj.inferences.state_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.state_node_context)(detach) !== undefined || detach);
}
function finalize_state__type__model_binding(obj:Cstate__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cstate__type__node>obj.inferences.state)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	switch (obj.properties.state_group_context.state.name) {
		case 'state group binding': finalize_state_group_binding(obj.properties.state_group_context.state.node, detach); break;
		case 'states list': finalize_states_list__state_group_context(obj.properties.state_group_context.state.node, detach); break;
	}
}
function finalize_state_group__type__model_binding(obj:Cstate_group__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstate_group__type__node>obj.inferences.state_group)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_rule__type__model_binding(obj:Cstate_rule__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cwhere_rule__type>obj.inferences.where_rule)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__model_binding(obj:Ctext__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ctext__type__node>obj.inferences.text)(detach) !== undefined || detach);
	finalize_model_context(obj.definitions.model_context, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.property_path, detach);
}
function finalize_user(obj:Cuser, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__has_user_node>obj.inferences.has_user_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cuser__type>obj.inferences.user)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
}
function finalize_window__type__model_binding(obj:Cwindow__type__model_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cwindow__type>obj.inferences.node)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_model_binding(obj:Cmodel_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cbinding__type__attributes>obj.inferences.model_binding)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'action': finalize_action(obj.properties.type.state.node, detach); break;
		case 'collection': finalize_collection__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'command': finalize_command(obj.properties.type.state.node, detach); break;
		case 'current node': finalize_current_node__type(obj.properties.type.state.node, detach); break;
		case 'entity': finalize_entity__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'joined entry': finalize_joined_entry(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'ordered graph': finalize_ordered_graph(obj.properties.type.state.node, detach); break;
		case 'query': finalize_query(obj.properties.type.state.node, detach); break;
		case 'query file': finalize_query_file(obj.properties.type.state.node, detach); break;
		case 'query number': finalize_query_number(obj.properties.type.state.node, detach); break;
		case 'query stategroup': finalize_query_stategroup(obj.properties.type.state.node, detach); break;
		case 'query text': finalize_query_text(obj.properties.type.state.node, detach); break;
		case 'report': finalize_report(obj.properties.type.state.node, detach); break;
		case 'report number parameter': finalize_report_number_parameter(obj.properties.type.state.node, detach); break;
		case 'report text parameter': finalize_report_text_parameter(obj.properties.type.state.node, detach); break;
		case 'sort property': finalize_sort_property(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'state rule': finalize_state_rule__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__model_binding(obj.properties.type.state.node, detach); break;
		case 'user': finalize_user(obj.properties.type.state.node, detach); break;
		case 'window': finalize_window__type__model_binding(obj.properties.type.state.node, detach); break;
	}
}
function finalize_in_window(obj:Cin_window, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cview__type__attributes>obj.inferences.open_view)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_external__using_views__view__type__gui_widget_configuration_attribute(obj:Cexternal__using_views__view__type__gui_widget_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cdependencies>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_internal__using_views__view__type__gui_widget_configuration_attribute(obj:Cinternal__using_views__view__type__gui_widget_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
}
function finalize_view__type__gui_widget_configuration_attribute(obj:Cview__type__gui_widget_configuration_attribute, detach:boolean = false) {
	switch (obj.properties.render.state.name) {
		case 'in window': finalize_in_window(obj.properties.render.state.node, detach); break;
	}
	switch (obj.properties.using_views.state.name) {
		case 'external': finalize_external__using_views__view__type__gui_widget_configuration_attribute(obj.properties.using_views.state.node, detach); break;
		case 'internal': finalize_internal__using_views__view__type__gui_widget_configuration_attribute(obj.properties.using_views.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.view.inferences.constraint)(detach) !== undefined || detach);
	finalize_view_context_path(obj.properties.view_context, detach);
}
function finalize_widget__type__gui_widget_configuration_attribute(obj:Cwidget__type__gui_widget_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget__type>obj.inferences.widget)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.instance, detach);
}
function finalize_window__type__gui_widget_configuration_attribute(obj:Cwindow__type__gui_widget_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwindow__type>obj.inferences.window)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => settings.Cwindows>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_gui_widget_configuration_attribute(obj:Cgui_widget_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cgui_widget_configuration_node>obj.input.gui_widget_configuration_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews__>obj.input.view)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cattributes__widget_configuration_node>obj.input.widget_configuration_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cgui_widget_configuration_node>obj.output.gui_widget_configuration_node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'configuration': finalize_configuration__type(obj.properties.type.state.node, detach); break;
		case 'model binding': finalize_model_binding(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__gui_widget_configuration_attribute(obj.properties.type.state.node, detach); break;
		case 'widget': finalize_widget__type__gui_widget_configuration_attribute(obj.properties.type.state.node, detach); break;
		case 'window': finalize_window__type__gui_widget_configuration_attribute(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_steps__gui_widget_configuration_list(obj:Cno__has_steps__gui_widget_configuration_list, detach:boolean = false) {
}
function finalize_yes__has_steps__gui_widget_configuration_list(obj:Cyes__has_steps__gui_widget_configuration_list, detach:boolean = false) {
	finalize_list_counter(obj.definitions.list_counter, detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	finalize_gui_widget_configuration_list(obj.properties.tail, detach);
}
function finalize_gui_widget_configuration_list(obj:Cgui_widget_configuration_list, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Clist_counter>obj.input.previous_entry)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews__>obj.input.view)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Clist_counter>obj.output.previous_entry)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__gui_widget_configuration_list(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__gui_widget_configuration_list(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_configuration__gui_widget_configuration_node(obj:Cconfiguration__gui_widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cattributes__widget_configuration_node>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.key.inferences.client_binding_context)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_attribute(obj.properties.attribute, detach);
}
function finalize_gui_widget_configuration_node(obj:Cgui_widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews__>obj.input.view)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_node)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.configuration) {
		finalize_configuration__gui_widget_configuration_node(entry, detach);
	}
	if (!detach) {
		(obj.properties.configuration as any).eval_required_keys(detach);
	}
}
function finalize_join_context(obj:Cjoin_context, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccontext>obj.input.model_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.referenced_model_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.output.model_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.output.referenced_model_context)(detach) !== undefined || detach);
	finalize_context(obj.definitions.context, detach);
}
function finalize_key_context(obj:Ckey_context, detach:boolean = false) {
	finalize_optional_key_context(obj.definitions.optional_key_context, detach);
}
function finalize_list_counter(obj:Clist_counter, detach:boolean = false) {
}
function finalize_no__has_steps__model_binding_property_path(obj:Cno__has_steps__model_binding_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__model_binding_property_path(obj:Cgroup__type__yes__has_steps__model_binding_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_parent__type__yes__has_steps__model_binding_property_path(obj:Cparent__type__yes__has_steps__model_binding_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_state_group__type__yes(obj:Cstate_group__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.conditional)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__model_binding_property_path(obj:Cyes__has_steps__model_binding_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	finalize_model_binding_property_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__yes__has_steps__model_binding_property_path(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__yes__has_steps__model_binding_property_path(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_model_binding_property_path(obj:Cmodel_binding_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.participation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__model_binding_property_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__model_binding_property_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_model_context(obj:Cmodel_context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.input.model_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.output.model_context)(detach) !== undefined || detach);
	finalize_entry_or_node_context(obj.definitions.entry_or_node_context, detach);
}
function finalize_no__has_steps__node_path(obj:Cno__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_collection_entry(obj:Ccollection_entry, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__node_path(obj:Cgroup__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_parent__type__yes__has_steps__node_path(obj:Cparent__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_reference__type__yes__has_steps__node_path(obj:Creference__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.text.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.text.inferences.navigable)(detach) !== undefined || detach);
}
function finalize_reference_rule__type__yes(obj:Creference_rule__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.property.inferences.navigable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__node_path(obj:Cstate__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_rule__type__yes__has_steps__node_path(obj:Cstate_rule__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.properties.rule.inferences.mandatory_reference)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__node_path(obj:Cyes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'reference rule': finalize_reference_rule__type__yes(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'state rule': finalize_state_rule__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_path(obj:Cnode_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_equals(obj:Cequals, detach:boolean = false) {
}
function finalize_no__greater_than(obj:Cno__greater_than, detach:boolean = false) {
}
function finalize_yes__greater_than(obj:Cyes__greater_than, detach:boolean = false) {
}
function finalize_no__less_than(obj:Cno__less_than, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cyes__greater_than>obj.inferences.must_be_greater_than)(detach) !== undefined || detach);
}
function finalize_yes__less_than(obj:Cyes__less_than, detach:boolean = false) {
}
function finalize_range(obj:Crange, detach:boolean = false) {
	switch (obj.properties.greater_than.state.name) {
		case 'no': finalize_no__greater_than(obj.properties.greater_than.state.node, detach); break;
		case 'yes': finalize_yes__greater_than(obj.properties.greater_than.state.node, detach); break;
	}
	switch (obj.properties.less_than.state.name) {
		case 'no': finalize_no__less_than(obj.properties.less_than.state.node, detach); break;
		case 'yes': finalize_yes__less_than(obj.properties.less_than.state.node, detach); break;
	}
}
function finalize_number_filter_expression(obj:Cnumber_filter_expression, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'equals': finalize_equals(obj.properties.operator.state.node, detach); break;
		case 'range': finalize_range(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_optional_key_context(obj:Coptional_key_context, detach:boolean = false) {
}
function finalize_optional_node(obj:Coptional_node, detach:boolean = false) {
}
function finalize_no__has_steps__query_path(obj:Cno__has_steps__query_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.gui_node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__query_path(obj:Cyes__has_steps__query_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_query_path_step(obj.properties.head, detach);
	finalize_query_path(obj.properties.tail, detach);
}
function finalize_query_path(obj:Cquery_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__query_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__query_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__query_path_step(obj:Cno__has_steps__query_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__query_path_step(obj:Cgroup__type__yes__has_steps__query_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__query_path_step(obj:Cstate__type__yes__has_steps__query_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__query_path_step(obj:Cyes__has_steps__query_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	finalize_query_path_step(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__yes__has_steps__query_path_step(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__query_path_step(obj.properties.type.state.node, detach); break;
	}
}
function finalize_query_path_step(obj:Cquery_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__query_path_step(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__query_path_step(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_tail(obj:Cno__has_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_yes__has_tail(obj:Cyes__has_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	finalize_view_context_parent_path(obj.properties.tail, detach);
}
function finalize_collection_parent(obj:Ccollection_parent, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_group_parent(obj:Cgroup_parent, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_state_parent(obj:Cstate_parent, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_view_context_parent_path(obj:Cview_context_parent_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_tail.state.name) {
		case 'no': finalize_no__has_tail(obj.properties.has_tail.state.node, detach); break;
		case 'yes': finalize_yes__has_tail(obj.properties.has_tail.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'collection parent': finalize_collection_parent(obj.properties.type.state.node, detach); break;
		case 'group parent': finalize_group_parent(obj.properties.type.state.node, detach); break;
		case 'state parent': finalize_state_parent(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_steps__view_context_path(obj:Cno__has_steps__view_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_entity__type__yes(obj:Centity__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_parent__type__yes__has_steps__view_context_path(obj:Cparent__type__yes__has_steps__view_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
	finalize_view_context_parent_path(obj.properties.path, detach);
}
function finalize_query_entry(obj:Cquery_entry, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cqueries>obj.inferences.query_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_reference__type__yes__has_steps__view_context_path(obj:Creference__type__yes__has_steps__view_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_state_rule__type__yes__has_steps__view_context_path(obj:Cstate_rule__type__yes__has_steps__view_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Crules>obj.inferences.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__view_context_path(obj:Cyes__has_steps__view_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'entity': finalize_entity__type__yes(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__yes__has_steps__view_context_path(obj.properties.type.state.node, detach); break;
		case 'query entry': finalize_query_entry(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference__type__yes__has_steps__view_context_path(obj.properties.type.state.node, detach); break;
		case 'state rule': finalize_state_rule__type__yes__has_steps__view_context_path(obj.properties.type.state.node, detach); break;
	}
}
function finalize_view_context_path(obj:Cview_context_path, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__view_context_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__view_context_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_group__type__widget_expression(obj:Cgroup__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
}
function finalize_parent__type__widget_expression(obj:Cparent__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
}
function finalize_reference__type__widget_expression(obj:Creference__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_reference_rule__type__widget_expression(obj:Creference_rule__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_state__type__widget_expression(obj:Cstate__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_context_rule(obj:Cstate_context_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>obj.inferences.state)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
	assert((<(detach?:boolean) => gui_model.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__switch(obj:Cstates__switch, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_widget_expression(obj.properties.next, detach);
}
function finalize_switch(obj:Cswitch, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__switch(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_external__using_views__view__type__widget_expression(obj:Cexternal__using_views__view__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cdependencies>(obj.properties.views as any).resolve)(detach) !== undefined || detach);
}
function finalize_internal__using_views__view__type__widget_expression(obj:Cinternal__using_views__view__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>obj.output.definition)(detach) !== undefined || detach);
}
function finalize_view__type__widget_expression(obj:Cview__type__widget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Centry_or_node_context>obj.inferences.model)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.inferences.view_binding)(detach) !== undefined || detach);
	switch (obj.properties.using_views.state.name) {
		case 'external': finalize_external__using_views__view__type__widget_expression(obj.properties.using_views.state.node, detach); break;
		case 'internal': finalize_internal__using_views__view__type__widget_expression(obj.properties.using_views.state.node, detach); break;
	}
	assert((<(detach?:boolean) => views.Cviews__>(obj.properties.view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.properties.view.inferences.constraint)(detach) !== undefined || detach);
}
function finalize_no__lazy(obj:Cno__lazy, detach:boolean = false) {
}
function finalize_yes__lazy(obj:Cyes__lazy, detach:boolean = false) {
}
function finalize_widget__type__widget_expression(obj:Cwidget__type__widget_expression, detach:boolean = false) {
	switch (obj.properties.lazy.state.name) {
		case 'no': finalize_no__lazy(obj.properties.lazy.state.node, detach); break;
		case 'yes': finalize_yes__lazy(obj.properties.lazy.state.node, detach); break;
	}
	finalize_widget_instance(obj.properties.widget, detach);
}
function finalize_widget_expression(obj:Cwidget_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews__>obj.input.view)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'reference rule': finalize_reference_rule__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'state context rule': finalize_state_context_rule(obj.properties.type.state.node, detach); break;
		case 'switch': finalize_switch(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__widget_expression(obj.properties.type.state.node, detach); break;
		case 'widget': finalize_widget__type__widget_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_widget_instance(obj:Cwidget_instance, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Ccontext>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => views.Cviews__>obj.input.view)(detach) !== undefined || detach);
	finalize_gui_widget_configuration_node(obj.properties.configuration, detach);
	assert((<(detach?:boolean) => widget.Cwidget>(obj.properties.widget as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.properties.widget.inferences.client_binding_check)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.properties.widget.inferences.client_binding_context)(detach) !== undefined || detach);
}
function finalize_dependencies(obj:Cdependencies, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Cviews>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_content(obj:Ccontent, detach:boolean = false) {
}
function finalize_id(obj:Cid, detach:boolean = false) {
}
function finalize_no__has_successor__columns(obj:Cno__has_successor__columns, detach:boolean = false) {
}
function finalize_yes__has_successor__columns(obj:Cyes__has_successor__columns, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccolumns>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_file__type__columns(obj:Cfile__type__columns, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cfile__type__property>(obj.properties.file as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_filter__number(obj:Cno__has_filter__number, detach:boolean = false) {
}
function finalize_none__initial_criteria(obj:Cnone__initial_criteria, detach:boolean = false) {
}
function finalize_none__has_offset__now__source__yes(obj:Cnone__has_offset__now__source__yes, detach:boolean = false) {
}
function finalize_yes__has_offset__now__source__yes(obj:Cyes__has_offset__now__source__yes, detach:boolean = false) {
}
function finalize_now__source__yes(obj:Cnow__source__yes, detach:boolean = false) {
	switch (obj.properties.has_offset.state.name) {
		case 'none': finalize_none__has_offset__now__source__yes(obj.properties.has_offset.state.node, detach); break;
		case 'yes': finalize_yes__has_offset__now__source__yes(obj.properties.has_offset.state.node, detach); break;
	}
}
function finalize_static__source__yes(obj:Cstatic__source__yes, detach:boolean = false) {
}
function finalize_yes__initial_criteria(obj:Cyes__initial_criteria, detach:boolean = false) {
	switch (obj.properties.source.state.name) {
		case 'now': finalize_now__source__yes(obj.properties.source.state.node, detach); break;
		case 'static': finalize_static__source__yes(obj.properties.source.state.node, detach); break;
	}
}
function finalize_no__operator_selected(obj:Cno__operator_selected, detach:boolean = false) {
}
function finalize_equal_to(obj:Cequal_to, detach:boolean = false) {
}
function finalize_greater_than(obj:Cgreater_than, detach:boolean = false) {
}
function finalize_greater_than_or_equal_to(obj:Cgreater_than_or_equal_to, detach:boolean = false) {
}
function finalize_smaller_than(obj:Csmaller_than, detach:boolean = false) {
}
function finalize_smaller_than_or_equal_to(obj:Csmaller_than_or_equal_to, detach:boolean = false) {
}
function finalize_yes__operator_selected(obj:Cyes__operator_selected, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'equal to': finalize_equal_to(obj.properties.operator.state.node, detach); break;
		case 'greater than': finalize_greater_than(obj.properties.operator.state.node, detach); break;
		case 'greater than or equal to': finalize_greater_than_or_equal_to(obj.properties.operator.state.node, detach); break;
		case 'smaller than': finalize_smaller_than(obj.properties.operator.state.node, detach); break;
		case 'smaller than or equal to': finalize_smaller_than_or_equal_to(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_yes__has_filter__number(obj:Cyes__has_filter__number, detach:boolean = false) {
	switch (obj.properties.initial_criteria.state.name) {
		case 'none': finalize_none__initial_criteria(obj.properties.initial_criteria.state.node, detach); break;
		case 'yes': finalize_yes__initial_criteria(obj.properties.initial_criteria.state.node, detach); break;
	}
	switch (obj.properties.operator_selected.state.name) {
		case 'no': finalize_no__operator_selected(obj.properties.operator_selected.state.node, detach); break;
		case 'yes': finalize_yes__operator_selected(obj.properties.operator_selected.state.node, detach); break;
	}
}
function finalize_number__type__columns(obj:Cnumber__type__columns, detach:boolean = false) {
	switch (obj.properties.has_filter.state.name) {
		case 'no': finalize_no__has_filter__number(obj.properties.has_filter.state.node, detach); break;
		case 'yes': finalize_yes__has_filter__number(obj.properties.has_filter.state.node, detach); break;
	}
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_filter__state_group(obj:Cno__has_filter__state_group, detach:boolean = false) {
}
function finalize_no__filter_enabled(obj:Cno__filter_enabled, detach:boolean = false) {
}
function finalize_yes__filter_enabled(obj:Cyes__filter_enabled, detach:boolean = false) {
}
function finalize_no__is_selected(obj:Cno__is_selected, detach:boolean = false) {
}
function finalize_yes__is_selected(obj:Cyes__is_selected, detach:boolean = false) {
}
function finalize_states__yes(obj:Cstates__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.is_selected.state.name) {
		case 'no': finalize_no__is_selected(obj.properties.is_selected.state.node, detach); break;
		case 'yes': finalize_yes__is_selected(obj.properties.is_selected.state.node, detach); break;
	}
}
function finalize_yes__has_filter__state_group(obj:Cyes__has_filter__state_group, detach:boolean = false) {
	switch (obj.properties.filter_enabled.state.name) {
		case 'no': finalize_no__filter_enabled(obj.properties.filter_enabled.state.node, detach); break;
		case 'yes': finalize_yes__filter_enabled(obj.properties.filter_enabled.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__yes(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_state_group__type__columns(obj:Cstate_group__type__columns, detach:boolean = false) {
	switch (obj.properties.has_filter.state.name) {
		case 'no': finalize_no__has_filter__state_group(obj.properties.has_filter.state.node, detach); break;
		case 'yes': finalize_yes__has_filter__state_group(obj.properties.has_filter.state.node, detach); break;
	}
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_current_id_path(obj:Ccurrent_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.navigable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.valid_entity)(detach) !== undefined || detach);
}
function finalize_none__filter(obj:Cnone__filter, detach:boolean = false) {
}
function finalize_simple(obj:Csimple, detach:boolean = false) {
}
function finalize_text__type__columns(obj:Ctext__type__columns, detach:boolean = false) {
	switch (obj.properties.filter.state.name) {
		case 'current id path': finalize_current_id_path(obj.properties.filter.state.node, detach); break;
		case 'none': finalize_none__filter(obj.properties.filter.state.node, detach); break;
		case 'simple': finalize_simple(obj.properties.filter.state.node, detach); break;
	}
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_widget__type__columns(obj:Cwidget__type__columns, detach:boolean = false) {
	finalize_widget_expression(obj.properties.instance, detach);
}
function finalize_columns(obj:Ccolumns, detach:boolean = false) {
	switch (obj.properties.column_type.state.name) {
		case 'content': finalize_content(obj.properties.column_type.state.node, detach); break;
		case 'id': finalize_id(obj.properties.column_type.state.node, detach); break;
	}
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__columns(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__columns(obj.properties.has_successor.state.node, detach); break;
	}
	assert((<(detach?:boolean) => phrases.Cphrases__>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'file': finalize_file__type__columns(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__columns(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__columns(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__columns(obj.properties.type.state.node, detach); break;
		case 'widget': finalize_widget__type__columns(obj.properties.type.state.node, detach); break;
	}
}
function finalize_candidates__context(obj:Ccandidates__context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.property_context, detach);
}
function finalize_current(obj:Ccurrent, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_context)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.query_context, detach);
}
function finalize_root__switch__node__context(obj:Croot__switch__node__context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_context)(detach) !== undefined || detach);
}
function finalize_node__context(obj:Cnode__context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.selected_context)(detach) !== undefined || detach);
	finalize_query_path(obj.properties.path, detach);
	switch (obj.properties.switch_.state.name) {
		case 'current': finalize_current(obj.properties.switch_.state.node, detach); break;
		case 'root': finalize_root__switch__node__context(obj.properties.switch_.state.node, detach); break;
	}
}
function finalize_exists(obj:Cexists, detach:boolean = false) {
}
function finalize_not_exists(obj:Cnot_exists, detach:boolean = false) {
}
function finalize_existence(obj:Cexistence, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'exists': finalize_exists(obj.properties.operator.state.node, detach); break;
		case 'not exists': finalize_not_exists(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_view_context(obj:Cview_context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.valid_entity)(detach) !== undefined || detach);
}
function finalize_node__type__filters(obj:Cnode__type__filters, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'existence': finalize_existence(obj.properties.type.state.node, detach); break;
		case 'view context': finalize_view_context(obj.properties.type.state.node, detach); break;
	}
}
function finalize_keys(obj:Ckeys, detach:boolean = false) {
}
function finalize_in(obj:Cin, detach:boolean = false) {
}
function finalize_not_in(obj:Cnot_in, detach:boolean = false) {
}
function finalize_collection__type__property(obj:Ccollection__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.keys) {
		finalize_keys(entry, detach);
	}
	if (!detach) {
		if (obj.properties.keys.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	switch (obj.properties.operator.state.name) {
		case 'in': finalize_in(obj.properties.operator.state.node, detach); break;
		case 'not in': finalize_not_in(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_number__type__property(obj:Cnumber__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>obj.inferences.number_type)(detach) !== undefined || detach);
	finalize_number_filter_expression(obj.properties.expression, detach);
}
function finalize_states_to_include(obj:Cstates_to_include, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_group__type__property(obj:Cstate_group__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.state_group_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states_to_include) {
		finalize_states_to_include(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states_to_include.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_text__type__property(obj:Ctext__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>obj.inferences.text_type)(detach) !== undefined || detach);
}
function finalize_property(obj:Cproperty, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_filters__filters(obj:Cfilters__filters, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node__type__filters(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__todo_filter(obj:Cno__todo_filter, detach:boolean = false) {
}
function finalize_yes__todo_filter(obj:Cyes__todo_filter, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
}
function finalize_filters__queries(obj:Cfilters__queries, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.filters) {
		finalize_filters__filters(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.todo_filter.state.name) {
		case 'no': finalize_no__todo_filter(obj.properties.todo_filter.state.node, detach); break;
		case 'yes': finalize_yes__todo_filter(obj.properties.todo_filter.state.node, detach); break;
	}
}
function finalize_no__has_columns(obj:Cno__has_columns, detach:boolean = false) {
}
function finalize_yes__has_columns(obj:Cyes__has_columns, detach:boolean = false) {
	assert((<(detach?:boolean) => views.Ccolumns>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_query_limits(obj:Cquery_limits, detach:boolean = false) {
}
function finalize_queries(obj:Cqueries, detach:boolean = false) {
	finalize_entry_or_node_context(obj.definitions.entry_or_node_context, detach);
	for (const [_key, entry] of obj.properties.columns) {
		finalize_columns(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.context.state.name) {
		case 'candidates': finalize_candidates__context(obj.properties.context.state.node, detach); break;
		case 'node': finalize_node__context(obj.properties.context.state.node, detach); break;
	}
	finalize_filters__queries(obj.properties.filters, detach);
	switch (obj.properties.has_columns.state.name) {
		case 'no': finalize_no__has_columns(obj.properties.has_columns.state.node, detach); break;
		case 'yes': finalize_yes__has_columns(obj.properties.has_columns.state.node, detach); break;
	}
	finalize_query_limits(obj.properties.query_limits, detach);
}
function finalize_no__translate_title(obj:Cno__translate_title, detach:boolean = false) {
}
function finalize_yes__translate_title(obj:Cyes__translate_title, detach:boolean = false) {
	assert((<(detach?:boolean) => phrases.Cphrases__>(obj.properties.title as any).resolve)(detach) !== undefined || detach);
}
function finalize_views__(obj:Cviews__, detach:boolean = false) {
	finalize_widget_expression(obj.properties.instance, detach);
	finalize_gui_node_type_path(obj.properties.node_type, detach);
	for (const [_key, entry] of obj.properties.queries) {
		finalize_queries(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.translate_title.state.name) {
		case 'no': finalize_no__translate_title(obj.properties.translate_title.state.node, detach); break;
		case 'yes': finalize_yes__translate_title(obj.properties.translate_title.state.node, detach); break;
	}
}
function finalize_views(obj:Cviews, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.dependencies) {
		finalize_dependencies(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.views) {
		finalize_views__(entry, detach);
	}
	if (!detach) {
	}
}

export namespace Cviews {
	export function create(init:Tviews, input: {
	'application':application.Capplication
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'phrases':phrases.Cphrases
	'reports':{[name:string]:parameters.Cparameters}
	'settings':settings.Csettings
	'views':{[name:string]:Cviews}
	'widgets':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile):Cviews {
		const instance = new Cviews(init, input as any, symbols_file);
		return instance;
	};
}
